import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartJSTooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import Papa from "papaparse";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar as RechartsBar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartJSTooltip, Legend);

// ═══════════════════════════════════════════════
// DATA
// ═══════════════════════════════════════════════

const CLAUSES = {
  fees: {
    title: "Fee Structures",
    desc: "Standard FE fee arrangements, success fee thresholds, and retainer terms we have historically agreed to.",
    items: [
      { title: "Success Fee — Standard Tiered", stance: "firm", text: 'A success fee equal to [X]% of the total Transaction Value, payable in full upon closing. For transactions above $5,000,000, a tiered rate schedule applies as set forth in Schedule A.', notes: "<strong>Where we hold firm:</strong> Minimum success fee floor of $25,000 regardless of transaction size. No contingency waivers without Senior Partner sign-off. We have not historically agreed to sub-3% on transactions below $2M." },
      { title: "Retainer Fee — Monthly Engagement", stance: "flexible", text: 'Client shall pay FE International a non-refundable monthly retainer of $[X,000] per month, commencing on the Engagement Date and continuing until the earlier of closing or termination.', notes: "<strong>Flexible:</strong> We have agreed to retainer waivers on high-conviction mandates with revenue above $5M. Retainer is sometimes credited against success fee at closing." },
      { title: "Transaction Value Definition", stance: "firm", text: '"Transaction Value" means the aggregate consideration paid or payable by the Buyer, including cash, stock, earnouts, assumed liabilities, non-competition payments, consulting arrangements, and any deferred consideration.', notes: "<strong>Where we hold firm:</strong> Earnouts and consulting fees are always included. We have consistently refused to exclude earnout consideration from Transaction Value." },
      { title: "Expense Reimbursement", stance: "negotiable", text: 'Client agrees to reimburse FE International for all reasonable out-of-pocket expenses incurred in connection with the engagement, including travel, legal, and third-party research costs.', notes: "<strong>Negotiable:</strong> We have agreed to cap expenses at $5,000 on transactions under $2M. Legal review costs are typically excluded from the cap." },
      { title: "Success Fee — Buy-Side Mandate", stance: "negotiable", text: 'For buy-side mandates, a success fee of [X]% of Transaction Value shall be payable by Client upon signing of a definitive purchase agreement.', notes: "<strong>Negotiable:</strong> Buy-side mandates have historically carried 1–2% success fees. Rate depends on complexity and exclusivity." },
      { title: "Fee Sharing with Co-Advisors", stance: "firm", text: "In the event a co-advisor is engaged, any fee sharing arrangement shall be agreed in writing prior to engagement. FE's fee is not reduced absent separate written agreement.", notes: "<strong>Where we hold firm:</strong> We do not reduce our fee to accommodate a co-advisor without written amendment." },
      { title: "Minimum Engagement Fee", stance: "firm", text: 'Notwithstanding any other provision, the minimum total fee payable to FE International shall be no less than $[25,000], regardless of transaction size or outcome.', notes: "<strong>Where we hold firm:</strong> The $25K floor has been maintained in 100% of engagements. No exceptions have been granted." }
    ]
  },
  exclusivity: {
    title: "Exclusivity & Lock-up",
    desc: "Terms governing our exclusive mandate, partner lock-up periods, and what happens when sellers approach buyers independently.",
    items: [
      { title: "Exclusive Engagement", stance: "firm", text: "Client hereby grants FE International the exclusive right to market the Business during the Exclusivity Period. Client shall not solicit, accept, or facilitate any offer, inquiry, or negotiation with any third party without FE's prior written consent.", notes: "<strong>Where we hold firm:</strong> We operate exclusively in all engagements. Pre-existing buyer relationships must be disclosed at signing." },
      { title: "Pre-Existing Buyer Carve-Outs", stance: "negotiable", text: 'The following buyers, identified on Schedule B, are excluded from the Exclusivity Period: [Buyer Names]. If a transaction is consummated with any such excluded buyer, a reduced success fee of [X]% shall apply.', notes: "<strong>Negotiable:</strong> We routinely agree to carve out 1–3 pre-existing buyer relationships with a reduced fee schedule. More than 5 carve-outs require Partner approval." },
      { title: "No-Shop Covenant", stance: "firm", text: "During the Exclusivity Period, Client shall promptly refer to FE International any buyer inquiry received directly. Client shall not engage in any discussions or share any materials with prospective buyers without FE's written consent.", notes: "<strong>Where we hold firm:</strong> Breach of the no-shop has triggered tail fee claims in multiple past engagements." },
      { title: "Exclusivity Period Duration", stance: "flexible", text: 'The initial Exclusivity Period shall be [12] months from the Engagement Date, automatically renewing for successive [3]-month periods unless either party provides written notice of non-renewal.', notes: "<strong>Flexible:</strong> We have agreed to 6-month initial periods on smaller mandates. Auto-renewal has been waived in 15–20% of engagements." }
    ]
  },
  liability: {
    title: "Liability & Indemnity",
    desc: "Limitation of liability caps, indemnification obligations, and how we handle third-party claims.",
    items: [
      { title: "Limitation of Liability", stance: "firm", text: "In no event shall FE International's aggregate liability arising out of or related to this Agreement exceed the total fees paid to FE International in the twelve (12) months preceding the claim.", notes: "<strong>Where we hold firm:</strong> The fee-cap on liability has never been waived. Mutual limitation of liability can be agreed." },
      { title: "Exclusion of Consequential Damages", stance: "firm", text: 'Neither party shall be liable to the other for any indirect, incidental, special, exemplary, or consequential damages, including lost profits, loss of business, or reputational harm.', notes: "<strong>Where we hold firm:</strong> This exclusion is standard in 100% of our agreements." },
      { title: "Client Indemnification of FE", stance: "firm", text: "Client shall indemnify, defend, and hold harmless FE International from any third-party claim arising from: (i) Client's breach of this Agreement; (ii) inaccuracy in information provided by Client; (iii) Client's own conduct in the sale process.", notes: "<strong>Where we hold firm:</strong> Client indemnity of FE for Client-caused claims is always maintained." },
      { title: "Reliance on Client Information", stance: "firm", text: 'FE International shall be entitled to rely on information provided by Client without independent verification. Client warrants that all information provided is accurate, complete, and not misleading.', notes: "<strong>Where we hold firm:</strong> We do not accept liability for errors in buyer materials that originate from Client-provided data." },
      { title: "Dispute Resolution", stance: "negotiable", text: 'Any dispute arising out of this Agreement shall be resolved by binding arbitration administered by the American Arbitration Association in [New York], applying the laws of the State of [New York].', notes: "<strong>Negotiable:</strong> Jurisdiction has been agreed as UK or Singapore for international mandates." }
    ]
  },
  termination: {
    title: "Termination Rights",
    desc: "Conditions under which either party may terminate, notice periods, and fee obligations post-termination.",
    items: [
      { title: "Termination for Convenience", stance: "negotiable", text: "Either party may terminate this Agreement for convenience upon [30] days' written notice. Termination shall not affect Client's obligation to pay fees earned prior to the termination date.", notes: "<strong>Negotiable:</strong> Notice period has ranged from 15 to 60 days." },
      { title: "Termination for Cause", stance: "firm", text: 'Either party may terminate this Agreement immediately upon written notice if the other party materially breaches this Agreement and fails to cure such breach within [10] business days after receiving written notice.', notes: "<strong>Where we hold firm:</strong> The 10-day cure period is standard." },
      { title: "Fee Obligations Upon Termination", stance: "firm", text: 'Upon termination, Client shall remain obligated to pay all accrued retainer fees and any success fees earned prior to termination. The tail period provisions shall survive termination.', notes: "<strong>Where we hold firm:</strong> Accrued fee obligations always survive termination." },
      { title: "Termination Following Failed Process", stance: "flexible", text: "In the event FE International is unable to identify a suitable buyer at or above Client's stated minimum price within the Exclusivity Period, either party may elect to terminate without further obligation.", notes: "<strong>Flexible:</strong> Agreed where the seller sets an unrealistic price floor. Does not affect retainer fees already paid." }
    ]
  },
  confidentiality: {
    title: "Confidentiality",
    desc: "NDA terms, data room access controls, and buyer confidentiality obligations we impose through the sale process.",
    items: [
      { title: "Mutual Non-Disclosure", stance: "flexible", text: 'Each party agrees to hold in confidence all Confidential Information received from the other party and to use such information solely for the purpose of evaluating or consummating the Transaction.', notes: "<strong>Flexible:</strong> Mutual NDAs are standard. We have agreed to one-way NDAs in some cases. Standard term is 2–3 years post-engagement." },
      { title: "Buyer NDA Obligations", stance: "firm", text: "FE International shall require all prospective buyers to execute a confidentiality agreement in FE's standard form prior to receiving any Confidential Information.", notes: "<strong>Where we hold firm:</strong> All buyers must sign our NDA before data room access. We do not use buyer-drafted NDAs." },
      { title: "Announcements & Press", stance: "firm", text: 'Neither party shall make any public announcement regarding the Transaction or this Agreement without the prior written consent of the other party, except as required by law.', notes: "<strong>Where we hold firm:</strong> FE reserves the right to list closed transactions in our deal history (anonymized) without client consent." }
    ]
  },
  tail: {
    title: "Tail Period",
    desc: "Post-termination success fee rights if a buyer introduced during the engagement later completes a transaction.",
    items: [
      { title: "Tail Period — Standard", stance: "firm", text: 'If, within [24] months following the termination or expiration of this Agreement, Client consummates a Transaction with any person or entity contacted or introduced by FE International during the Engagement, Client shall pay FE a success fee as if this Agreement were still in effect.', notes: "<strong>Where we hold firm:</strong> The 24-month tail is standard and has been enforced. We have not agreed to tails shorter than 12 months." },
      { title: "Tail Period — Reduced Fee", stance: "negotiable", text: 'Notwithstanding the standard tail provisions, if the Transaction is consummated after [12] months following termination, the applicable success fee shall be reduced by [25]%.', notes: "<strong>Negotiable:</strong> We have agreed to a declining fee schedule for years 2–3 of the tail in approximately 20% of engagements. Never below 75% of the standard rate." },
      { title: "Tail Buyer Log", stance: "firm", text: 'FE International shall provide Client with a written log of all buyers contacted during the Engagement within [10] business days of termination.', notes: "<strong>Where we hold firm:</strong> The buyer log process is essential to tail enforcement." }
    ]
  },
  diligence: {
    title: "Due Diligence Access",
    desc: "Data room obligations, information sharing protocols, and what buyers are entitled to review.",
    items: [
      { title: "Data Room Obligations", stance: "firm", text: 'Client shall maintain an organized virtual data room containing the documents listed in Schedule C. Client shall update such materials within [5] business days of any material change.', notes: "<strong>Where we hold firm:</strong> FE manages the data room. Seller-controlled data rooms have led to deal failures and are discouraged." },
      { title: "Management Access for Buyers", stance: "negotiable", text: "FE shall facilitate, at Client's discretion, up to [3] management presentations with qualified buyers prior to LOI.", notes: "<strong>Negotiable:</strong> Management call frequency varies by deal. We have agreed to limit buyer access to post-LOI in some cases." },
      { title: "Financial Statement Requirements", stance: "firm", text: 'Client shall provide FE with [3] years of profit and loss statements, balance sheets, and tax returns, as well as trailing twelve-month financials, prior to marketing commencement.', notes: "<strong>Where we hold firm:</strong> Full financials are required before any buyer outreach." },
      { title: "Third-Party Verification", stance: "flexible", text: "At FE's reasonable request, Client shall consent to analytics, revenue, and traffic verification by FE-designated third parties.", notes: "<strong>Flexible:</strong> Traffic/revenue verification is standard for content and SaaS businesses. Waived for established enterprise clients with audited financials." }
    ]
  },
  closing: {
    title: "Closing Conditions",
    desc: "Conditions precedent to closing, FE's role at closing, and fee payment mechanics.",
    items: [
      { title: "Fee Payment at Closing", stance: "firm", text: "The Success Fee shall be paid to FE International simultaneously with or prior to the closing of the Transaction, from the gross Transaction proceeds, via wire transfer to FE's designated account.", notes: "<strong>Where we hold firm:</strong> Payment at closing is non-negotiable. We do not accept deferred fee payments tied to earnout milestones without explicit agreement and escrow." },
      { title: "FE's Role in Documentation", stance: "flexible", text: "FE International shall cooperate with Client's legal counsel in reviewing deal documentation to ensure consistency with agreed commercial terms.", notes: "<strong>Flexible:</strong> We take an active commercial review role. We do not act as legal counsel." },
      { title: "Conditions Precedent to Closing", stance: "firm", text: "FE's obligation to assist in closing is conditioned upon: (i) execution of a definitive agreement; (ii) satisfaction of all buyer contingencies; (iii) payment of all fees due to FE International.", notes: "<strong>Where we hold firm:</strong> Fee payment is always a condition we insist upon." },
      { title: "Earnout Monitoring", stance: "negotiable", text: 'Where the Transaction includes an earnout component, FE International shall have the right, but not the obligation, to review quarterly earnout calculations during the earnout period.', notes: "<strong>Negotiable:</strong> We offer post-close monitoring as a premium service." }
    ]
  }
};

const CLAUSE_CATEGORIES = [
  { key: "fees", label: "Fee Structures", count: 7 },
  { key: "exclusivity", label: "Exclusivity & Lock-up", count: 4 },
  { key: "liability", label: "Liability & Indemnity", count: 5 },
  { key: "termination", label: "Termination Rights", count: 4 },
  { key: "confidentiality", label: "Confidentiality", count: 3 },
  { key: "tail", label: "Tail Period", count: 3 },
  { key: "diligence", label: "Due Diligence Access", count: 4 },
  { key: "closing", label: "Closing Conditions", count: 4 },
];

const TOOL_INFO = {
  briefing: { title: "📋 Prospect Briefing", sub: "Big 4-format internal briefing · Revenue drivers, 3yr trend, market position" },
  teaser: { title: "◈ Deal Teaser", sub: "Institutional CIM-standard teaser · Anonymity controls + narrative focus" },
  valuation_narrative: { title: "⬡ Valuation Narrative", sub: "Audit-grade valuation · Revenue quality, EBITDA normalisation, comp multiples" },
  risk: { title: "⚠ Commercial Risk Lens", sub: "Structured risk snapshot · Customer concentration, margin stability, contract visibility" },
  vri: { title: "🧠 Valuation Request Intelligence", sub: "Inbound deal qualification — one ingest run fills benchmarking, intent score, and response draft; batch scoring is a separate flow." },
};

const EMPTY_ORIG_FORMS = {
  briefing: {
    domain: "", ind: "", rev: "", ebitda: "", growth: "", rev1: "", recur: "", conc: "", churn: "", pricing: "", mktpos: "", ctx: "",
  },
  teaser: {
    model: "", ind: "", rev: "", ebitda: "", growth: "", price: "", recur: "", churn: "", conc: "", str: "", ebnotes: "",
    anon: "High", narrative: "Growth Story", redFlag: false,
    supporting_documents: [],
  },
  valuation_narrative: {
    name: "", ind: "", rev: "", ebitda: "", growth: "", recur: "", conc: "", churn: "", mktpos: "", ebnotes: "", hi: "",
  },
  risk: {
    name: "", ind: "", rev: "", ebitda: "", growth: "", ebitdaPrior: "", top5: "", top10: "", top1: "", recur: "", churn: "", contract: "", ctx: "",
  },
  vri: {
    step: 1,
    url: "", ind: "", rev: "", ebitda: "", growth: "", ctx: "",
    supporting_documents: [],
    step1Output: "", step2Output: "", step3Output: "", responseDraft: "",
    batchUrls: "",
    batchRawOutput: "",
    stepError: "",
  },
};

const EXAMPLE_QUERIES = [
  { label: "SaaS multiples", q: "Show SaaS deals with EV/Rev above 5x" },
  { label: "Affiliate buyers", q: "Find affiliate companies" },
  { label: "eComm comps", q: "Find e-Commerce companies with sale price between 500K and 1M" },
  { label: "High multiples", q: "Show deals with EV/EBITDA above 6x" },
  { label: "Advertising 2018", q: "Show deals in advertising in year 2018" },
  { label: "Content Australia", q: "Content deals in Australia" }
];

// ═══════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════

const fmt = (n) => n ? "$" + (n >= 1e6 ? (n / 1e6).toFixed(1) + "M" : n >= 1e3 ? (n / 1e3).toFixed(0) + "K" : n.toFixed(0)) : "—";
const fmtX = (n) => (n && n > 0) ? n.toFixed(2) + "x" : "—";
/** pandas `to_json` often sends DATE/DATETIME as epoch ms; also accepts ISO strings. */
const fmtDealDate = (v) => {
  if (v == null || v === "") return "—";
  const trimmed = typeof v === "string" ? v.trim() : "";
  const asNum = typeof v === "number" ? v : trimmed && /^\d+\.?\d*$/.test(trimmed) ? Number(trimmed) : NaN;
  if (Number.isFinite(asNum) && asNum > 1e8) {
    const ms = asNum > 1e11 ? asNum : asNum * 1000;
    const d = new Date(ms);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    }
  }
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }
  return String(v);
};
const med = (arr) => {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};
const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
/** Coerce to a usable positive number for multiples / amounts (pandas/JSON may send strings). */
const finitePositive = (v) => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

function normalizeIntelDeal(row) {
  if (!row || typeof row !== "object") return row;
  const id = row.id != null ? Number(row.id) : row.deal_id != null ? Number(row.deal_id) : null;
  return { ...row, id };
}

/**
 * Per-column configuration for the Excel-style multi-select header filters on
 * the deals table. `value(d)` MUST return the same string the cell displays so
 * that the filter list mirrors what the user sees in the row.
 */
const DEAL_COLUMN_FILTER_CONFIG = {
  name:     { label: "Company",     value: (d) => d.name || d.domain || "—", popAlign: "left" },
  industry: { label: "Industry",    value: (d) => d.industry || "—",         popAlign: "left" },
  geo:      { label: "Location",    value: (d) => d.geo || "—",              popAlign: "left" },
  year:     { label: "Year",        value: (d) => (d.year != null && d.year !== "" ? String(d.year) : "—"), popAlign: "left" },
  rev:      { label: "Revenue LTM", value: (d) => fmt(d.rev),                popAlign: "right" },
  ebitda:   { label: "EBITDA LTM",  value: (d) => fmt(d.ebitda),             popAlign: "right" },
  sale:     { label: "Sale Price",  value: (d) => fmt(d.sale),               popAlign: "right" },
  ev_eb:    { label: "EV/EBITDA",   value: (d) => fmtX(d.ev_eb),             popAlign: "right" },
  ev_rev:   { label: "EV/Rev",      value: (d) => fmtX(d.ev_rev),            popAlign: "right" },
  buyer:    { label: "Buyer",       value: (d) => d.buyer || "—",            popAlign: "right" },
  cim:      { label: "CIM",         value: (d) => (cimHref(d.cim) ? "Has link" : "—"), popAlign: "right" },
};

/** Apply Excel-style column multi-select filters to a list of deal rows. */
function applyDealColumnFilters(rows, columnFilters) {
  const active = Object.entries(columnFilters || {}).filter(
    ([, vals]) => Array.isArray(vals) && vals.length > 0
  );
  if (active.length === 0) return rows;
  return rows.filter((d) =>
    active.every(([key, vals]) => {
      const cfg = DEAL_COLUMN_FILTER_CONFIG[key];
      if (!cfg) return true;
      return vals.includes(cfg.value(d));
    })
  );
}

/** URL for CIM column link; null if empty. */
function cimHref(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
}

const EMPTY_ADD_DEAL_FORM = {
  deal_id: "",
  name: "",
  domain: "",
  url: "",
  industry: "",
  sub: "",
  geo: "",
  year: "",
  asking: "",
  rev: "",
  ebitda: "",
  sale: "",
  buyer: "",
  cim: "",
  seller: "",
  finalised: "",
  comm_pct: "",
  offer_t: "",
  exec_t: "",
  total_t: "",
  ev_rev: "",
  ev_eb: "",
  sale_mult: "",
  ask_mult: "",
};

function MaNewsThumbnail({ src }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  const showImg = Boolean(src) && !failed;
  return (
    <div className="news-card-thumb-wrap">
      {showImg ? (
        <img className="news-card-thumb" src={src} alt="" onError={() => setFailed(true)} />
      ) : (
        <div className="news-card-thumb-ph" aria-hidden>
          FE
        </div>
      )}
    </div>
  );
}

function formatNewsDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Flask backend proxy URL — adjust to your setup
const API_BASE = "/api";

const NEWS_PER_PAGE = 21;

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

// ═══════════════════════════════════════════════
// STYLES (injected as <style> tag)
// ═══════════════════════════════════════════════

const GLOBAL_STYLES = `
/* Webfonts: load via index.html <link> (preconnect + stylesheet) — not @import here (production/CSP/async issues). */

:root {
  --navy:#212246; --steel:#899FB7; --blue:#2FA9FE; --teal:#30C0CA;
  --purple:#906BF6; --grey:#C6C6C6;
  --navy-dark:#16172e; --navy-mid:#2a2d5c;
  --blue-glow:rgba(47,169,254,0.15); --teal-glow:rgba(48,192,202,0.12);
  --purple-glow:rgba(144,107,246,0.15);
  --white:#ffffff; --off-white:#f0f2f8; --surface:#f7f9fc;
  --border:rgba(198,198,198,0.4); --border-hi:rgba(47,169,254,0.3);
  --text-main:#1a1c38; --text-sub:#5a6480; --text-dim:#8892a8;
  --radius-sm:6px; --radius:10px; --radius-lg:16px;
  --shadow:0 2px 16px rgba(33,34,70,0.08);
  --shadow-lg:0 8px 48px rgba(33,34,70,0.14);
  --shadow-blue:0 4px 24px rgba(47,169,254,0.18);
}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth;-webkit-text-size-adjust:100%;text-size-adjust:100%}
body{font-family:'Bricolage Grotesque','Segoe UI',system-ui,-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;background:var(--off-white);color:var(--text-main);min-height:100vh;font-size:14px;line-height:1.55;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility}

.topbar{background:var(--navy);height:60px;display:flex;align-items:center;padding:0 28px;position:sticky;top:0;z-index:200;border-bottom:1px solid rgba(47,169,254,0.2);background-image:radial-gradient(ellipse 40% 60% at 0% 50%,rgba(48,192,202,0.08) 0%,transparent 60%),radial-gradient(ellipse 30% 80% at 100% 50%,rgba(144,107,246,0.07) 0%,transparent 60%)}
.brand{display:flex;align-items:center;gap:12px;margin-right:36px;flex-shrink:0}
.brand-logo-img{height:30px;width:auto;object-fit:contain;flex-shrink:0}
.brand-divider{width:1px;height:24px;background:rgba(137,159,183,0.35);margin:0 4px}
.brand-name{font-family:'Clash Display',sans-serif;font-weight:600;font-size:18px;color:#fff;letter-spacing:-0.3px}
.brand-name span{background:linear-gradient(90deg,var(--blue),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.brand .nav-tab-brand{height:60px;padding:0 8px 0 4px;margin:0;border-radius:0;font-family:inherit}
.brand .nav-tab-brand .brand-name{pointer-events:none}
.nav-tabs{display:flex;height:60px;flex:1}
.nav-tab{height:100%;padding:0 20px;background:none;border:none;border-bottom:2px solid transparent;font-family:'Bricolage Grotesque',sans-serif;font-size:13px;font-weight:500;color:var(--steel);cursor:pointer;display:flex;align-items:center;gap:8px;transition:all 0.18s;white-space:nowrap}
.nav-tab:hover{color:#c8d4e0}
.nav-tab.active{color:#fff;border-bottom-color:var(--blue);background:rgba(47,169,254,0.07)}
.nav-tab:disabled{opacity:0.45;cursor:not-allowed}
.nav-tab:disabled:hover{color:var(--steel)}
.nav-tab:disabled .nav-tab-icon{opacity:0.8}
.nav-tab-icon{width:18px;height:18px;border-radius:4px;display:flex;align-items:center;justify-content:center;font-size:11px;flex-shrink:0}
.icon-deals{background:linear-gradient(135deg,var(--blue),var(--teal))}
.icon-clause{background:linear-gradient(135deg,var(--teal),#2de0bd)}
.icon-orig{background:linear-gradient(135deg,var(--purple),#c472f8)}
.icon-metrics{background:linear-gradient(135deg,#10b981,#30C0CA)}
.icon-exec{background:linear-gradient(135deg,#f59e0b,#ef4444)}
.metrics-page{padding:32px;max-width:1020px;margin:0 auto}
.metrics-hero{margin-bottom:28px}
.metrics-hero h2{font-family:'Clash Display',sans-serif;font-weight:700;font-size:30px;letter-spacing:-0.5px;margin-bottom:6px}
.metrics-hero h2 span{background:linear-gradient(90deg,#10b981,var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.metrics-hero p{font-size:14px;color:var(--text-sub)}
.metrics-tools-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:24px}
.metrics-tool-card{background:#fff;border:1.5px solid var(--border);border-radius:var(--radius);padding:22px 24px;cursor:pointer;transition:all 0.18s;position:relative;overflow:hidden;display:flex;align-items:flex-start;gap:14px;text-align:left;width:100%;font:inherit}
.metrics-tool-card::after{content:'';position:absolute;bottom:0;left:0;right:0;height:3px;transform:scaleX(0);transform-origin:left;transition:transform 0.2s}
.metrics-tool-card.mt-val::after{background:linear-gradient(90deg,var(--blue),var(--teal))}
.metrics-tool-card.mt-saas::after{background:linear-gradient(90deg,#10b981,var(--teal))}
.metrics-tool-card:hover,.metrics-tool-card.active-mt{border-color:transparent;box-shadow:0 0 0 1.5px var(--blue),var(--shadow-blue)}
.metrics-tool-card:hover::after,.metrics-tool-card.active-mt::after{transform:scaleX(1)}
.metrics-tool-card.active-mt{border-color:var(--blue)}
.mt-icon{width:42px;height:42px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0;color:#fff}
.mt-icon.v{background:linear-gradient(135deg,var(--blue),var(--teal))}
.mt-icon.s{background:linear-gradient(135deg,#10b981,var(--teal))}
.mt-card-title{font-family:'Clash Display',sans-serif;font-weight:600;font-size:15px;color:var(--text-main);margin-bottom:4px}
.mt-card-desc{font-size:12px;color:var(--text-dim);line-height:1.5}
.metrics-workspace{background:#fff;border:1.5px solid var(--border);border-radius:var(--radius-lg);overflow:hidden}
.metrics-ws-header{display:flex;align-items:center;justify-content:space-between;padding:16px 22px;border-bottom:1px solid var(--border);background:var(--surface);gap:12px;flex-wrap:wrap}
.metrics-ws-title{font-family:'Clash Display',sans-serif;font-weight:700;font-size:16px;color:var(--text-main)}
.metrics-ws-sub{font-size:12px;color:var(--text-dim);margin-top:2px}
.metrics-ws-body{padding:28px}
.mf-section-title{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:2px;text-transform:uppercase;color:var(--blue);font-weight:600;margin:0 0 14px;padding-bottom:7px;border-bottom:1px solid var(--border)}
.mf-grid2{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:20px}
.mf-grid1{display:grid;grid-template-columns:1fr;gap:14px;margin-bottom:20px}
.mf-row{display:flex;flex-direction:column;gap:5px}
.mf-label{font-size:12.5px;font-weight:600;color:var(--text-sub)}
.mf-label-hint{font-size:11px;color:var(--text-dim);font-weight:400;margin-left:5px}
.mf-input{width:100%;padding:9px 12px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-size:13px;font-family:'Bricolage Grotesque',sans-serif;color:var(--text-main);background:var(--surface);outline:none;transition:border-color 0.15s;box-sizing:border-box}
.mf-input:focus{border-color:var(--blue);background:#fff}
.mf-select{width:100%;padding:9px 12px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-size:13px;font-family:'Bricolage Grotesque',sans-serif;color:var(--text-main);background:var(--surface);outline:none;cursor:pointer;transition:border-color 0.15s;box-sizing:border-box}
.mf-select:focus{border-color:var(--blue);background:#fff}
.mf-textarea{width:100%;padding:9px 12px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-size:13px;font-family:'Bricolage Grotesque',sans-serif;color:var(--text-main);background:var(--surface);outline:none;resize:vertical;min-height:80px;transition:border-color 0.15s;box-sizing:border-box}
.mf-textarea:focus{border-color:var(--blue);background:#fff}
.mf-slider-wrap{display:flex;flex-direction:column;gap:6px}
.mf-slider{width:100%;accent-color:var(--blue)}
.mf-slider-labels{display:flex;justify-content:space-between;font-family:'JetBrains Mono',monospace;font-size:9.5px;color:var(--text-dim)}
.mf-slider-val{font-family:'JetBrains Mono',monospace;font-size:11px;color:var(--blue);font-weight:600;text-align:right}
.mf-submit-btn{width:100%;padding:12px;background:var(--blue);border:none;border-radius:var(--radius-sm);font-family:'Clash Display',sans-serif;font-size:14px;font-weight:600;color:#fff;cursor:pointer;transition:all 0.15s;letter-spacing:0.2px}
.mf-submit-btn:hover{background:var(--teal)}
.mf-submit-btn:disabled{opacity:0.5;cursor:not-allowed}
.metrics-loading{display:flex;align-items:center;gap:12px;padding:24px;font-family:'JetBrains Mono',monospace;font-size:11px;color:var(--text-dim);letter-spacing:0.5px}
.metrics-spinner{width:18px;height:18px;border:2.5px solid var(--border);border-top-color:var(--blue);border-radius:50%;animation:rot 0.7s linear infinite;flex-shrink:0}
.metrics-output{padding:0 28px 28px}
.metrics-output-actions{display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap}
.mout-btn{padding:6px 14px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:0.5px;text-transform:uppercase;color:var(--text-dim);background:#fff;cursor:pointer;transition:all 0.15s}
.mout-btn:hover{border-color:var(--blue);color:var(--blue)}
.saas-mode-tabs{display:flex;gap:0;border-bottom:1px solid var(--border);margin-bottom:0;background:var(--surface)}
.saas-mode-tab{padding:11px 20px;cursor:pointer;font-size:13px;font-weight:600;color:var(--text-dim);border-bottom:2.5px solid transparent;transition:all 0.15s;display:flex;align-items:center;gap:7px;background:none;border-top:none;border-left:none;border-right:none;font-family:'Bricolage Grotesque',sans-serif}
.saas-mode-tab:hover{color:var(--text-sub)}
.saas-mode-tab.active{color:var(--blue);border-bottom-color:var(--blue)}
.csv-dropzone{display:block;border:2px dashed var(--border);border-radius:var(--radius-lg);padding:40px 20px;text-align:center;cursor:pointer;transition:all 0.18s;background:var(--surface);position:relative;margin-bottom:20px}
.csv-dropzone:hover,.csv-dropzone.dragging{border-color:var(--blue);background:rgba(47,169,254,0.04)}
.csv-dropzone.has-file{border-color:#10b981;background:rgba(16,185,129,0.04);border-style:solid}
.csv-dz-icon{font-size:32px;margin-bottom:10px;display:block}
.csv-dz-title{font-family:'Clash Display',sans-serif;font-weight:600;font-size:15px;color:var(--text-main);margin-bottom:5px}
.csv-dz-sub{font-size:12px;color:var(--text-dim);line-height:1.5}
.csv-dz-input{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%;height:100%}
.csv-file-info{display:flex;align-items:center;gap:10px;padding:12px 16px;background:rgba(16,185,129,0.06);border:1px solid rgba(16,185,129,0.2);border-radius:var(--radius);margin-bottom:16px}
.csv-file-icon{font-size:20px;flex-shrink:0}
.csv-file-name{font-family:'JetBrains Mono',monospace;font-size:12px;color:var(--text-main);font-weight:500}
.csv-file-size{font-size:11px;color:var(--text-dim);margin-top:2px}
.csv-remove-btn{margin-left:auto;padding:4px 10px;border:1px solid rgba(198,198,198,0.5);border-radius:var(--radius-sm);font-size:11px;color:var(--text-dim);background:#fff;cursor:pointer;transition:all 0.12s}
.csv-remove-btn:hover{border-color:#e74c3c;color:#e74c3c}
.csv-sample-label{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:2px;text-transform:uppercase;color:var(--text-dim);margin-bottom:8px}
.csv-sample-table-wrap{overflow-x:auto;border:1px solid var(--border);border-radius:var(--radius);max-height:200px;overflow-y:auto}
.csv-sample-table{width:100%;border-collapse:collapse;font-size:11.5px}
.csv-sample-table th{background:var(--surface);padding:7px 10px;text-align:left;font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:0.8px;text-transform:uppercase;color:var(--text-dim);border-bottom:1px solid var(--border);white-space:nowrap;position:sticky;top:0}
.csv-sample-table td{padding:6px 10px;border-bottom:1px solid rgba(198,198,198,0.15);color:var(--text-sub);white-space:nowrap;max-width:150px;overflow:hidden;text-overflow:ellipsis}
.csv-sample-table tr:last-child td{border-bottom:none}
.csv-mapping-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:20px}
.csv-map-row{display:flex;flex-direction:column;gap:5px}
.csv-map-label{font-size:12px;font-weight:600;color:var(--text-sub);display:flex;align-items:center;gap:6px}
.csv-map-req{width:6px;height:6px;border-radius:50%;background:var(--blue);flex-shrink:0}
.csv-map-select{width:100%;padding:8px 10px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-size:12.5px;font-family:'Bricolage Grotesque',sans-serif;color:var(--text-main);background:var(--surface);outline:none;cursor:pointer;transition:border-color 0.15s;box-sizing:border-box}
.csv-map-select:focus{border-color:var(--blue)}
.csv-map-hint{font-size:10.5px;color:var(--text-dim);margin-top:2px}
.csv-process-btn{width:100%;padding:12px;background:linear-gradient(90deg,#10b981,var(--teal));border:none;border-radius:var(--radius-sm);font-family:'Clash Display',sans-serif;font-size:14px;font-weight:600;color:#fff;cursor:pointer;transition:all 0.15s;letter-spacing:0.2px}
.csv-process-btn:hover{filter:brightness(1.06)}
.csv-process-btn:disabled{opacity:0.45;cursor:not-allowed}
.csv-processing{display:flex;align-items:center;gap:10px;padding:14px 0;font-family:'JetBrains Mono',monospace;font-size:11px;color:var(--text-dim)}
.saas-csv-chart-toolbar{display:flex;flex-wrap:wrap;gap:14px 22px;margin-bottom:14px;align-items:flex-end}
.saas-csv-chart-toolbar label{display:flex;flex-direction:column;gap:4px;font-size:11px;font-weight:600;color:var(--text-sub);font-family:'Bricolage Grotesque',sans-serif}
.saas-csv-chart-toolbar select{min-width:200px;padding:8px 10px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-size:12.5px;font-family:'Bricolage Grotesque',sans-serif;color:var(--text-main);background:var(--surface);cursor:pointer}
.saas-csv-recharts-wrap{width:100%;height:380px;margin-top:6px}
.saas-csv-pie-row{display:flex;flex-wrap:wrap;gap:20px;justify-content:space-between;margin-top:18px}
.saas-csv-pie-cell{flex:1;min-width:260px}
.saas-csv-pie-title{font-family:'Clash Display',sans-serif;font-weight:600;font-size:14px;margin-bottom:8px;color:var(--text-main)}
.saas-csv-export-row{display:flex;flex-wrap:wrap;gap:12px;align-items:center;margin-bottom:10px;padding-top:4px}
.metrics-placeholder-note{font-size:13px;color:var(--text-sub);line-height:1.75;padding:12px 0}
.metrics-placeholder-note strong{color:var(--text-main);font-weight:600}
.metrics-val-results{padding-top:8px}
.metrics-val-results-title{font-family:'Clash Display',sans-serif;font-weight:700;font-size:18px;margin:8px 0 12px;color:var(--text-main);text-align:center}
.metrics-val-intro{font-size:13px;color:var(--text-sub);line-height:1.7;text-align:center;margin:0 0 20px;padding-top:12px;border-top:1px solid var(--border)}
.metrics-val-intro strong{color:var(--text-main);font-weight:600}
.metrics-val-chart-wrap{height:380px;width:100%;max-width:720px;margin:0 auto 8px}
.metrics-val-table-title{font-family:'Clash Display',sans-serif;font-weight:600;font-size:14px;margin:22px 0 10px;padding-bottom:6px;border-bottom:1px solid var(--border);color:var(--text-main)}
.metrics-table-scroll{overflow-x:auto;margin-bottom:8px;border:1px solid var(--border);border-radius:var(--radius-sm)}
.metrics-data-table{width:100%;border-collapse:collapse;font-size:12px}
.metrics-data-table th{background:var(--surface);padding:8px 10px;text-align:left;font-family:'JetBrains Mono',monospace;font-size:10px;text-transform:uppercase;letter-spacing:0.4px;color:var(--text-dim);border-bottom:1px solid var(--border);white-space:nowrap}
.metrics-data-table td{padding:8px 10px;border-bottom:1px solid var(--border);color:var(--text-main)}
.metrics-data-table tr:last-child td{border-bottom:none}
.metrics-data-table th.num,.metrics-data-table td.num{text-align:right}
.metrics-data-table th.ctr,.metrics-data-table td.ctr{text-align:center}
.metrics-val-empty{font-size:12px;color:var(--text-dim);margin:0 0 12px}
@media(max-width:720px){.metrics-tools-grid{grid-template-columns:1fr}.metrics-page{padding:20px 16px}.mf-grid2{grid-template-columns:1fr}.csv-mapping-grid{grid-template-columns:1fr}}

.search-banner{background:#fff;border-bottom:1px solid var(--border);padding:12px 28px;display:flex;align-items:flex-start;gap:12px;position:sticky;top:60px;z-index:100;box-shadow:0 2px 8px rgba(33,34,70,0.06)}
.search-banner-inner{flex:1;display:flex;flex-direction:column;gap:8px;max-width:100%}
.search-banner-row{display:flex;align-items:center;gap:10px;max-width:860px}
.search-bar-wrap{position:relative;display:flex;align-items:center;flex:1}
.search-bar{width:100%;padding:11px 16px 11px 44px;border:2px solid var(--border);border-radius:10px;font-family:'Bricolage Grotesque',sans-serif;font-size:14px;color:var(--text-main);background:#fff;outline:none;transition:all 0.2s;box-shadow:0 1px 4px rgba(33,34,70,0.06)}
.search-bar.search-bar--clearable{padding-right:40px}
.search-bar:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-glow)}
.search-bar::placeholder{color:var(--text-dim)}
.search-icon-left{position:absolute;left:14px;top:50%;transform:translateY(-50%);color:var(--text-dim);font-size:16px;pointer-events:none}
.search-bar-clear{position:absolute;right:8px;top:50%;transform:translateY(-50%);z-index:2;display:flex;align-items:center;justify-content:center;width:30px;height:30px;margin:0;padding:0;border:none;border-radius:8px;background:transparent;color:var(--text-dim);font-size:20px;line-height:1;cursor:pointer;transition:background 0.15s,color 0.15s}
.search-bar-clear:hover{background:var(--off-white);color:var(--text-main)}
.search-bar-clear:focus{outline:2px solid var(--blue);outline-offset:1px}
.search-actions{display:flex;align-items:center;flex-shrink:0;gap:10px}
.ai-search-toggle-wrap{display:flex;align-items:center;gap:8px;flex-shrink:0;padding:0 4px 0 0;border-right:1px solid var(--border);margin-right:2px}
.ai-search-toggle-label{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:0.6px;text-transform:uppercase;color:var(--text-dim);user-select:none;white-space:nowrap}
.ai-search-switch{position:relative;width:44px;height:24px;border-radius:12px;background:var(--border);border:none;cursor:pointer;padding:0;flex-shrink:0;transition:background 0.2s}
.ai-search-switch.on{background:var(--blue)}
.ai-search-switch:focus-visible{outline:2px solid var(--blue);outline-offset:2px}
.ai-search-switch-thumb{position:absolute;top:3px;left:3px;width:18px;height:18px;border-radius:50%;background:#fff;transition:transform 0.2s;box-shadow:0 1px 3px rgba(33,34,70,0.12)}
.ai-search-switch.on .ai-search-switch-thumb{transform:translateX(20px)}
.search-go-btn{display:inline-flex;align-items:center;justify-content:center;padding:0 18px;height:44px;background:var(--blue);border:none;border-radius:10px;font-family:'Clash Display',sans-serif;font-weight:700;font-size:13px;color:#fff;cursor:pointer;letter-spacing:1px;transition:background 0.15s;white-space:nowrap;flex-shrink:0}
.search-go-btn:hover{background:#1a8ede}
.search-clear-btn{display:inline-flex;align-items:center;justify-content:center;padding:0 16px;height:44px;background:var(--surface);border:2px solid var(--border);border-radius:10px;font-family:'Clash Display',sans-serif;font-weight:600;font-size:12px;color:var(--text-dim);cursor:pointer;letter-spacing:0.5px;white-space:nowrap;transition:background 0.15s,color 0.15s;box-sizing:border-box}
.search-clear-btn:hover{background:var(--off-white);color:var(--text-main)}
.search-examples{display:flex;gap:6px;flex-wrap:wrap;align-items:center}
.ex-pill{font-family:'JetBrains Mono',monospace;font-size:9.5px;padding:4px 10px;background:var(--off-white);border:1px solid var(--border);border-radius:100px;color:var(--text-dim);cursor:pointer;transition:all 0.12s;white-space:nowrap;letter-spacing:0.3px}
.ex-pill:hover{background:var(--blue-glow);border-color:var(--blue);color:var(--blue)}
.search-loading{display:flex;align-items:center;gap:8px;font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--text-dim);letter-spacing:0.5px}
.deal-search-err{font-family:'JetBrains Mono',monospace;font-size:10px;color:#b91c1c;margin-top:6px;letter-spacing:0.3px}
.spin-sm{width:14px;height:14px;border:2px solid var(--off-white);border-top-color:var(--blue);border-radius:50%;animation:rot 0.7s linear infinite}
@keyframes rot{to{transform:rotate(360deg)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
@keyframes fadeUp{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:none}}
@keyframes blink{0%,80%,100%{opacity:0.25;transform:scale(0.7)}40%{opacity:1;transform:scale(1)}}

/* ══════════════════════════════════════════════
   HOME — GLOBAL M&A FEED HEADING
══════════════════════════════════════════════ */
#panel-home{background:var(--off-white);min-height:calc(100vh - 60px)}
.feed-page-heading{max-width:680px;margin:0 auto;padding:32px 24px 0}
.feed-page-heading h1{font-family:'Clash Display',sans-serif;font-weight:700;font-size:26px;color:var(--navy);letter-spacing:-0.4px;line-height:1.2;margin:0 0 4px}
.feed-page-heading h1 em{font-style:normal;background:linear-gradient(90deg,var(--blue),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.feed-page-sub{font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-dim);margin-top:6px}

.home-news-wrap{max-width:960px;margin:0 auto;padding:28px 24px 48px}
.home-news-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:18px;flex-wrap:wrap}
.home-news-title{font-family:'Clash Display',sans-serif;font-weight:600;font-size:15px;color:var(--navy);letter-spacing:-0.2px}
.home-news-meta{font-family:'JetBrains Mono',monospace;font-size:9.5px;letter-spacing:0.5px;color:var(--text-dim)}
.home-news-filter{display:flex;align-items:center;gap:8px;margin-left:auto}
.home-news-filter-label{font-family:'JetBrains Mono',monospace;font-size:9px;font-weight:500;letter-spacing:1.8px;text-transform:uppercase;color:var(--text-dim)}
.home-news-industry-select{padding:7px 28px 7px 10px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-size:12px;color:var(--text-main);background:#fff;outline:none;cursor:pointer;appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23899FB7'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 10px center;min-width:180px;max-width:260px;transition:border-color 0.15s}
.home-news-industry-select:focus{border-color:var(--blue)}
.home-news-industry-select:disabled{opacity:0.5;cursor:not-allowed}
.news-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px}
.news-card{display:flex;flex-direction:column;background:#fff;border:1.5px solid var(--border);border-radius:var(--radius-lg);overflow:hidden;transition:box-shadow 0.18s,border-color 0.18s;text-decoration:none;color:inherit}
.news-card:hover{border-color:var(--border-hi);box-shadow:var(--shadow)}
.news-card-thumb-wrap{aspect-ratio:16/9;background:var(--surface);position:relative;overflow:hidden}
.news-card-thumb{width:100%;height:100%;object-fit:cover}
.news-card-thumb-ph{display:flex;align-items:center;justify-content:center;font-family:'Clash Display',sans-serif;font-size:22px;font-weight:600;color:var(--text-dim);opacity:0.35;background:linear-gradient(135deg,rgba(47,169,254,0.06),rgba(48,192,202,0.08))}
.news-card-body{padding:14px 16px 16px;display:flex;flex-direction:column;gap:8px;flex:1}
.news-card-source{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:1px;text-transform:uppercase;color:var(--blue)}
.news-card-title{font-family:'Bricolage Grotesque',sans-serif;font-weight:600;font-size:13.5px;line-height:1.35;color:var(--text-main);display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.news-card-snippet{font-size:12px;line-height:1.5;color:var(--text-sub);display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.news-card-foot{margin-top:auto;padding-top:6px;display:flex;align-items:center;justify-content:space-between;gap:8px;border-top:1px solid var(--border);padding-top:10px}
.news-card-date{font-family:'JetBrains Mono',monospace;font-size:9.5px;color:var(--text-dim)}
.news-card-cta{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:0.5px;color:var(--blue)}
.news-pagi{display:flex;align-items:center;justify-content:center;gap:8px;margin-top:24px;flex-wrap:wrap}
.news-pagi-btn{padding:8px 14px;border:1.5px solid var(--border);border-radius:var(--radius-sm);background:#fff;font-family:'JetBrains Mono',monospace;font-size:9.5px;letter-spacing:0.5px;text-transform:uppercase;color:var(--text-dim);cursor:pointer;transition:all 0.12s}
.news-pagi-btn:hover:not(:disabled){border-color:var(--blue);color:var(--blue);background:var(--blue-glow)}
.news-pagi-btn:disabled{opacity:0.4;cursor:not-allowed}
.news-pagi-info{font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--text-dim);padding:0 8px}

.arp-mult{display:inline-block;padding:2px 7px;border-radius:4px;font-family:'JetBrains Mono',monospace;font-size:10.5px;font-weight:500}
.mult-high{background:rgba(48,192,202,0.1);color:#0e7490;border:1px solid rgba(48,192,202,0.25)}
.mult-mid{background:rgba(47,169,254,0.1);color:#1a7ec4;border:1px solid rgba(47,169,254,0.2)}
.mult-low{background:rgba(198,198,198,0.2);color:var(--text-dim);border:1px solid var(--border)}

.intel-wrap{display:flex;min-height:calc(100vh - 60px)}
.sidebar{width:280px;flex-shrink:0;background:var(--white);border-right:1px solid var(--border);padding:18px 14px;overflow-y:auto;position:sticky;top:60px;height:calc(100vh - 60px)}
.main-content{flex:1;min-width:0;display:flex;flex-direction:column}
.sidebar-group{margin-bottom:16px}
.filter-label{font-family:'JetBrains Mono',monospace;font-size:9px;font-weight:500;letter-spacing:1.8px;text-transform:uppercase;color:var(--text-dim);display:block;margin-bottom:6px}
.filter-select{width:100%;padding:8px 28px 8px 10px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-size:12.5px;color:var(--text-main);background:var(--surface);outline:none;cursor:pointer;appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23899FB7'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 10px center;transition:border-color 0.15s;margin-bottom:7px}
.filter-select:focus{border-color:var(--blue);outline:none}
.range-row{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:7px}
.range-field label{font-size:9px;color:var(--text-dim);font-family:'JetBrains Mono',monospace;letter-spacing:0.5px;display:block;margin-bottom:3px}
.range-input{width:100%;padding:7px 9px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'JetBrains Mono',monospace;font-size:11px;color:var(--text-main);background:var(--surface);outline:none;transition:border-color 0.15s}
.range-input:focus{border-color:var(--blue)}
.sidebar-divider{height:1px;background:var(--border);margin:14px -14px}
.reset-btn{width:100%;padding:9px;border:1.5px solid var(--border);border-radius:var(--radius-sm);background:none;font-family:'JetBrains Mono',monospace;font-size:9.5px;letter-spacing:1px;color:var(--text-dim);cursor:pointer;transition:all 0.15s;text-transform:uppercase}
.reset-btn:hover{border-color:var(--blue);color:var(--blue);background:var(--blue-glow)}

.stats-strip{background:#212246;display:grid;grid-template-columns:repeat(4,1fr);border-bottom:1px solid rgba(47,169,254,0.15)}
.stat-cell{padding:14px 20px;border-right:1px solid rgba(137,159,183,0.15);display:flex;flex-direction:column;align-items:center;gap:2px}
.stat-cell:last-child{border-right:none}
.stat-val{font-family:'Clash Display',sans-serif;font-weight:700;font-size:22px;color:#2FA9FE;letter-spacing:-0.5px;line-height:1}
.stat-lbl{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:1.2px;text-transform:uppercase;color:#ffffff;font-weight:700;margin-top:1px;opacity:0.85}

.results-bar{background:#fff;padding:11px 20px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid var(--border);gap:12px}
.results-count{font-family:'JetBrains Mono',monospace;font-size:11px;color:var(--text-dim)}
.results-count strong{color:var(--text-main);font-weight:500}
.sort-group{display:flex;gap:5px;align-items:center}
.sort-lbl{font-family:'JetBrains Mono',monospace;font-size:9.5px;letter-spacing:1px;color:var(--text-dim);margin-right:3px;text-transform:uppercase}
.sort-pill{font-family:'JetBrains Mono',monospace;font-size:10px;padding:4px 10px;border:1.5px solid var(--border);border-radius:100px;background:none;color:var(--text-dim);cursor:pointer;letter-spacing:0.3px;transition:all 0.12s}
.sort-pill:hover{border-color:var(--blue);color:var(--blue)}
.sort-pill.active{border-color:var(--blue);color:var(--blue);background:var(--blue-glow)}
.add-deal-btn{display:inline-flex;align-items:center;justify-content:center;padding:0 14px;height:32px;background:var(--navy);border:1.5px solid rgba(47,169,254,0.35);border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:500;letter-spacing:0.4px;color:#fff;cursor:pointer;transition:all 0.12s;white-space:nowrap;flex-shrink:0}
.add-deal-btn:hover{background:var(--blue);border-color:var(--blue)}
.export-deals-btn{display:inline-flex;align-items:center;justify-content:center;padding:0 14px;height:32px;background:#fff;border:1.5px solid var(--border);border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:500;letter-spacing:0.4px;color:var(--text-dim);cursor:pointer;transition:all 0.12s;white-space:nowrap;flex-shrink:0}
.export-deals-btn:hover:not(:disabled){border-color:var(--blue);color:var(--blue)}
.export-deals-btn:disabled{opacity:0.45;cursor:not-allowed}
.add-deal-modal-overlay{position:fixed;inset:0;background:rgba(16,17,38,0.55);z-index:302;backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:24px}
.add-deal-modal{background:var(--white);border-radius:var(--radius-lg);box-shadow:var(--shadow-lg);max-width:720px;width:100%;max-height:min(90vh,880px);display:flex;flex-direction:column;border:1px solid var(--border);overflow:hidden;min-height:0}
.add-deal-modal-head{padding:18px 22px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;gap:12px;flex-shrink:0;background:var(--surface);border-radius:var(--radius-lg) var(--radius-lg) 0 0}
.add-deal-modal-title{font-family:'Clash Display',sans-serif;font-weight:600;font-size:17px;color:var(--navy);letter-spacing:-0.2px}
.add-deal-modal-x{background:rgba(137,159,183,0.15);border:none;color:var(--text-dim);width:32px;height:32px;border-radius:8px;cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;transition:all 0.12s}
.add-deal-modal-x:hover{background:rgba(47,169,254,0.15);color:var(--blue)}
.add-deal-modal-scroll{padding:18px 22px;overflow-y:auto;flex:1;min-height:0}
.add-deal-form-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px 16px}
@media(max-width:640px){.add-deal-form-grid{grid-template-columns:1fr}}
.add-deal-field{display:flex;flex-direction:column;gap:5px}
.add-deal-field label{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:0.8px;text-transform:uppercase;color:var(--text-dim)}
.add-deal-field input,.add-deal-field-full input{width:100%;padding:8px 11px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-size:13px;color:var(--text-main);background:var(--surface);outline:none;transition:border-color 0.15s;box-sizing:border-box}
.add-deal-field input:focus,.add-deal-field-full input:focus{border-color:var(--blue)}
.add-deal-field-full{grid-column:1/-1}
.add-deal-modal-foot{padding:14px 22px 18px;border-top:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;flex-shrink:0;background:var(--white);border-radius:0 0 var(--radius-lg) var(--radius-lg)}
.add-deal-submit{display:inline-flex;align-items:center;justify-content:center;padding:0 20px;height:40px;background:var(--blue);border:none;border-radius:var(--radius-sm);font-family:'Clash Display',sans-serif;font-weight:600;font-size:13px;color:#fff;cursor:pointer;letter-spacing:0.5px;transition:background 0.15s}
.add-deal-submit:hover:not(:disabled){background:#1a8ede}
.add-deal-submit:disabled{opacity:0.5;cursor:not-allowed}
.add-deal-msg-success{font-family:'Bricolage Grotesque',sans-serif;font-size:18px;font-weight:600;color:#0f766e;line-height:1.4}
.add-deal-msg-err{font-family:'Bricolage Grotesque',sans-serif;font-size:14px;font-weight:500;color:#b91c1c;line-height:1.4;max-width:420px}

.table-wrap{overflow-x:auto;background:#fff;position:relative}
table{width:max-content;min-width:100%;border-collapse:collapse}
thead{position:sticky;top:0;z-index:10}
thead th{background:#C6C6C6;color:#212246;font-family:'Bricolage Grotesque',sans-serif;font-size:12px;letter-spacing:0.3px;text-transform:uppercase;font-weight:600;padding:11px 14px;text-align:left;white-space:nowrap;user-select:none;border-bottom:1px solid rgba(33,34,70,0.12);transition:background 0.15s,color 0.15s}
thead th:hover{background:#2FA9FE;color:#fff}
.th-inner{display:flex;align-items:center;gap:6px}
.th-inner-left{justify-content:flex-start}
.th-inner-right{justify-content:flex-end}
.th-inner-center{justify-content:center}
.th-label{flex:0 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis}
.th-filter-btn{display:inline-flex;align-items:center;justify-content:center;gap:3px;background:transparent;border:1px solid transparent;border-radius:4px;width:auto;min-width:18px;height:18px;padding:0 4px;color:inherit;opacity:0.55;cursor:pointer;transition:all 0.12s;flex-shrink:0}
.th-filter-btn:hover{opacity:1;background:rgba(33,34,70,0.08);border-color:rgba(33,34,70,0.18)}
thead th:hover .th-filter-btn{opacity:0.95}
thead th:hover .th-filter-btn:hover{background:rgba(255,255,255,0.18);border-color:rgba(255,255,255,0.4)}
.th-filter-btn.active{opacity:1;color:var(--blue);background:rgba(47,169,254,0.12);border-color:rgba(47,169,254,0.4)}
.th-filter-btn.active:hover{background:rgba(47,169,254,0.2)}
thead th:hover .th-filter-btn.active{color:#fff;background:rgba(255,255,255,0.22);border-color:rgba(255,255,255,0.45)}
.th-filter-badge{font-family:'JetBrains Mono',monospace;font-size:9px;font-weight:600;line-height:1;letter-spacing:0}
.th-filter-pop{position:fixed;z-index:9999;background:var(--white);border:1px solid var(--border);border-radius:var(--radius);box-shadow:var(--shadow-lg);display:flex;flex-direction:column;max-height:380px;overflow:hidden;font-family:'Bricolage Grotesque',sans-serif;color:var(--text-main);text-transform:none;letter-spacing:0;font-weight:400}
.th-filter-head{display:flex;align-items:center;justify-content:space-between;padding:10px 12px 8px;border-bottom:1px solid var(--border)}
.th-filter-title{font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:1.2px;text-transform:uppercase;color:var(--text-dim);font-weight:600}
.th-filter-x{background:rgba(137,159,183,0.15);border:none;color:var(--text-dim);width:22px;height:22px;border-radius:6px;cursor:pointer;font-size:11px;display:flex;align-items:center;justify-content:center;transition:all 0.12s}
.th-filter-x:hover{background:rgba(47,169,254,0.15);color:var(--blue)}
.th-filter-search-wrap{padding:8px 12px 4px}
.th-filter-search{width:100%;padding:7px 10px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-size:12.5px;color:var(--text-main);background:var(--surface);outline:none;transition:border-color 0.15s}
.th-filter-search:focus{border-color:var(--blue)}
.th-filter-actions{display:flex;gap:6px;padding:6px 12px 6px}
.th-filter-act{flex:1;padding:5px 8px;border:1px solid var(--border);border-radius:var(--radius-sm);background:var(--white);font-family:'JetBrains Mono',monospace;font-size:9.5px;letter-spacing:0.6px;text-transform:uppercase;color:var(--text-dim);cursor:pointer;transition:all 0.12s}
.th-filter-act:hover:not(:disabled){border-color:var(--blue);color:var(--blue);background:var(--blue-glow)}
.th-filter-act:disabled{opacity:0.4;cursor:not-allowed}
.th-filter-list{flex:1;overflow-y:auto;padding:4px 6px 6px;border-top:1px solid var(--border)}
.th-filter-row{display:flex;align-items:center;gap:8px;padding:6px 8px;border-radius:var(--radius-sm);cursor:pointer;transition:background 0.1s}
.th-filter-row:hover{background:var(--surface)}
.th-filter-row.checked{background:rgba(47,169,254,0.06)}
.th-filter-row input[type="checkbox"]{accent-color:var(--blue);width:14px;height:14px;cursor:pointer;flex-shrink:0;margin:0}
.th-filter-val{flex:1;min-width:0;font-size:12.5px;color:var(--text-main);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.th-filter-count{flex-shrink:0;font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--text-dim);background:var(--surface);padding:1px 7px;border-radius:100px;border:1px solid var(--border)}
.th-filter-empty{padding:18px 10px;text-align:center;font-size:12px;color:var(--text-dim);font-style:italic}
.th-filter-foot{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:8px 12px;border-top:1px solid var(--border);background:var(--surface)}
.th-filter-meta{font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--text-dim)}
.th-filter-done{padding:5px 14px;background:var(--blue);border:none;border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-weight:600;font-size:12px;color:#fff;cursor:pointer;transition:background 0.12s}
.th-filter-done:hover{background:#1a8ede}
tbody tr{border-bottom:1px solid rgba(198,198,198,0.2);cursor:pointer;transition:background 0.1s}
tbody tr:hover{background:rgba(47,169,254,0.04)}
td{padding:10px 14px;vertical-align:middle;white-space:nowrap;font-size:12.5px}
.cell-name{font-weight:600;max-width:180px;overflow:hidden;text-overflow:ellipsis;color:var(--text-main)}
.cell-buyer{font-size:12px;color:var(--text-sub);max-width:120px;overflow:hidden;text-overflow:ellipsis}
.chip{display:inline-block;padding:2px 9px;border-radius:100px;font-size:10px;font-family:'JetBrains Mono',monospace;letter-spacing:0.2px;white-space:nowrap}
.chip-blue{background:rgba(47,169,254,0.1);color:#1a7ec4;border:1px solid rgba(47,169,254,0.2)}
.mono{font-family:'JetBrains Mono',monospace}
.num-accent{color:var(--blue);font-weight:500}
.dim{color:var(--text-dim)}
.empty-msg{text-align:center;padding:64px 20px;color:var(--text-dim)}
.empty-msg .em-icon{font-size:40px;margin-bottom:14px;opacity:0.3}

.overlay{position:fixed;inset:0;background:rgba(16,17,38,0.55);z-index:300;backdrop-filter:blur(4px)}
.drawer{position:fixed;right:0;top:0;bottom:0;width:500px;background:var(--white);z-index:301;transform:translateX(0);transition:transform 0.28s cubic-bezier(0.22,1,0.36,1);overflow-y:auto;display:flex;flex-direction:column}
.drawer-top{background:var(--navy);padding:24px 24px 20px;position:sticky;top:0;z-index:10;flex-shrink:0;background-image:radial-gradient(ellipse 80% 60% at 0% 0%,rgba(47,169,254,0.08) 0%,transparent 60%);border-bottom:1px solid rgba(47,169,254,0.15)}
.drawer-close{position:absolute;top:16px;right:16px;background:rgba(137,159,183,0.15);border:none;color:var(--steel);width:28px;height:28px;border-radius:6px;cursor:pointer;font-size:14px;display:flex;align-items:center;justify-content:center;transition:all 0.12s}
.drawer-close:hover{background:rgba(47,169,254,0.2);color:var(--blue)}
.drawer-title{font-family:'Clash Display',sans-serif;font-weight:600;font-size:19px;color:#fff;display:flex;align-items:center;gap:10px;margin-bottom:3px}
.drawer-sub{font-size:12px;color:var(--steel);line-height:1.5;min-height:16px}
.drawer-body{padding:22px 24px;flex:1}
.kpi-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:20px}
.kpi{background:var(--surface);border:1.5px solid var(--border);border-radius:var(--radius);padding:16px 18px;border-top:3px solid #2FA9FE}
.kpi-lbl{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-dim);display:block;margin-bottom:5px}
.kpi-v{font-family:'Clash Display',sans-serif;font-weight:700;font-size:22px;color:#2FA9FE;letter-spacing:-0.5px;line-height:1}
.kpi-s{font-size:11px;color:var(--text-dim);margin-top:3px;font-family:'JetBrains Mono',monospace}
.sec-head{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:2px;text-transform:uppercase;color:var(--text-dim);margin:18px 0 10px;display:flex;align-items:center;gap:8px}
.sec-head.bold{font-weight:700;color:var(--navy);font-size:10px}
.sec-head::after{content:'';flex:1;height:1px;background:var(--border)}
.info-grid{display:flex;flex-direction:column}
.info-row{display:flex;justify-content:space-between;align-items:flex-start;padding:8px 0;border-bottom:1px solid rgba(198,198,198,0.2);gap:12px}
.info-row:last-child{border-bottom:none}
.ik{font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--text-dim);flex-shrink:0;letter-spacing:0.3px}
.iv{font-size:12.5px;font-weight:500;color:var(--text-main);text-align:right;word-break:break-word;max-width:280px}
.comp-section{margin-top:16px}
.comp-row{display:flex;justify-content:space-between;font-family:'JetBrains Mono',monospace;font-size:9.5px;color:var(--text-dim);margin-bottom:5px}
.comp-track{height:8px;background:var(--surface);border-radius:100px;border:1px solid var(--border);position:relative;overflow:hidden}
.comp-fill{height:100%;background:linear-gradient(90deg,var(--blue),var(--teal));border-radius:100px;transition:width 0.5s cubic-bezier(0.4,0,0.2,1)}
.comp-median{position:absolute;top:-1px;bottom:-1px;width:2px;background:var(--navy);border-radius:1px;z-index:2}
.comp-note{font-family:'JetBrains Mono',monospace;font-size:9px;color:var(--text-dim);margin-top:6px;letter-spacing:0.3px}
.ai-box{background:#C6C6C6;border-radius:var(--radius);padding:14px 16px;margin-top:18px}
.ai-label{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:2px;text-transform:uppercase;color:#212246;margin-bottom:8px;display:flex;align-items:center;gap:6px;font-weight:600}
.ai-label::before{content:'';width:6px;height:6px;border-radius:50%;background:#212246;flex-shrink:0}
.ai-text{font-size:12.5px;color:#212246;line-height:1.75}
.ai-dots{display:flex;gap:4px;align-items:center}
.ai-dots span{width:5px;height:5px;border-radius:50%;background:#212246;animation:blink 1.2s ease-in-out infinite}
.ai-dots span:nth-child(2){animation-delay:0.2s}
.ai-dots span:nth-child(3){animation-delay:0.4s}

.clause-wrap{display:grid;grid-template-columns:260px 1fr;min-height:calc(100vh - 60px)}
.clause-sidebar{background:var(--white);border-right:1px solid var(--border);padding:20px 0;position:sticky;top:60px;max-height:calc(100vh - 60px);overflow-y:auto}
.csb-head{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:2px;text-transform:uppercase;color:var(--text-dim);padding:0 16px 12px;display:block}
.cat-btn{width:100%;text-align:left;border:none;background:none;padding:10px 16px;cursor:pointer;font-family:'Bricolage Grotesque',sans-serif;font-size:13px;color:var(--text-sub);display:flex;align-items:center;justify-content:space-between;transition:all 0.12s;border-left:3px solid transparent;gap:10px}
.cat-btn:hover{background:var(--surface);color:var(--text-main)}
.cat-btn.active{background:rgba(47,169,254,0.06);color:var(--navy);border-left-color:var(--blue);font-weight:500}
.cat-count{font-family:'JetBrains Mono',monospace;font-size:10px;background:var(--off-white);border:1px solid var(--border);padding:2px 7px;border-radius:100px;color:var(--text-dim);flex-shrink:0}
.cat-btn.active .cat-count{background:rgba(47,169,254,0.12);border-color:rgba(47,169,254,0.25);color:var(--blue)}
.clause-main{padding:28px 32px}
.clause-top{display:flex;align-items:flex-start;justify-content:space-between;gap:20px;margin-bottom:22px}
.clause-top-left h2{font-family:'Clash Display',sans-serif;font-weight:600;font-size:24px;letter-spacing:-0.3px;margin-bottom:5px}
.clause-top-left p{font-size:13px;color:var(--text-sub);max-width:480px;line-height:1.6}
.clause-search-input{padding:9px 14px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-size:13px;outline:none;width:230px;flex-shrink:0;transition:all 0.15s;background:var(--surface);color:var(--text-main)}
.clause-search-input:focus{border-color:var(--blue);background:#fff;box-shadow:0 0 0 3px var(--blue-glow)}
.clause-card{background:#fff;border:1.5px solid var(--border);border-radius:var(--radius);margin-bottom:12px;overflow:hidden;transition:box-shadow 0.15s,border-color 0.15s}
.clause-card:hover{box-shadow:var(--shadow)}
.clause-card.open{border-color:rgba(47,169,254,0.25);box-shadow:0 0 0 1px rgba(47,169,254,0.1),var(--shadow)}
.clause-head{padding:14px 18px;display:flex;align-items:center;justify-content:space-between;cursor:pointer;gap:14px}
.clause-title{font-family:'Bricolage Grotesque',sans-serif;font-weight:600;font-size:14px;flex:1;color:var(--text-main)}
.stance{font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:500;letter-spacing:0.8px;text-transform:uppercase;padding:3px 10px;border-radius:100px;flex-shrink:0}
.s-firm{background:rgba(220,38,38,0.08);color:#b91c1c;border:1px solid rgba(220,38,38,0.18)}
.s-flex{background:rgba(48,192,202,0.1);color:#0d7280;border:1px solid rgba(48,192,202,0.2)}
.s-neg{background:rgba(144,107,246,0.1);color:#5b21b6;border:1px solid rgba(144,107,246,0.2)}
.clause-chevron{color:var(--text-dim);font-size:12px;transition:transform 0.2s;flex-shrink:0}
.clause-card.open .clause-chevron{transform:rotate(180deg)}
.clause-body{padding:0 18px 18px;border-top:1px solid var(--off-white);animation:fadeUp 0.15s ease}
.clause-text{background:var(--surface);border-left:3px solid var(--blue);border-radius:0 var(--radius-sm) var(--radius-sm) 0;padding:14px 16px;margin-top:14px;font-family:'JetBrains Mono',monospace;font-size:11.5px;color:var(--navy);line-height:1.8}
.clause-notes{margin-top:12px;font-size:13px;color:var(--text-sub);line-height:1.7}
.clause-actions{margin-top:14px;display:flex;gap:8px;align-items:center}
.hold-tag{display:inline-flex;align-items:center;gap:5px;background:rgba(220,38,38,0.07);border:1px solid rgba(220,38,38,0.15);color:#b91c1c;font-family:'JetBrains Mono',monospace;font-size:9.5px;letter-spacing:0.5px;text-transform:uppercase;padding:4px 10px;border-radius:4px}

.mod-banner{background:var(--navy);padding:18px 28px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid rgba(47,169,254,0.15);background-image:radial-gradient(ellipse 40% 100% at 0% 50%,rgba(48,192,202,0.06) 0%,transparent 60%),radial-gradient(ellipse 30% 100% at 100% 50%,rgba(144,107,246,0.05) 0%,transparent 60%)}
.mod-banner h2{font-family:'Clash Display',sans-serif;font-weight:700;font-size:19px;color:#fff;margin-bottom:2px}
.mod-banner p{font-family:'JetBrains Mono',monospace;font-size:10.5px;color:var(--steel);letter-spacing:0.3px}
.mod-pill{background:rgba(47,169,254,0.12);border:1px solid rgba(47,169,254,0.25);color:var(--blue);font-family:'JetBrains Mono',monospace;font-size:9.5px;letter-spacing:1.5px;text-transform:uppercase;padding:5px 14px;border-radius:100px;flex-shrink:0}

.orig-page{padding:32px;max-width:1020px;margin:0 auto}
.orig-hero{margin-bottom:28px}
.orig-hero h2{font-family:'Clash Display',sans-serif;font-weight:700;font-size:30px;letter-spacing:-0.5px;margin-bottom:6px}
.orig-hero h2 span{background:linear-gradient(90deg,var(--blue),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.orig-hero p{font-size:14px;color:var(--text-sub)}
.orig-section-label{font-family:'JetBrains Mono',monospace;font-size:8.5px;letter-spacing:2px;text-transform:uppercase;color:var(--text-dim);margin-bottom:8px;display:flex;align-items:center;gap:8px}
.orig-section-label::before{content:'';width:14px;height:1.5px;background:var(--border);display:block;flex-shrink:0}
.tools-grid-v2{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px}
@media(max-width:900px){.tools-grid-v2{grid-template-columns:1fr 1fr}}
@media(max-width:520px){.tools-grid-v2{grid-template-columns:1fr}}
.tools-grid-v2-row2{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-bottom:24px}
@media(max-width:520px){.tools-grid-v2-row2{grid-template-columns:1fr}}
.tool-card{background:#fff;border:1.5px solid var(--border);border-radius:var(--radius);padding:20px;cursor:pointer;transition:all 0.18s;position:relative;overflow:hidden;font:inherit;color:inherit;text-align:left}
.tool-badge{display:inline-block;font-family:'JetBrains Mono',monospace;font-size:7.5px;letter-spacing:1px;text-transform:uppercase;padding:2px 6px;border-radius:3px;margin-top:6px;font-weight:600}
.badge-new{background:rgba(16,185,129,0.12);color:#065f46;border:1px solid rgba(16,185,129,0.25)}
.badge-upgrade{background:rgba(47,169,254,0.1);color:#1a7ec4;border:1px solid rgba(47,169,254,0.2)}
.tool-card::after{content:'';position:absolute;bottom:0;left:0;right:0;height:3px;transform:scaleX(0);transform-origin:left;transition:transform 0.2s}
.tool-card.t-brief::after{background:linear-gradient(90deg,var(--teal),#2de0bd)}
.tool-card.t-teas::after{background:linear-gradient(90deg,var(--purple),#c472f8)}
.tool-card.t-val::after{background:linear-gradient(90deg,var(--navy),var(--blue))}
.tool-card.t-risk::after{background:linear-gradient(90deg,#f59e0b,#ef4444)}
.tool-card.t-vri::after{background:linear-gradient(90deg,#8b5cf6,#3b82f6)}
.tool-card:hover,.tool-card.active-tool{border-color:transparent;box-shadow:0 0 0 1.5px var(--blue),var(--shadow-blue)}
.tool-card.active-tool{border-color:var(--blue)}
.tool-card:hover::after,.tool-card.active-tool::after{transform:scaleX(1)}
.tc-icon{width:38px;height:38px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:18px;margin-bottom:12px}
.icon-brief{background:linear-gradient(135deg,rgba(48,192,202,0.12),rgba(45,224,189,0.1))}
.icon-teas{background:linear-gradient(135deg,rgba(144,107,246,0.12),rgba(196,114,248,0.1))}
.icon-val{background:linear-gradient(135deg,rgba(33,34,70,0.08),rgba(47,169,254,0.1))}
.icon-risk{background:linear-gradient(135deg,rgba(245,158,11,0.15),rgba(239,68,68,0.1))}
.icon-vri{background:linear-gradient(135deg,rgba(139,92,246,0.15),rgba(59,130,246,0.1))}
.field-group-header{font-family:'JetBrains Mono',monospace;font-size:8px;letter-spacing:2px;text-transform:uppercase;color:var(--blue);padding:10px 0 6px;margin-top:6px;border-bottom:1px solid var(--border);margin-bottom:12px;display:flex;align-items:center;gap:8px}
.field-group-header::before{content:'';display:block;width:14px;height:1.5px;background:var(--blue);opacity:0.5}
.form-3col{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px}
@media(max-width:900px){.form-3col{grid-template-columns:1fr 1fr}}
@media(max-width:520px){.form-3col{grid-template-columns:1fr}}
.fi-select{width:100%;padding:10px 28px 10px 12px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-size:13px;color:var(--text-main);background:var(--surface);outline:none;cursor:pointer;appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23899FB7'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 10px center;transition:border-color 0.15s}
.fi-select:focus{border-color:var(--blue);background:#fff;box-shadow:0 0 0 3px var(--blue-glow)}
.toggle-row{width:100%;box-sizing:border-box;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:16px 18px;min-height:64px;background:var(--surface);border:2px solid var(--border);border-radius:var(--radius);cursor:pointer;transition:all 0.15s;margin-bottom:12px;text-align:left}
.toggle-row > div:first-of-type{flex:1;min-width:0;text-align:left}
.toggle-row:hover{border-color:var(--blue)}
.toggle-label{font-family:'Bricolage Grotesque',sans-serif;font-size:15px;line-height:1.35;font-weight:500;color:var(--text-main)}
.toggle-label small{display:block;font-size:12px;font-weight:400;line-height:1.45;color:var(--text-dim);margin-top:4px}
.toggle-switch{width:48px;height:26px;background:var(--border);border-radius:100px;position:relative;transition:background 0.2s;flex-shrink:0}
.toggle-switch::after{content:'';position:absolute;width:18px;height:18px;border-radius:50%;background:#fff;top:4px;left:4px;transition:transform 0.2s;box-shadow:0 1px 5px rgba(0,0,0,0.18)}
.toggle-row.active .toggle-switch{background:var(--blue)}
.toggle-row.active .toggle-switch::after{transform:translateX(22px)}
.vri-step-tabs{display:flex;gap:0;margin-bottom:18px;border:1.5px solid var(--border);border-radius:var(--radius-sm);overflow:hidden}
.vri-step-tab{flex:1;margin:0;padding:9px 6px;text-align:center;font-family:'JetBrains Mono',monospace;font-size:9px;line-height:1.2;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;background:#fff;color:var(--text-dim);cursor:pointer;border:none;border-right:1px solid var(--border);transition:all 0.15s;position:relative;box-sizing:border-box;-webkit-appearance:none;appearance:none}
.vri-step-tab:last-child{border-right:none}
.vri-step-tab.active{background:var(--blue);color:#fff;border-color:var(--blue)}
.vri-step-tab .step-num{display:block;font-size:14px;font-weight:700;margin-bottom:2px;font-family:'Clash Display',sans-serif}
.vri-panel{display:none}
.vri-panel.active{display:block;animation:fadeUp 0.18s ease}
.vri-step-output{min-height:120px;font-size:13px;color:var(--text-sub);line-height:1.75;white-space:pre-wrap;background:var(--surface);border:1.5px solid var(--border);border-radius:var(--radius-sm);padding:14px 16px;margin-top:8px}
.vri-intent-stack{margin-top:8px}
.vri-intent-detail{font-size:13px;line-height:1.8;color:var(--text-sub);margin-top:4px}
.orig-sec-body{font-size:13px;line-height:1.75;color:var(--text-sub)}
.out-sec-head{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:2px;text-transform:uppercase;color:var(--blue);font-weight:600;margin:16px 0 6px;padding:6px 0;border-bottom:1.5px solid var(--border)}
.out-bullet{padding:4px 0 4px 16px;position:relative;font-size:13px;color:var(--text-sub);line-height:1.65}
.out-bullet::before{content:'\\25B8';position:absolute;left:0;color:var(--teal);font-size:10px;top:6px}
.intent-score-card{background:#fff;border:1.5px solid var(--border);border-radius:var(--radius);padding:16px 18px;margin-bottom:12px}
.intent-score-row{display:flex;align-items:center;gap:14px;margin-bottom:14px}
.intent-score-num{width:52px;height:52px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Clash Display',sans-serif;font-weight:700;font-size:22px;flex-shrink:0}
.intent-score-label{flex:1;min-width:0}
.intent-score-label .score-title{font-family:'Clash Display',sans-serif;font-weight:600;font-size:15px;color:var(--text-main)}
.intent-score-label .score-sub{font-size:11px;color:var(--text-dim);margin-top:2px;font-family:'JetBrains Mono',monospace;letter-spacing:0.3px}
.intent-action-pill{display:inline-flex;align-items:center;gap:6px;padding:6px 14px;border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:500;letter-spacing:0.5px;flex-shrink:0}
.intent-action-pursue{background:rgba(16,185,129,0.1);color:#059669;border:1.5px solid rgba(16,185,129,0.35)}
.intent-action-review{background:rgba(245,158,11,0.1);color:#b45309;border:1.5px solid rgba(245,158,11,0.35)}
.intent-action-pass{background:rgba(239,68,68,0.08);color:#b91c1c;border:1.5px solid rgba(239,68,68,0.25)}
.vri-step-tab.vri-tab-batch{flex:0.9 1 88px;min-width:72px;max-width:124px;background:rgba(144,107,246,0.06);color:var(--purple);border-left:1px solid rgba(144,107,246,0.2)}
.vri-step-tab.vri-tab-batch .step-num{font-size:12px}
.vri-step-tab.vri-tab-batch.active{background:var(--purple);color:#fff;border-color:var(--purple)}
.batch-score-header{display:flex;align-items:center;gap:8px;margin-bottom:6px}
.batch-badge{font-family:'JetBrains Mono',monospace;font-size:8px;letter-spacing:1.5px;text-transform:uppercase;padding:3px 8px;background:rgba(144,107,246,0.1);border:1px solid rgba(144,107,246,0.3);border-radius:100px;color:var(--purple)}
.batch-url-input{width:100%;min-height:110px;padding:10px 12px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'JetBrains Mono',monospace;font-size:12px;color:var(--text-main);background:#fff;outline:none;resize:vertical;line-height:1.8;transition:border-color 0.2s;box-sizing:border-box}
.batch-url-input:focus{border-color:var(--purple);box-shadow:0 0 0 3px var(--purple-glow)}
.batch-url-input::placeholder{color:var(--text-dim)}
.batch-score-table{width:100%;border-collapse:collapse;margin-top:14px;font-size:12.5px}
.batch-score-table th{text-align:left;padding:7px 10px;border-bottom:2px solid var(--border);font-family:'JetBrains Mono',monospace;font-size:8.5px;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-dim);font-weight:400}
.batch-score-table td{padding:8px 10px;border-bottom:1px solid rgba(198,198,198,0.18);vertical-align:top}
.batch-score-table tr:last-child td{border-bottom:none}
.batch-score-table tr:hover td{background:rgba(47,169,254,0.03)}
.score-badge{display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;font-family:'Clash Display',sans-serif;font-weight:700;font-size:13px;flex-shrink:0}
.score-5{background:rgba(16,185,129,0.12);color:#059669;border:1.5px solid rgba(16,185,129,0.35)}
.score-4{background:rgba(47,169,254,0.12);color:#1a7ec4;border:1.5px solid rgba(47,169,254,0.3)}
.score-3{background:rgba(245,158,11,0.12);color:#b45309;border:1.5px solid rgba(245,158,11,0.3)}
.score-2{background:rgba(239,68,68,0.09);color:#b91c1c;border:1.5px solid rgba(239,68,68,0.25)}
.score-1{background:rgba(107,114,128,0.1);color:#374151;border:1.5px solid rgba(107,114,128,0.2)}
.batch-url-cell{font-family:'JetBrains Mono',monospace;font-size:10.5px;color:var(--blue);word-break:break-all}
.batch-reason-cell{font-size:12px;color:var(--text-sub);line-height:1.6}
.batch-results-wrap{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:14px;margin-top:12px;display:none}
.batch-results-wrap.show{display:block;animation:fadeUp 0.2s ease}
.batch-results-title{font-family:'JetBrains Mono',monospace;font-size:8.5px;letter-spacing:2px;text-transform:uppercase;color:var(--purple);margin-bottom:10px;display:flex;align-items:center;gap:6px}
.batch-results-title::before{content:'';display:block;width:12px;height:1.5px;background:var(--purple);opacity:0.5}
.batch-summary-row{display:flex;gap:14px;margin-top:12px;font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:0.5px;flex-wrap:wrap}
.action-badge{display:inline-flex;align-items:center;padding:3px 9px;border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:9.5px;font-weight:500;letter-spacing:0.3px;white-space:nowrap}
.action-pursue{background:rgba(16,185,129,0.1);color:#059669;border:1px solid rgba(16,185,129,0.3)}
.action-review{background:rgba(245,158,11,0.1);color:#b45309;border:1px solid rgba(245,158,11,0.3)}
.action-pass{background:rgba(239,68,68,0.08);color:#b91c1c;border:1px solid rgba(239,68,68,0.2)}
.response-draft-box{background:var(--surface);border:1.5px solid var(--border);border-radius:var(--radius-sm);padding:14px;margin-top:12px}
.response-draft-textarea{width:100%;border:none;background:transparent;font-family:'Bricolage Grotesque',sans-serif;font-size:13px;line-height:1.8;color:var(--text-main);outline:none;resize:vertical;min-height:200px}
.vri-nav-btn{display:inline-flex;align-items:center;gap:6px;padding:9px 18px;border-radius:var(--radius-sm);font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:0.5px;text-transform:uppercase;cursor:pointer;transition:all 0.15s;border:none;font:inherit}
.vri-next{background:var(--blue);color:#fff;box-shadow:0 2px 10px rgba(47,169,254,0.25)}
.vri-next:hover{opacity:0.88}
.vri-back{background:#fff;color:var(--text-dim);border:1.5px solid var(--border)}
.vri-back:hover{border-color:var(--blue);color:var(--blue)}
.supporting-docs-dropzone{display:flex;align-items:center;gap:8px;cursor:pointer;padding:8px 12px;border:1px dashed var(--border);border-radius:var(--radius-sm);background:rgba(255,255,255,0.65);transition:border-color 0.2s,background 0.2s}
.supporting-docs-dropzone:hover{border-color:var(--blue);background:rgba(47,169,254,0.04)}
.supporting-docs-list{margin-top:10px;border:2px solid var(--border-hi);border-radius:var(--radius-sm);background:#fff;padding:12px 14px;box-shadow:0 1px 8px rgba(33,34,70,0.06)}
.supporting-docs-list-title{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:1.4px;text-transform:uppercase;color:var(--blue);font-weight:600;margin-bottom:10px;display:flex;align-items:center;gap:8px}
.supporting-docs-list-title::before{content:'';width:10px;height:2px;background:var(--blue);opacity:0.6;border-radius:1px}
.supporting-docs-file-row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 0;border-bottom:1px solid rgba(198,198,198,0.35)}
.supporting-docs-file-row:last-child{border-bottom:none;padding-bottom:2px}
.supporting-docs-file-name{font-size:14px;font-weight:500;color:var(--text-main);display:flex;align-items:flex-start;gap:10px;min-width:0;word-break:break-word;line-height:1.4}
.supporting-docs-file-name .sd-ico{flex-shrink:0;font-size:16px;line-height:1.2;margin-top:1px}
.supporting-docs-remove{flex-shrink:0;padding:6px 12px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:0.5px;text-transform:uppercase;background:#fff;color:var(--text-dim);cursor:pointer;transition:all 0.12s}
.supporting-docs-remove:hover{border-color:var(--blue);color:var(--blue);background:var(--blue-glow)}
.tc-name{font-family:'Clash Display',sans-serif;font-weight:600;font-size:14px;margin-bottom:4px;color:var(--text-main)}
.tc-desc{font-size:11.5px;color:var(--text-dim);line-height:1.55}
.gen-box{background:#fff;border:1.5px solid var(--border);border-radius:var(--radius-lg);overflow:hidden}
#origination-toolkit-form{scroll-margin-top:72px}
.gen-header{background:var(--navy);padding:16px 22px;display:flex;align-items:center;justify-content:space-between;background-image:radial-gradient(ellipse 50% 80% at 0% 50%,rgba(47,169,254,0.07) 0%,transparent 60%)}
.gen-h-title{font-family:'Clash Display',sans-serif;font-weight:600;font-size:15px;color:#fff;display:flex;align-items:center;gap:10px}
.gen-h-sub{font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--steel);letter-spacing:0.5px;margin-top:2px}
.gen-body-inner{padding:24px}
.form-label{font-family:'JetBrains Mono',monospace;font-size:9.5px;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-dim);margin-bottom:6px;display:block}
.fi{width:100%;padding:10px 12px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-size:13px;color:var(--text-main);background:var(--surface);outline:none;transition:all 0.15s}
.fi:focus{border-color:var(--blue);background:#fff;box-shadow:0 0 0 3px var(--blue-glow)}
textarea.fi{resize:vertical;min-height:80px}
.form-2col{display:grid;grid-template-columns:1fr 1fr;gap:14px}
@media(max-width:640px){.form-2col{grid-template-columns:1fr}}
.form-row{margin-bottom:14px}
.gen-btn{display:inline-flex;align-items:center;gap:8px;padding:11px 22px;background:linear-gradient(135deg,var(--blue),var(--teal));border:none;border-radius:var(--radius-sm);font-family:'Clash Display',sans-serif;font-weight:600;font-size:13px;color:#fff;cursor:pointer;letter-spacing:0.3px;transition:opacity 0.15s,transform 0.1s;box-shadow:0 4px 16px rgba(47,169,254,0.3)}
.gen-btn:hover{opacity:0.9;transform:translateY(-1px)}
.tone-select{padding:10px 28px 10px 12px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-size:12.5px;color:var(--text-main);background:var(--surface);outline:none;cursor:pointer;appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23899FB7'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 10px center;transition:border-color 0.15s}
.orig-output-ta{display:block;width:100%;box-sizing:border-box;margin-top:18px;padding:18px 20px;border:1.5px solid var(--border);border-radius:var(--radius);font-size:13px;line-height:1.85;color:var(--text-main);white-space:pre-wrap;font-family:'Bricolage Grotesque',sans-serif;min-height:160px;resize:vertical;background:var(--surface);outline:none}
.orig-output-ta:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-glow)}
.output-btns{margin-top:10px;display:flex;gap:8px}
.act-btn{padding:6px 14px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:0.5px;text-transform:uppercase;background:#fff;color:var(--text-dim);cursor:pointer;transition:all 0.12s}
.act-btn:hover{border-color:var(--blue);color:var(--blue);background:var(--blue-glow)}
.gen-loading{display:flex;align-items:center;gap:12px;padding:20px 0;font-family:'JetBrains Mono',monospace;font-size:11px;color:var(--text-dim);letter-spacing:0.5px}
.et-wrap{display:flex;min-height:calc(100vh - 60px);overflow:hidden}
.et-sidebar{width:232px;flex-shrink:0;background:#fff;border-right:1px solid var(--border);overflow-y:auto;display:flex;flex-direction:column}
.et-sb-brand{padding:16px 18px 10px;border-bottom:1px solid var(--border)}
.et-sb-logo{font-family:'Clash Display',sans-serif;font-weight:700;font-size:13px;color:var(--text-main);letter-spacing:-0.2px;display:flex;align-items:center;gap:7px}
.et-sb-logo span{display:inline-flex;width:22px;height:22px;border-radius:5px;background:linear-gradient(135deg,#f59e0b,#ef4444);align-items:center;justify-content:center;font-size:11px;color:#fff}
.et-sb-sub{font-family:'JetBrains Mono',monospace;font-size:8px;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-dim);margin-top:3px}
.et-sb-section{padding:14px 18px 5px;font-family:'JetBrains Mono',monospace;font-size:7.5px;letter-spacing:2px;text-transform:uppercase;color:var(--text-dim)}
.et-nav{display:flex;flex-direction:column;gap:1px;padding:0 8px}
.et-nav-item{display:flex;align-items:center;gap:9px;padding:9px 10px;cursor:pointer;border-radius:var(--radius-sm);border-left:3px solid transparent;transition:all 0.14s;font-size:13px;color:var(--text-sub);font-family:'Bricolage Grotesque',sans-serif;margin-bottom:1px;background:none;border:none;width:calc(100% - 0px);text-align:left;font:inherit}
.et-nav-item:hover{background:var(--off-white);color:var(--text-main)}
.et-nav-item.active{background:rgba(47,169,254,0.07);border-left-color:var(--blue);color:var(--blue);font-weight:500}
.et-nav-item.et-nav-soon{opacity:0.42;cursor:not-allowed}
.et-nav-icon{width:26px;height:26px;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:12px;flex-shrink:0}
.et-ni-cim{background:linear-gradient(135deg,rgba(47,169,254,0.14),rgba(48,192,202,0.14));color:var(--blue)}
.et-ni-model{background:linear-gradient(135deg,rgba(144,107,246,0.14),rgba(196,114,248,0.14));color:var(--purple)}
.et-ni-sync{background:linear-gradient(135deg,rgba(48,192,202,0.14),rgba(16,185,129,0.14));color:var(--teal)}
.et-ni-scenario{background:linear-gradient(135deg,rgba(245,158,11,0.14),rgba(239,68,68,0.14));color:#b45309}
.et-main{flex:1;overflow-y:auto;background:var(--off-white);padding:22px 26px}
.et-panel{display:none;animation:fadeUp 0.18s ease}
.et-panel.active{display:block}
.et-header{border-radius:var(--radius-lg);padding:18px 22px;margin-bottom:18px;position:relative;overflow:hidden}
.et-header.cim{background:var(--navy);background-image:radial-gradient(ellipse 70% 100% at 0% 50%,rgba(47,169,254,0.18) 0%,transparent 60%),radial-gradient(ellipse 40% 100% at 100% 50%,rgba(48,192,202,0.12) 0%,transparent 60%)}
.et-header.sync{background:var(--navy);background-image:radial-gradient(ellipse 70% 100% at 0% 50%,rgba(48,192,202,0.18) 0%,transparent 60%),radial-gradient(ellipse 40% 100% at 100% 50%,rgba(16,185,129,0.12) 0%,transparent 60%)}
.et-header h2{font-family:'Clash Display',sans-serif;font-weight:700;font-size:19px;color:#fff;letter-spacing:-0.3px;margin-bottom:3px}
.et-header p{font-family:'JetBrains Mono',monospace;font-size:9.5px;color:var(--steel);letter-spacing:0.3px}
.et-badge{display:inline-flex;align-items:center;gap:5px;padding:3px 11px;border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:8.5px;letter-spacing:1px;text-transform:uppercase;margin-top:8px}
.et-badge.cim{background:rgba(47,169,254,0.15);border:1px solid rgba(47,169,254,0.3);color:var(--blue)}
.et-badge.sync{background:rgba(48,192,202,0.15);border:1px solid rgba(48,192,202,0.3);color:var(--teal)}
.et-sync-hero-wrap{padding:14px 16px;border-radius:calc(var(--radius-lg) + 6px);background:linear-gradient(165deg,rgba(47,169,254,0.1) 0%,rgba(47,169,254,0.04) 45%,rgba(48,192,202,0.06) 100%);border:1.5px solid rgba(47,169,254,0.32);box-shadow:0 0 0 1px rgba(255,255,255,0.55) inset,0 10px 32px rgba(47,169,254,0.12);margin-bottom:18px}
.et-sync-hero-wrap .et-header{margin-bottom:0}
.et-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:16px}
@media(max-width:900px){.et-grid{grid-template-columns:1fr}}
.et-card{background:#fff;border:1px solid var(--border);border-radius:var(--radius);padding:16px 18px;transition:box-shadow 0.14s,border-color 0.14s}
.et-card:hover{box-shadow:var(--shadow);border-color:rgba(47,169,254,0.2)}
.et-card-title{font-family:'JetBrains Mono',monospace;font-size:8px;letter-spacing:2px;text-transform:uppercase;margin-bottom:12px;display:flex;align-items:center;gap:6px;color:var(--blue)}
.et-card-title.teal{color:var(--teal)}
.et-card-title::before{content:'';display:block;width:10px;height:1.5px;background:currentColor;opacity:0.5}
.et-label{font-size:11.5px;color:var(--text-sub);font-weight:500;margin-bottom:4px;display:block}
.et-input{width:100%;padding:9px 11px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-size:13px;color:var(--text-main);background:#fff;outline:none;transition:border-color .18s;box-sizing:border-box}
.et-input:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-glow)}
.et-textarea{width:100%;padding:9px 11px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-size:13px;color:var(--text-main);background:#fff;outline:none;resize:vertical;line-height:1.6;transition:border-color .18s;box-sizing:border-box;min-height:70px}
.et-textarea:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-glow)}
.et-select{width:100%;padding:9px 32px 9px 11px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:'Bricolage Grotesque',sans-serif;font-size:13px;color:var(--text-main);background:#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='7'%3E%3Cpath d='M1 1l4.5 5L10 1' stroke='%238892a8' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat right 10px center;outline:none;appearance:none;cursor:pointer;transition:border-color .18s;box-sizing:border-box}
.et-select:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-glow)}
.et-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;padding:9px 18px;border-radius:var(--radius-sm);font-family:'Clash Display',sans-serif;font-weight:700;font-size:12px;letter-spacing:0.4px;cursor:pointer;transition:all 0.14s;border:none;white-space:nowrap}
.et-btn:disabled{opacity:0.45;cursor:not-allowed}
.et-btn-primary{background:linear-gradient(135deg,var(--blue),var(--teal));color:#fff;box-shadow:0 2px 10px rgba(47,169,254,0.22)}
.et-btn-primary:hover:not(:disabled){opacity:0.88;box-shadow:0 4px 18px rgba(47,169,254,0.3)}
.et-btn-ghost{background:#fff;color:var(--text-sub);border:1.5px solid var(--border)}
.et-btn-ghost:hover:not(:disabled){border-color:var(--blue);color:var(--blue)}
.et-btn-sm{padding:6px 13px;font-size:10.5px}
.et-btn-lg{padding:12px 26px;font-size:13.5px}
.et-sec-tabs{display:flex;gap:5px;flex-wrap:wrap;margin-bottom:0}
.et-sec-tab{padding:5px 13px;border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:0.5px;cursor:pointer;border:1.5px solid var(--border);background:#fff;color:var(--text-dim);transition:all 0.12s}
.et-sec-tab:hover{border-color:var(--blue);color:var(--blue)}
.et-sec-tab.active{background:var(--blue);border-color:var(--blue);color:#fff}
.et-cim-export-wrap{position:relative}
.et-cim-export-menu{display:none;position:absolute;right:0;top:calc(100% + 6px);background:#fff;border:1.5px solid var(--border);border-radius:var(--radius);box-shadow:var(--shadow-lg);min-width:248px;z-index:300;overflow:hidden}
.et-cim-export-menu.open{display:block}
.et-cim-export-row{width:100%;padding:12px 16px;background:none;border:none;cursor:pointer;text-align:left;display:flex;align-items:center;gap:10px;border-bottom:1px solid var(--border);transition:background 0.12s;font:inherit}
.et-cim-export-row:last-of-type{border-bottom:none}
.et-cim-export-row:hover:not(:disabled){background:rgba(47,169,254,0.06)}
.et-cim-export-row:disabled{cursor:not-allowed;opacity:0.45}
.et-cim-export-row.compact{padding:11px 16px}
.et-cim-export-ico{width:32px;height:32px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0}
.et-cim-export-ico.ppt{background:linear-gradient(135deg,#d04020,#f0683a)}
.et-cim-export-ico.txt{background:linear-gradient(135deg,var(--blue),var(--teal))}
.et-cim-export-ico.pdf{background:linear-gradient(135deg,#ef4444,#b91c1c)}
.et-cim-export-row-title{font-family:'Clash Display',sans-serif;font-weight:600;font-size:13px;color:var(--text-main)}
.et-cim-export-row-meta{font-family:'JetBrains Mono',monospace;font-size:9px;color:var(--text-dim);margin-top:1px}
.et-cim-export-tag{margin-left:auto;font-family:'JetBrains Mono',monospace;font-size:8px;padding:2px 7px;border-radius:100px;border:1px solid transparent;flex-shrink:0}
.et-cim-export-tag.rec{background:rgba(47,169,254,0.1);color:var(--blue);border-color:rgba(47,169,254,0.2)}
.et-cim-export-tag.soon{background:var(--off-white);color:var(--text-dim)}
.et-output{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:15px 18px;margin-top:12px}
.et-output-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;padding-bottom:8px;border-bottom:1px solid var(--border);flex-wrap:wrap;gap:8px}
.et-output-live{font-family:'JetBrains Mono',monospace;font-size:8px;letter-spacing:2px;text-transform:uppercase;color:var(--teal);display:flex;align-items:center;gap:5px}
.et-output-live::before{content:'';width:6px;height:6px;border-radius:50%;background:var(--teal);box-shadow:0 0 6px var(--teal);animation:pulse 2s infinite}
.et-output-body{font-size:13px;line-height:1.82;color:var(--text-main);white-space:pre-wrap;word-break:break-word}
.et-loading{display:none;align-items:center;gap:9px;font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--text-dim);letter-spacing:0.3px;padding:10px 0}
.et-loading.show{display:flex}
.et-spin{width:14px;height:14px;border:2px solid var(--off-white);border-top-color:var(--blue);border-radius:50%;animation:rot 0.7s linear infinite;flex-shrink:0}
.et-disclaimer{display:flex;gap:9px;align-items:flex-start;padding:10px 14px;background:rgba(245,158,11,0.07);border:1px solid rgba(245,158,11,0.28);border-radius:var(--radius-sm);margin-top:18px}
.et-disclaimer-text{font-family:'JetBrains Mono',monospace;font-size:10px;color:#92400e;line-height:1.65;letter-spacing:0.2px}
.et-ai-info{display:flex;gap:12px;align-items:flex-start;background:rgba(47,169,254,0.05);border:1px solid rgba(47,169,254,0.18);border-radius:var(--radius);padding:12px 16px;margin-bottom:16px}
.et-ai-info-title{font-family:'JetBrains Mono',monospace;font-size:8px;letter-spacing:1.5px;text-transform:uppercase;color:var(--blue);margin-bottom:3px}
.et-gen-status{font-family:'JetBrains Mono',monospace;font-size:9px;color:var(--text-dim);margin-bottom:12px}
.et-header.model{background:var(--navy);background-image:radial-gradient(ellipse 70% 100% at 0% 50%,rgba(144,107,246,0.22) 0%,transparent 55%),radial-gradient(ellipse 45% 100% at 100% 40%,rgba(196,114,248,0.14) 0%,transparent 55%)}
.et-badge.model{background:rgba(144,107,246,0.15);border:1px solid rgba(144,107,246,0.35);color:var(--purple)}
.et-header.scenario{background:var(--navy);background-image:radial-gradient(ellipse 70% 100% at 0% 50%,rgba(245,158,11,0.18) 0%,transparent 55%),radial-gradient(ellipse 40% 80% at 100% 40%,rgba(239,68,68,0.1) 0%,transparent 50%)}
.et-badge.scenario{background:rgba(245,158,11,0.12);border:1px solid rgba(245,158,11,0.35);color:#b45309}
.et-card-title.purple{color:var(--purple)}
.et-card-title.purple::before{background:var(--purple)}
.et-det-banner{display:flex;gap:12px;align-items:flex-start;border-radius:var(--radius);padding:11px 16px;margin-bottom:16px}
.et-det-banner.purple{background:rgba(144,107,246,0.05);border:1px solid rgba(144,107,246,0.18)}
.et-det-banner.amber{background:rgba(245,158,11,0.05);border:1px solid rgba(245,158,11,0.2)}
.et-det-banner-title{font-family:'JetBrains Mono',monospace;font-size:8px;letter-spacing:1.5px;text-transform:uppercase;margin-bottom:3px}
.et-det-banner.purple .et-det-banner-title{color:var(--purple)}
.et-det-banner.amber .et-det-banner-title{color:#b45309}
.et-dropzone{border:2px dashed var(--border);border-radius:var(--radius);padding:22px 16px;text-align:center;cursor:pointer;transition:border-color 0.18s,background 0.18s;background:rgba(144,107,246,0.03)}
.et-dropzone:hover,.et-dropzone.et-dz-drag{border-color:var(--purple);background:rgba(144,107,246,0.07)}
.et-dz-icon{margin-bottom:8px;line-height:1}
.et-dz-text{font-size:12.5px;color:var(--text-sub)}
.et-btn-purple{background:linear-gradient(135deg,#906bf6,#c472f8);color:#fff;box-shadow:0 2px 12px rgba(144,107,246,0.28)}
.et-btn-purple:hover:not(:disabled){opacity:0.9}
.et-btn-amber{background:linear-gradient(135deg,#f59e0b,#ea580c);color:#fff;box-shadow:0 2px 10px rgba(245,158,11,0.22)}
.et-btn-amber:hover:not(:disabled){opacity:0.9}
.et-spin.purple{border-top-color:var(--purple)}
.et-spin.teal{border-top-color:var(--teal)}
.et-spin.amber{border-top-color:#f59e0b}
.et-model-col-map-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-size:12px}
.et-model-col-map-row select{flex:1;min-width:140px}
.et-model-kpi-cell{background:var(--off-white);border:1px solid var(--border);border-radius:var(--radius-sm);padding:8px 10px}
.et-model-kpi-label{font-family:'JetBrains Mono',monospace;font-size:8px;color:var(--text-dim);text-transform:uppercase;letter-spacing:0.5px}
.et-model-kpi-val{font-family:'Clash Display',sans-serif;font-weight:700;font-size:15px;color:var(--text-main);margin-top:2px}
.et-model-proj-table{width:100%;border-collapse:collapse;font-family:'JetBrains Mono',monospace;font-size:10.5px}
.et-model-proj-table th,.et-model-proj-table td{padding:7px 10px;border-bottom:1px solid rgba(198,198,198,0.25);text-align:right}
.et-model-proj-table th:first-child,.et-model-proj-table td:first-child{text-align:left}
.et-sc-tabs{display:flex;border:1.5px solid var(--border);border-radius:var(--radius-sm);overflow:hidden;margin-bottom:14px;background:#fff}
.et-sc-tab{flex:1;padding:9px;font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:0.4px;text-align:center;cursor:pointer;border:none;border-right:1px solid var(--border);background:transparent;color:var(--text-dim);transition:all 0.12s;box-sizing:border-box}
.et-sc-tab:last-child{border-right:none}
.et-sc-tab:hover{background:var(--off-white)}
.et-sc-tab.active.base{background:rgba(47,169,254,0.1);color:var(--blue);font-weight:600}
.et-sc-tab.active.up{background:rgba(16,185,129,0.1);color:#059669;font-weight:600}
.et-sc-tab.active.dn{background:rgba(239,68,68,0.07);color:#b91c1c;font-weight:600}
.et-sc-actuals-chip{display:flex;align-items:center;gap:7px;padding:6px 10px;background:rgba(47,169,254,0.06);border:1px solid rgba(47,169,254,0.2);border-radius:5px;margin-top:5px}
.et-assumption-slider-row{display:flex;flex-direction:column;gap:4px}
.et-assumption-slider-row input[type=range]{width:100%;accent-color:var(--purple)}
.et-manual-toggle{background:none;border:none;font-family:'JetBrains Mono',monospace;font-size:9px;color:var(--text-dim);cursor:pointer;letter-spacing:0.5px;text-decoration:underline;padding:4px}
.et-manual-toggle:hover{color:var(--purple)}
.et-cim-attach-chips{display:flex;flex-direction:column;gap:6px;margin-top:8px}
.et-cim-attach-chip{display:flex;align-items:center;gap:10px;padding:8px 11px;background:rgba(47,169,254,0.06);border:1px solid rgba(47,169,254,0.2);border-radius:var(--radius-sm);font-size:12px;color:var(--text-main)}
.et-cim-attach-chip-name{font-family:'JetBrains Mono',monospace;font-size:11px;flex:1;min-width:0;word-break:break-word}
.et-cim-attach-remove{flex-shrink:0;padding:3px 10px;border:1px solid var(--border);border-radius:var(--radius-sm);font-size:10px;color:var(--text-dim);background:#fff;cursor:pointer;font-family:'JetBrains Mono',monospace;letter-spacing:0.3px}
.et-cim-attach-remove:hover{border-color:var(--blue);color:var(--blue)}
.et-sb-foot{margin-top:auto;padding:12px 10px;border-top:1px solid var(--border)}
.et-sb-foot-lbl{font-family:'JetBrains Mono',monospace;font-size:7.5px;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-dim);margin-bottom:6px;padding:0 8px}
.spin{width:18px;height:18px;border:2px solid var(--off-white);border-top-color:var(--blue);border-radius:50%;animation:rot 0.75s linear infinite}
`;

function fmtMetricsFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const SAAS_CSV_CHART_VARIABLES = [
  "MRR",
  "Total MRR Churn",
  "MRR Churn",
  "MRR Churn Rate",
  "Customers churned",
  "Total Customers",
  "Customer churn rate",
  "ARR",
  "ARPU",
  "LTV",
];

const SAAS_CSV_MAP_FIELDS = [
  ["Transaction Date", "Invoice or payment date column"],
  ["Customer Identifier", "Customer or account ID"],
  ["Currency", "Row currency (e.g. USD)"],
  ["Payment Frequency", "Billing cadence (monthly, annual, …)"],
  ["Net Amount", "Payment amount in row currency"],
];

function formatSaasCsvChartValue(value, variable) {
  const v = Number(value);
  if (Number.isNaN(v)) return "—";
  if (["MRR", "Total MRR Churn", "MRR Churn", "ARR", "ARPU", "LTV"].includes(variable)) {
    return `$${Math.round(v).toLocaleString()}`;
  }
  if (["MRR Churn Rate", "Customer churn rate"].includes(variable)) {
    return `${(v * 100).toFixed(1)}%`;
  }
  return Math.round(v).toLocaleString();
}

function formatSaasCsvStackedDollar(value) {
  return `$${Math.round(Number(value) || 0).toLocaleString()}`;
}

function saasCsvCalculatePieChartData(recurringData, nonRecurringData, selectedIndex) {
  if (!recurringData?.data?.length || !nonRecurringData?.data) return null;
  const startIndex = Math.max(0, selectedIndex - 10);
  const endIndex = selectedIndex + 2;
  const recurringSum = recurringData.data
    .slice(startIndex, endIndex)
    .reduce((sum, item) => sum + (Number(item[1]) || 0), 0);
  const nonRecurringSum = nonRecurringData.data
    .slice(startIndex, endIndex)
    .reduce((sum, item) => sum + (Number(item[1]) || 0), 0);
  return [
    { name: "Recurring", value: recurringSum },
    { name: "Non-Recurring", value: nonRecurringSum },
  ];
}

function saasCsvCalculateStackedBarData(recurringData, nonRecurringData, selectedIndex) {
  if (!recurringData?.data?.length) return [];
  const data = [];
  let currentIndex = selectedIndex;
  while (currentIndex >= 0) {
    const endIndex = currentIndex + 2;
    const startIndex = Math.max(0, endIndex - 12);
    const recurringSum = recurringData.data
      .slice(startIndex, endIndex)
      .reduce((sum, item) => sum + (Number(item[1]) || 0), 0);
    const nonRecurringSum = nonRecurringData?.data?.length
      ? nonRecurringData.data
          .slice(startIndex, endIndex)
          .reduce((sum, item) => sum + (Number(item[1]) || 0), 0)
      : 0;
    const rowStart = recurringData.data[startIndex];
    const rowEnd = recurringData.data[endIndex - 1];
    if (!rowStart || !rowEnd) break;
    const startDate = new Date(rowStart[0]);
    const endDate = new Date(rowEnd[0]);
    data.unshift({
      period: `${startDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })} - ${endDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })}`,
      Recurring: recurringSum,
      "Non-Recurring": nonRecurringSum,
    });
    currentIndex -= 12;
    if (startIndex === 0) break;
  }
  return data;
}

function SaasCsvAxisTick({ x, y, payload }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text y={0} dy={16} fill="#666" fontSize={12} textAnchor="middle">
        {payload.value}
      </text>
    </g>
  );
}

function formatValuationUSD(number) {
  const n = Number(number);
  if (number == null || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function formatValuationMultiple(value) {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return "—";
  return `${n.toFixed(1)}x`;
}

function MetricsPanel() {
  const currentYear = new Date().getFullYear();
  const [metricsTool, setMetricsTool] = useState("val");
  const [csvDragging, setCsvDragging] = useState(false);
  const csvInputRef = useRef(null);

  const [valName, setValName] = useState("");
  const [valYear, setValYear] = useState("");
  const [valDesc, setValDesc] = useState("");
  const [valRev, setValRev] = useState("");
  const [valRevW, setValRevW] = useState(3);
  const [valEbitda, setValEbitda] = useState("");
  const [valEbitdaW, setValEbitdaW] = useState(3);
  const [valGrowth, setValGrowth] = useState("");
  const [valGrowthW, setValGrowthW] = useState(3);
  const [valPrecN, setValPrecN] = useState("5");
  const [valPubN, setValPubN] = useState("3");
  const [valMultType, setValMultType] = useState("None");
  const [valMultVal, setValMultVal] = useState("");
  const [valLoading, setValLoading] = useState(false);
  const [valExportLoading, setValExportLoading] = useState(false);
  const [valResponseData, setValResponseData] = useState(null);
  const [valChartData, setValChartData] = useState(null);
  const [valPrecedentComps, setValPrecedentComps] = useState([]);
  const [valPublicComps, setValPublicComps] = useState([]);
  const [valXAxisLabels, setValXAxisLabels] = useState([]);
  const [valError, setValError] = useState("");

  const [csvFiles, setCsvFiles] = useState([]);
  const [csvProcessing, setCsvProcessing] = useState(false);
  const [csvParseBusy, setCsvParseBusy] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvSaasSelections, setCsvSaasSelections] = useState(() => ["", "", "", "", ""]);
  const [saasCsvChartData, setSaasCsvChartData] = useState(null);
  const [saasCsvDisplayData, setSaasCsvDisplayData] = useState(null);
  const [saasCsvPivotData, setSaasCsvPivotData] = useState(null);
  const [saasCsvRecurringData, setSaasCsvRecurringData] = useState(null);
  const [saasCsvNonRecurringData, setSaasCsvNonRecurringData] = useState(null);
  const [saasCsvPieChartData, setSaasCsvPieChartData] = useState(null);
  const [saasCsvStackedBarData, setSaasCsvStackedBarData] = useState(null);
  const [saasCsvSelectedVariable, setSaasCsvSelectedVariable] = useState("MRR");
  const [saasCsvSelectedDate, setSaasCsvSelectedDate] = useState(null);
  const [saasCsvExportLoading, setSaasCsvExportLoading] = useState(false);
  const [saasCsvUseFormulas, setSaasCsvUseFormulas] = useState(false);

  useEffect(() => {
    if (!valResponseData?.Valuations) return;
    const valuations = valResponseData.Valuations;
    const ranges = valuations.valuation_range_dict;
    if (!ranges) return;

    setValChartData({
      labels: ["Indicative Range", "Precedent Comps", "Public Company Comps"],
      datasets: [
        {
          label: "Valuation Range",
          data: [
            [ranges.indicative_range_low, ranges.indicative_range_high],
            [ranges.precedent_comps_low, ranges.precedent_comps_high],
            [ranges.public_company_comps_low, ranges.public_company_comps_high],
          ],
          backgroundColor: "rgba(47, 169, 254, 0.35)",
        },
      ],
    });

    setValXAxisLabels([
      `Indicative Range\n(${formatValuationMultiple(ranges.indicative_range_low_multiple)} - ${formatValuationMultiple(ranges.indicative_range_high_multiple)})`,
      `Precedent Comps\n(${formatValuationMultiple(ranges.precedent_comps_low_multiple)} - ${formatValuationMultiple(ranges.precedent_comps_high_multiple)})`,
      `Public Company Comps\n(${formatValuationMultiple(ranges.public_company_comps_low_multiple)} - ${formatValuationMultiple(ranges.public_company_comps_high_multiple)})`,
    ]);

    setValPrecedentComps(Array.isArray(valuations.precedent_comps) ? valuations.precedent_comps : []);
    setValPublicComps(Array.isArray(valuations.public_company_comps) ? valuations.public_company_comps : []);
  }, [valResponseData]);

  const valuationChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: {
          callbacks: {
            label(ctx) {
              const raw = ctx.raw;
              if (!Array.isArray(raw) || raw.length < 2) return "";
              return [`Lower: ${formatValuationUSD(raw[0])}`, `Upper: ${formatValuationUSD(raw[1])}`];
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: false,
          title: { display: false },
          ticks: {
            color: "var(--text-dim)",
            callback(value) {
              return formatValuationUSD(value);
            },
          },
        },
        x: {
          title: { display: false },
          ticks: {
            color: "var(--text-dim)",
            maxRotation: 0,
            callback(value, index) {
              return valXAxisLabels[index] ?? "";
            },
          },
        },
      },
    }),
    [valXAxisLabels]
  );

  const resetSaasCsvCharts = useCallback(() => {
    setSaasCsvChartData(null);
    setSaasCsvDisplayData(null);
    setSaasCsvPivotData(null);
    setSaasCsvRecurringData(null);
    setSaasCsvNonRecurringData(null);
    setSaasCsvPieChartData(null);
    setSaasCsvStackedBarData(null);
    setSaasCsvSelectedDate(null);
    setSaasCsvSelectedVariable("MRR");
  }, []);

  useEffect(() => {
    if (!saasCsvChartData) return;
    if (saasCsvSelectedDate) {
      const dateIndex = saasCsvChartData.findIndex((item) => item.date === saasCsvSelectedDate);
      if (dateIndex !== -1) {
        const startIndex = Math.max(0, dateIndex - 11);
        setSaasCsvDisplayData(saasCsvChartData.slice(startIndex, dateIndex + 1));
      }
    } else {
      setSaasCsvDisplayData(saasCsvChartData);
    }
  }, [saasCsvChartData, saasCsvSelectedDate]);

  const valuationBarPlugins = useMemo(
    () => [
      {
        id: "valuationRangeLabels",
        afterDraw: (chart) => {
          const ctx = chart.ctx;
          chart.data.datasets.forEach((dataset, i) => {
            const meta = chart.getDatasetMeta(i);
            meta.data.forEach((bar, index) => {
              const data = dataset.data[index];
              if (!Array.isArray(data) || data.length < 2) return;
              ctx.fillStyle = "var(--text-main)";
              ctx.textAlign = "center";
              ctx.font = "11px 'JetBrains Mono', monospace";
              ctx.textBaseline = "top";
              ctx.fillText(formatValuationUSD(data[0]), bar.x, bar.y + bar.height + 4);
              ctx.textBaseline = "bottom";
              ctx.fillText(formatValuationUSD(data[1]), bar.x, bar.y - 4);
            });
          });
        },
      },
    ],
    []
  );

  const runValuation = async () => {
    if (!valName.trim()) {
      setValError("Please enter a business name or URL.");
      return;
    }
    if (valRev === "" || valRev == null) {
      setValError("Please enter Last 12 Months Revenue.");
      return;
    }
    setValError("");
    setValResponseData(null);
    setValChartData(null);
    setValPrecedentComps([]);
    setValPublicComps([]);
    setValXAxisLabels([]);
    setValLoading(true);
    try {
      const { data } = await axios.post("/api/valuations", {
        businessName: valName.trim(),
        yearEstablished: valYear === "" || valYear == null ? null : String(valYear),
        description: valDesc.trim() || null,
        ltmRevenue: String(valRev),
        ltmRevenueWeight: valRevW,
        ltmEbitda: valEbitda === "" || valEbitda == null ? null : String(valEbitda),
        ltmEbitdaWeight: valEbitdaW,
        revenueYoyGrowth: valGrowth === "" || valGrowth == null ? null : String(valGrowth),
        revenueYoyGrowthWeight: valGrowthW,
        numPrecedentComps: valPrecN === "" ? null : String(valPrecN),
        numPublicComps: valPubN === "" ? null : String(valPubN),
        forcedMultipleType: valMultType || "None",
        forcedMultipleValue: valMultVal === "" || valMultVal == null ? null : String(valMultVal),
      });
      setValResponseData(data);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Valuation request failed.";
      setValError(typeof msg === "string" ? msg : "Valuation request failed.");
    } finally {
      setValLoading(false);
    }
  };

  const exportValuationExcel = async () => {
    if (!valResponseData?.Valuations) return;
    setValExportLoading(true);
    try {
      const response = await axios.post(
        "/api/export_valuations_data",
        {
          precedent_comps: valResponseData.Valuations.precedent_comps,
          public_company_comps: valResponseData.Valuations.public_company_comps,
          company_dict: valResponseData.Valuations.company_dict,
        },
        { responseType: "blob" }
      );
      const ct = response.headers["content-type"] || "";
      if (
        response.status === 200 &&
        ct.includes("spreadsheetml") &&
        response.data instanceof Blob
      ) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Modified_Comps_and_Football_Field.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        window.alert("Export did not return an Excel file. Please try again.");
      }
    } catch (err) {
      console.error(err);
      window.alert("Error exporting Excel file. Please try again.");
    } finally {
      setValExportLoading(false);
    }
  };

  const parseCsvFiles = useCallback(
    async (files) => {
      setCsvParseBusy(true);
      setCsvHeaders([]);
      setCsvRows([]);
      setCsvSaasSelections(["", "", "", "", ""]);
      resetSaasCsvCharts();

      const resetFiles = () => {
        setCsvFiles([]);
        if (csvInputRef.current) csvInputRef.current.value = "";
      };
      const parseOne = (file) =>
        new Promise((resolve, reject) => {
          Papa.parse(file, {
            header: false,
            skipEmptyLines: "greedy",
            complete: (result) => resolve(result.data || []),
            error: (err) => reject(new Error(`${file.name}: ${err.message || "parse error"}`)),
          });
        });
      const normHeader = (h, i) => {
        let v = String(h ?? "").trim();
        if (i === 0) v = v.replace(/^\uFEFF/, "");
        return v;
      };

      let parsed;
      try {
        parsed = await Promise.all(files.map(parseOne));
      } catch (err) {
        setCsvParseBusy(false);
        console.error(err);
        window.alert(`Could not read CSV: ${err.message}`);
        resetFiles();
        return;
      }

      const emptyFiles = files.filter((_, i) => !parsed[i].length).map((f) => f.name);
      if (emptyFiles.length) {
        setCsvParseBusy(false);
        window.alert(`The following CSV file(s) appear empty:\n${emptyFiles.join("\n")}`);
        resetFiles();
        return;
      }

      // All files must share the same column names (order may differ; rows are realigned to the first file).
      const baseHeaders = parsed[0][0].map(normHeader);
      const baseKey = [...baseHeaders].sort().join("\u0000");
      const mismatches = [];
      const allRows = [];
      files.forEach((file, fi) => {
        const headers = parsed[fi][0].map(normHeader);
        if ([...headers].sort().join("\u0000") !== baseKey) {
          const missing = baseHeaders.filter((h) => !headers.includes(h));
          const extra = headers.filter((h) => !baseHeaders.includes(h));
          mismatches.push(
            `• ${file.name}` +
              (missing.length ? `\n    missing: ${missing.join(", ")}` : "") +
              (extra.length ? `\n    unexpected: ${extra.join(", ")}` : "") +
              (!missing.length && !extra.length ? "\n    duplicate column names differ" : "")
          );
          return;
        }
        const order = baseHeaders.map((h) => headers.indexOf(h));
        const sameOrder = order.every((idx, i) => idx === i);
        parsed[fi].slice(1).forEach((row) => {
          if (row.length !== headers.length) return;
          if (!row.some((cell) => cell !== undefined && cell !== null && String(cell).trim() !== "")) return;
          allRows.push(sameOrder ? row : order.map((idx) => row[idx]));
        });
      });

      setCsvParseBusy(false);
      if (mismatches.length) {
        window.alert(
          `Column names are inconsistent across the uploaded CSV files.\n\n` +
            `Expected columns (from ${files[0].name}):\n${baseHeaders.join(", ")}\n\n` +
            `Mismatched file(s):\n${mismatches.join("\n")}`
        );
        resetFiles();
        return;
      }
      setCsvHeaders(baseHeaders);
      setCsvRows(allRows);
    },
    [resetSaasCsvCharts]
  );

  const runSaasCsvProcess = async () => {
    if (!csvRows.length || !csvHeaders.length) {
      window.alert("Upload a valid CSV first.");
      return;
    }
    if (csvSaasSelections.some((s) => !s)) {
      window.alert("Map all five columns before processing.");
      return;
    }
    setCsvProcessing(true);
    resetSaasCsvCharts();
    try {
      const { data } = await axios.post("/api/process_metrics", {
        csvData: csvRows,
        headers: csvHeaders,
        selections: csvSaasSelections,
      });
      if (data.error) {
        window.alert(typeof data.error === "string" ? data.error : "Processing failed.");
        return;
      }
      const metricsData = data.full_metrics;
      setSaasCsvChartData(metricsData);
      setSaasCsvDisplayData(metricsData);
      setSaasCsvPivotData(data.pivot_smoothed);
      setSaasCsvRecurringData(data.recurring_by_month);
      setSaasCsvNonRecurringData(data.non_recurring_by_month);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || err.message || "An error occurred while processing the data.";
      window.alert(typeof msg === "string" ? msg : "An error occurred while processing the data.");
    } finally {
      setCsvProcessing(false);
    }
  };

  const exportSaasCsvExcel = async () => {
    if (!saasCsvPivotData) return;
    setSaasCsvExportLoading(true);
    try {
      const response = await axios.post(
        "/api/export_metrics",
        { pivotData: saasCsvPivotData, formulaFlag: saasCsvUseFormulas },
        {
          responseType: "blob",
          headers: { "Content-Type": "application/json" },
        }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "fe_metrics_output.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      window.alert("Export complete. Your file has been downloaded.");
    } catch (err) {
      console.error(err);
      window.alert("An error occurred while exporting the data.");
    } finally {
      setSaasCsvExportLoading(false);
    }
  };

  const handleSaasCsvDateChange = (event) => {
    const newDate = event.target.value === "all" ? null : event.target.value;
    setSaasCsvSelectedDate(newDate);
    if (newDate && saasCsvRecurringData && saasCsvNonRecurringData) {
      const idx = saasCsvRecurringData.data.findIndex((item) => item[0] === newDate);
      if (idx !== -1) {
        setSaasCsvPieChartData(saasCsvCalculatePieChartData(saasCsvRecurringData, saasCsvNonRecurringData, idx));
        setSaasCsvStackedBarData(saasCsvCalculateStackedBarData(saasCsvRecurringData, saasCsvNonRecurringData, idx));
      }
    } else {
      setSaasCsvPieChartData(null);
      setSaasCsvStackedBarData(null);
    }
  };

  const clearCsvFile = () => {
    setCsvFiles([]);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvSaasSelections(["", "", "", "", ""]);
    setCsvParseBusy(false);
    resetSaasCsvCharts();
    if (csvInputRef.current) csvInputRef.current.value = "";
  };

  const onCsvPick = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const bad = files.filter((f) => !/\.csv$/i.test(f.name));
    if (bad.length) {
      window.alert(`Please choose .csv files only. Not a CSV:\n${bad.map((f) => f.name).join("\n")}`);
      if (csvInputRef.current) csvInputRef.current.value = "";
      return;
    }
    setCsvFiles(files.map((f) => ({ name: f.name, size: f.size })));
    parseCsvFiles(files);
  };

  const handleCsvMapChange = (index, value) => {
    setCsvSaasSelections((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  return (
    <div id="panel-metrics" style={{ animation: "fadeUp 0.22s ease" }}>
      <div className="mod-banner">
        <div>
          <h2>Metrics</h2>
          <p>Valuations with comparable transactions · SaaS metrics from payment CSV</p>
        </div>
        <span className="mod-pill">FE Intel</span>
      </div>

      <div className="metrics-page">
        <div className="metrics-hero">
          <h2>
            Business <span>Metrics &amp; Valuation</span>
          </h2>
          <p>Select a tool below to run a valuation or compute SaaS metrics from an uploaded payment CSV.</p>
        </div>

        <div className="metrics-tools-grid">
          <button
            type="button"
            className={`metrics-tool-card mt-val${metricsTool === "val" ? " active-mt" : ""}`}
            onClick={() => setMetricsTool("val")}
          >
            <div className="mt-icon v" aria-hidden>
              $
            </div>
            <div>
              <div className="mt-card-title">Valuations</div>
              <div className="mt-card-desc">
                Indicative valuation range based on comparable precedent transactions and public company analysis.
              </div>
            </div>
          </button>
          <button
            type="button"
            className={`metrics-tool-card mt-saas${metricsTool === "saas" ? " active-mt" : ""}`}
            onClick={() => setMetricsTool("saas")}
          >
            <div className="mt-icon s" aria-hidden>
              ∑
            </div>
            <div>
              <div className="mt-card-title">SaaS Metrics</div>
              <div className="mt-card-desc">
                MRR, ARR, churn, and related metrics from a payment CSV — map columns, process, and export to Excel.
              </div>
            </div>
          </button>
        </div>

        <div className="metrics-workspace">
          {metricsTool === "val" ? (
            <div id="mf-val">
              <div className="metrics-ws-header">
                <div>
                  <div className="metrics-ws-title">Valuations</div>
                  <div className="metrics-ws-sub">
                    Provides an indicative valuation range based on an analysis of comparable precedent transactions and public company.
                  </div>
                </div>
                <span className="mod-pill" style={{ fontSize: 10 }}>
                  AI Powered
                </span>
              </div>
              <div className="metrics-ws-body">
                <div className="mf-section-title">General Business Information</div>
                <div className="mf-grid2">
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-name">
                      Business Name / URL
                    </label>
                    <input
                      id="metrics-val-name"
                      className="mf-input"
                      value={valName}
                      onChange={(e) => setValName(e.target.value)}
                      placeholder="Enter Business Name or URL"
                    />
                  </div>
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-year">
                      Year Established
                    </label>
                    <input
                      id="metrics-val-year"
                      className="mf-input"
                      type="number"
                      min={1980}
                      max={currentYear}
                      value={valYear}
                      onChange={(e) => setValYear(e.target.value)}
                      placeholder="Enter Year Established"
                    />
                  </div>
                </div>
                <div className="mf-grid1">
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-desc">
                      Business Description <span className="mf-label-hint">(Optional)</span>
                    </label>
                    <textarea
                      id="metrics-val-desc"
                      className="mf-textarea"
                      value={valDesc}
                      onChange={(e) => setValDesc(e.target.value)}
                      placeholder="Enter Business Description (Optional)"
                    />
                  </div>
                </div>

                <div className="mf-section-title">Financial Information</div>
                <div className="mf-grid2">
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-rev">
                      Last 12 Months Revenue ($)
                    </label>
                    <input
                      id="metrics-val-rev"
                      className="mf-input"
                      type="number"
                      min={0}
                      value={valRev}
                      onChange={(e) => setValRev(e.target.value)}
                      placeholder="Enter Last 12 Months Revenue ($)"
                    />
                  </div>
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-rev-w">
                      LTM Revenue Weight <span className="mf-label-hint">{valRevW}</span>
                    </label>
                    <div className="mf-slider-wrap">
                      <input
                        id="metrics-val-rev-w"
                        className="mf-slider"
                        type="range"
                        min={1}
                        max={5}
                        value={valRevW}
                        onChange={(e) => setValRevW(Number(e.target.value))}
                      />
                      <div className="mf-slider-labels">
                        <span>1</span>
                        <span>2</span>
                        <span>3</span>
                        <span>4</span>
                        <span>5</span>
                      </div>
                    </div>
                  </div>
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-ebitda">
                      Last 12 Months EBITDA ($)
                    </label>
                    <input
                      id="metrics-val-ebitda"
                      className="mf-input"
                      type="number"
                      min={0}
                      value={valEbitda}
                      onChange={(e) => setValEbitda(e.target.value)}
                      placeholder="Enter Last 12 Months EBITDA ($)"
                    />
                  </div>
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-ebitda-w">
                      LTM EBITDA Weight <span className="mf-label-hint">{valEbitdaW}</span>
                    </label>
                    <div className="mf-slider-wrap">
                      <input
                        id="metrics-val-ebitda-w"
                        className="mf-slider"
                        type="range"
                        min={1}
                        max={5}
                        value={valEbitdaW}
                        onChange={(e) => setValEbitdaW(Number(e.target.value))}
                      />
                      <div className="mf-slider-labels">
                        <span>1</span>
                        <span>2</span>
                        <span>3</span>
                        <span>4</span>
                        <span>5</span>
                      </div>
                    </div>
                  </div>
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-growth">
                      Revenue YoY Growth (%)
                    </label>
                    <input
                      id="metrics-val-growth"
                      className="mf-input"
                      type="number"
                      value={valGrowth}
                      onChange={(e) => setValGrowth(e.target.value)}
                      placeholder="Enter Revenue YoY Growth (%)"
                    />
                  </div>
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-growth-w">
                      Revenue YoY Growth Weight <span className="mf-label-hint">{valGrowthW}</span>
                    </label>
                    <div className="mf-slider-wrap">
                      <input
                        id="metrics-val-growth-w"
                        className="mf-slider"
                        type="range"
                        min={1}
                        max={5}
                        value={valGrowthW}
                        onChange={(e) => setValGrowthW(Number(e.target.value))}
                      />
                      <div className="mf-slider-labels">
                        <span>1</span>
                        <span>2</span>
                        <span>3</span>
                        <span>4</span>
                        <span>5</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mf-section-title">Output Configuration</div>
                <div className="mf-grid2">
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-prec">
                      Desired No. of Precedent Comps
                    </label>
                    <input
                      id="metrics-val-prec"
                      className="mf-input"
                      type="number"
                      min={1}
                      max={15}
                      value={valPrecN}
                      onChange={(e) => setValPrecN(e.target.value)}
                      placeholder="e.g. 5"
                    />
                  </div>
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-pub">
                      Desired No. of Public Company Comps
                    </label>
                    <input
                      id="metrics-val-pub"
                      className="mf-input"
                      type="number"
                      min={1}
                      max={10}
                      value={valPubN}
                      onChange={(e) => setValPubN(e.target.value)}
                      placeholder="e.g. 3"
                    />
                  </div>
                </div>
                <div className="mf-grid2">
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-mult-type">
                      Set Forced Multiple Type <span className="mf-label-hint">(Optional)</span>
                    </label>
                    <select
                      id="metrics-val-mult-type"
                      className="mf-select"
                      value={valMultType}
                      onChange={(e) => setValMultType(e.target.value)}
                    >
                      <option value="None">None</option>
                      <option value="Revenue">Revenue</option>
                      <option value="EBITDA">EBITDA</option>
                    </select>
                  </div>
                  <div className="mf-row">
                    <label className="mf-label" htmlFor="metrics-val-mult-val">
                      Forced Multiple Value <span className="mf-label-hint">(Optional)</span>
                    </label>
                    <input
                      id="metrics-val-mult-val"
                      className="mf-input"
                      type="number"
                      min={0}
                      step={0.1}
                      value={valMultVal}
                      onChange={(e) => setValMultVal(e.target.value)}
                      placeholder="Enter Forced Multiple Value (Optional)"
                    />
                  </div>
                </div>

                <button type="button" className="mf-submit-btn" onClick={runValuation} disabled={valLoading}>
                  Submit
                </button>
              </div>

              {valError ? (
                <div className="metrics-output" style={{ paddingTop: 16 }}>
                  <div className="metrics-placeholder-note" style={{ color: "#b91c1c" }}>
                    {valError}
                  </div>
                </div>
              ) : null}

              {valLoading ? (
                <div className="metrics-loading" role="status" aria-live="polite">
                  <div className="metrics-spinner" />
                  <span>Analysing comparable transactions…</span>
                </div>
              ) : null}

              {valChartData && valResponseData?.Valuations && !valLoading ? (
                <div className="metrics-output metrics-val-results">
                  <div className="metrics-output-actions">
                    <button
                      type="button"
                      className="mout-btn"
                      disabled={valExportLoading}
                      onClick={exportValuationExcel}
                    >
                      {valExportLoading ? "Exporting…" : "⬇ Export Excel"}
                    </button>
                    <button
                      type="button"
                      className="mout-btn"
                      onClick={() => {
                        setValResponseData(null);
                        setValChartData(null);
                        setValPrecedentComps([]);
                        setValPublicComps([]);
                        setValXAxisLabels([]);
                        setValError("");
                      }}
                    >
                      ✕ Clear results
                    </button>
                  </div>

                  <h3 className="metrics-val-results-title">Valuation results</h3>
                  <p className="metrics-val-intro">
                    Based on our analysis, the indicative valuation range for the business is estimated to be between{" "}
                    <strong>
                      {formatValuationUSD(valResponseData.Valuations.valuation_range_dict.indicative_range_low)}
                    </strong>{" "}
                    and{" "}
                    <strong>
                      {formatValuationUSD(valResponseData.Valuations.valuation_range_dict.indicative_range_high)}
                    </strong>
                    . This range reflects financial performance, market positioning, and comparable company multiples.
                  </p>

                  <div className="metrics-val-chart-wrap">
                    <Bar data={valChartData} options={valuationChartOptions} plugins={valuationBarPlugins} />
                  </div>

                  <h4 className="metrics-val-table-title">Precedent transaction comps</h4>
                  {valPrecedentComps.length > 0 ? (
                    <div className="metrics-table-scroll">
                      <table className="metrics-data-table">
                        <thead>
                          <tr>
                            <th>Company</th>
                            <th>Primary industry</th>
                            <th className="num">Revenue</th>
                            <th className="ctr">EBITDA multiple</th>
                            <th>Data source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {valPrecedentComps.map((company, index) => (
                            <tr key={`p-${index}`}>
                              <td>{company.Companies}</td>
                              <td>{company["Primary Industry Code"]}</td>
                              <td className="num">{formatValuationUSD(Math.round(Number(company.Revenue) || 0))}</td>
                              <td className="ctr">
                                {company["Implied EVdivEBITDA"] != null
                                  ? `${Math.round(Number(company["Implied EVdivEBITDA"]) * 10) / 10}x`
                                  : "—"}
                              </td>
                              <td>{company["Data Source"]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="metrics-val-empty">No precedent comps returned.</p>
                  )}

                  <h4 className="metrics-val-table-title">Public company comps</h4>
                  {valPublicComps.length > 0 ? (
                    <div className="metrics-table-scroll">
                      <table className="metrics-data-table">
                        <thead>
                          <tr>
                            <th>Company</th>
                            <th>Primary industry</th>
                            <th>Primary geo</th>
                            <th className="num">Revenue</th>
                            <th className="ctr">EBITDA multiple</th>
                            <th>Data source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {valPublicComps.map((company, index) => (
                            <tr key={`u-${index}`}>
                              <td>{company.Companies}</td>
                              <td>{company["Primary Industry Code"]}</td>
                              <td>{company["Primary Geo"]}</td>
                              <td className="num">{formatValuationUSD(Math.round(Number(company.Revenue) || 0))}</td>
                              <td className="ctr">
                                {company["Implied EVdivEBITDA"] != null
                                  ? `${Math.round(Number(company["Implied EVdivEBITDA"]) * 10) / 10}x`
                                  : "—"}
                              </td>
                              <td>{company["Data Source"]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="metrics-val-empty">No public comps returned.</p>
                  )}
                </div>
              ) : null}
            </div>
          ) : (
            <div id="mf-saas">
              <div className="metrics-ws-header">
                <div>
                  <div className="metrics-ws-title">SaaS Metrics</div>
                  <div className="metrics-ws-sub">
                    Upload a payment CSV, map columns, and process to compute MRR, ARR, churn, and related metrics.
                  </div>
                </div>
                <span
                  className="mod-pill"
                  style={{ fontSize: 10, background: "rgba(16,185,129,0.12)", color: "#059669", border: "1px solid rgba(16,185,129,0.25)" }}
                >
                  CSV
                </span>
              </div>

              <div className="metrics-ws-body">
                  <label
                    className={`csv-dropzone${csvDragging ? " dragging" : ""}${csvFiles.length ? " has-file" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setCsvDragging(true);
                    }}
                    onDragLeave={() => setCsvDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setCsvDragging(false);
                      onCsvPick(e.dataTransfer.files);
                    }}
                  >
                    <input
                      id="metrics-saas-csv-upload"
                      ref={csvInputRef}
                      className="csv-dz-input"
                      type="file"
                      accept=".csv,text/csv"
                      multiple
                      aria-label="Upload payment CSV files"
                      onChange={(e) => onCsvPick(e.target.files)}
                    />
                    <span className="csv-dz-icon" aria-hidden>
                      📄
                    </span>
                    <div className="csv-dz-title">Drop your payment CSV(s) here</div>
                    <div className="csv-dz-sub">
                      or click to browse · .csv files only · multiple files are combined (column names must match)
                      <br />
                      Map columns to: transaction date, customer ID, currency, payment frequency, and net amount.
                    </div>
                  </label>

                  {csvFiles.length ? (
                    <div className="csv-file-info">
                      <span className="csv-file-icon" aria-hidden>
                        ✓
                      </span>
                      <div>
                        <div className="csv-file-name">
                          {csvFiles.length === 1 ? csvFiles[0].name : `${csvFiles.length} files combined`}
                        </div>
                        <div className="csv-file-size">
                          {csvFiles.length === 1
                            ? fmtMetricsFileSize(csvFiles[0].size)
                            : csvFiles.map((f) => `${f.name} (${fmtMetricsFileSize(f.size)})`).join(" · ")}
                        </div>
                      </div>
                      <button type="button" className="csv-remove-btn" onClick={clearCsvFile}>
                        ✕ Remove
                      </button>
                    </div>
                  ) : null}

                  {csvParseBusy ? (
                    <div className="csv-processing" role="status">
                      <div className="metrics-spinner" />
                      <span>Parsing CSV{csvFiles.length > 1 ? "s" : ""}…</span>
                    </div>
                  ) : null}

                  <div className="mf-section-title" style={{ marginTop: csvFiles.length ? 20 : 0 }}>
                    Map Your Columns
                  </div>
                  <p className="csv-dz-sub" style={{ marginBottom: 14 }}>
                    Choose which column in your file corresponds to each required field, then run process to call the metrics pipeline.
                  </p>
                  <div className="csv-mapping-grid">
                    {SAAS_CSV_MAP_FIELDS.map(([label, hint], i) => (
                      <div className="csv-map-row" key={label}>
                        <label className="csv-map-label" htmlFor={`metrics-csv-map-${i}`}>
                          <span className="csv-map-req" />
                          {label}
                        </label>
                        <select
                          id={`metrics-csv-map-${i}`}
                          className="csv-map-select"
                          disabled={!csvHeaders.length || csvParseBusy}
                          value={csvSaasSelections[i] || ""}
                          onChange={(e) => handleCsvMapChange(i, e.target.value)}
                        >
                          <option value="">— Select column —</option>
                          {csvHeaders.map((header, hi) => (
                            <option key={hi} value={header}>
                              {header === "" || header == null ? `(empty ${hi})` : String(header)}
                            </option>
                          ))}
                        </select>
                        <div className="csv-map-hint">{hint}</div>
                      </div>
                    ))}
                  </div>

                  {csvHeaders.length > 0 && csvRows.length > 0 ? (
                    <p className="csv-dz-sub" style={{ marginBottom: 12 }}>
                      <strong>{csvRows.length.toLocaleString()}</strong> data rows loaded
                      {csvHeaders.length ? ` · ${csvHeaders.length} columns` : ""}.
                    </p>
                  ) : null}

                  {csvProcessing ? (
                    <div className="csv-processing" role="status">
                      <div className="metrics-spinner" />
                      <span>Processing payment data…</span>
                    </div>
                  ) : null}

                  <button
                    type="button"
                    className="csv-process-btn"
                    onClick={runSaasCsvProcess}
                    disabled={
                      !csvFiles.length ||
                      !csvRows.length ||
                      csvSaasSelections.some((s) => !s) ||
                      csvProcessing ||
                      csvParseBusy
                    }
                  >
                    Process Data
                  </button>
                </div>

              {saasCsvChartData && Array.isArray(saasCsvDisplayData) ? (
                <div className="metrics-output">
                  <h3 className="metrics-val-results-title" style={{ marginTop: 4 }}>
                    SaaS metrics
                  </h3>
                  <div className="saas-csv-export-row">
                    <label htmlFor="saas-csv-formulas" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                      <span style={{ fontWeight: 600, color: "var(--text-sub)" }}>Excel formulas</span>
                      <select
                        id="saas-csv-formulas"
                        className="csv-map-select"
                        style={{ minWidth: 100 }}
                        value={saasCsvUseFormulas ? "yes" : "no"}
                        onChange={(e) => setSaasCsvUseFormulas(e.target.value === "yes")}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      className="mout-btn"
                      onClick={exportSaasCsvExcel}
                      disabled={saasCsvExportLoading || !saasCsvPivotData}
                    >
                      {saasCsvExportLoading ? "Exporting…" : "⬇ Export to Excel"}
                    </button>
                  </div>
                  <div className="saas-csv-chart-toolbar">
                    <label htmlFor="saas-csv-metric">
                      Metric
                      <select
                        id="saas-csv-metric"
                        value={saasCsvSelectedVariable}
                        onChange={(e) => setSaasCsvSelectedVariable(e.target.value)}
                      >
                        {SAAS_CSV_CHART_VARIABLES.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label htmlFor="saas-csv-end-date">
                      Last 12 months ending
                      <select id="saas-csv-end-date" value={saasCsvSelectedDate || "all"} onChange={handleSaasCsvDateChange}>
                        <option value="all">Show all</option>
                        {saasCsvChartData.map((item, idx) => (
                          <option key={idx} value={item.date}>
                            {item.date}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="saas-csv-recharts-wrap">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={saasCsvDisplayData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" height={56} tick={<SaasCsvAxisTick />} />
                        <YAxis
                          tickFormatter={(val) => formatSaasCsvChartValue(val, saasCsvSelectedVariable)}
                          tick={{ fontSize: 10 }}
                        />
                        <RechartsTooltip formatter={(val) => [formatSaasCsvChartValue(val, saasCsvSelectedVariable), saasCsvSelectedVariable]} />
                        <Line
                          type="monotone"
                          dataKey={saasCsvSelectedVariable}
                          stroke="#6487dc"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 6 }}
                          label={(props) => {
                            const { x, y, value } = props;
                            if (x == null || y == null) return null;
                            return (
                              <text x={x} y={y} dy={-10} fill="#666" fontSize={10} textAnchor="middle">
                                {formatSaasCsvChartValue(value, saasCsvSelectedVariable)}
                              </text>
                            );
                          }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  {saasCsvPieChartData && saasCsvStackedBarData?.length ? (
                    <div className="saas-csv-pie-row">
                      <div className="saas-csv-pie-cell">
                        <div className="saas-csv-pie-title">LTM revenue — recurring vs non-recurring</div>
                        <div className="saas-csv-recharts-wrap" style={{ height: 300 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={saasCsvPieChartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={88}
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              >
                                <Cell fill="#6487dc" />
                                <Cell fill="#808080" />
                              </Pie>
                              <RechartsTooltip formatter={(val, name) => [formatSaasCsvStackedDollar(val), name]} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      <div className="saas-csv-pie-cell">
                        <div className="saas-csv-pie-title">LTM revenue vs prior periods</div>
                        <div className="saas-csv-recharts-wrap" style={{ height: 300 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={saasCsvStackedBarData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="period" height={72} interval={0} tick={{ fontSize: 10 }} />
                              <YAxis tickFormatter={formatSaasCsvStackedDollar} tick={{ fontSize: 10 }} />
                              <RechartsTooltip formatter={(val, name) => [formatSaasCsvStackedDollar(val), name]} />
                              <RechartsBar dataKey="Recurring" stackId="a" fill="#6487dc" />
                              <RechartsBar dataKey="Non-Recurring" stackId="a" fill="#808080" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function readFileAsBase64Payload(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || "");
      const i = res.indexOf(",");
      resolve({ filename: file.name, data: i >= 0 ? res.slice(i + 1) : res });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function readFileAsUtf8Text(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file, "UTF-8");
  });
}

const CIM_ET_SECTIONS = [
  { key: "exec_summary", label: "Exec. Summary" },
  { key: "investment_merits", label: "Inv. Merits" },
  { key: "industry", label: "Industry" },
  { key: "competitive", label: "Competition" },
  { key: "company", label: "Company" },
  { key: "products", label: "Products" },
  { key: "gtm", label: "GTM & Pricing" },
  { key: "growth", label: "Growth" },
  { key: "customers", label: "Customers" },
  { key: "financial", label: "Financials" },
  { key: "management", label: "Mgmt & Assets" },
  { key: "risks", label: "Risks" },
  { key: "contact", label: "Contact" },
];

const CIM_GEN_STATUS_MSGS = [
  "Researching company and web context…",
  "Synthesising investment narrative…",
  "Drafting Exec. Summary…",
  "Drafting Inv. Merits…",
  "Drafting Industry & Competition…",
  "Drafting Company & Products…",
  "Drafting GTM, Growth & Customers…",
  "Drafting Financials & Mgmt…",
  "Drafting Risks & Contact…",
];

const ET_MODEL_UPLOAD_ACCEPT = ".xlsx";
const ET_MODEL_COL_TARGETS = [
  { key: "period", label: "Period / Date", required: true },
  { key: "revenue", label: "Revenue (or GMV)", required: true },
  { key: "ebitda", label: "EBITDA (or contribution)", required: false },
  { key: "cogs", label: "COGS / variable costs", required: false },
];

/** Manual model inputs — same fields as FE_Intel_ET12.html `etModelShowManual`. */
const ET_MODEL_MANUAL_FIELDS = [
  { key: "ltmRevenue", label: "LTM Revenue ($)", placeholder: "e.g. 4200000" },
  { key: "arr", label: "ARR ($)", placeholder: "e.g. 4200000" },
  { key: "ltmEbitda", label: "LTM EBITDA ($)", placeholder: "e.g. 1806000" },
  { key: "grossMargin", label: "Gross Margin (%)", placeholder: "e.g. 80" },
  { key: "totalCustomers", label: "Total Customers", placeholder: "e.g. 250" },
  { key: "cac", label: "CAC ($)", placeholder: "e.g. 1200" },
  { key: "ltv", label: "LTV ($)", placeholder: "e.g. 14400" },
];

function emptyEtModelManualMetrics() {
  return Object.fromEntries(ET_MODEL_MANUAL_FIELDS.map((f) => [f.key, ""]));
}
const ET_MODEL_TEMPLATES = [
  { value: "saas", label: "SaaS / ARR Model" },
  { value: "ecomm", label: "E-Commerce / GMV Model" },
  { value: "3stmt", label: "3-Statement Model" },
  { value: "lbo", label: "LBO Model" },
  { value: "content", label: "Content / Affiliate Model" },
  { value: "services", label: "Digital Services Model" },
];

function etModelAssumptionSchema(template) {
  switch (template) {
    case "ecomm":
      return [
        { key: "gmvGrowth", label: "GMV growth (YoY %)", min: -15, max: 70, step: 1, def: 18 },
        { key: "takeRate", label: "Net take rate (%)", min: 5, max: 35, step: 0.5, def: 12 },
      ];
    case "3stmt":
      return [
        { key: "revGrowth", label: "Revenue growth (YoY %)", min: -10, max: 45, step: 1, def: 15 },
        { key: "cogsPct", label: "COGS (% of revenue)", min: 20, max: 85, step: 1, def: 42 },
        { key: "opexGrowth", label: "OpEx growth (YoY %)", min: -5, max: 30, step: 1, def: 8 },
      ];
    case "lbo":
      return [
        { key: "entryMult", label: "Entry EV / EBITDA (×)", min: 4, max: 18, step: 0.5, def: 8.5 },
        { key: "leverage", label: "Debt / EBITDA (×)", min: 0, max: 7, step: 0.25, def: 4 },
        { key: "exitMult", label: "Exit EV / EBITDA (×)", min: 4, max: 20, step: 0.5, def: 9.5 },
      ];
    case "content":
      return [
        { key: "trafficGrowth", label: "Traffic growth (YoY %)", min: -20, max: 60, step: 1, def: 12 },
        { key: "rpmGrowth", label: "RPM / monetisation uplift (%)", min: -10, max: 40, step: 1, def: 6 },
      ];
    case "services":
      return [
        { key: "util", label: "Billable utilisation (%)", min: 40, max: 95, step: 1, def: 72 },
        { key: "rateGrowth", label: "Blended rate growth (YoY %)", min: -5, max: 25, step: 0.5, def: 4 },
      ];
    case "saas":
    default:
      return [
        { key: "revGrowth", label: "Revenue growth (YoY %)", min: -15, max: 80, step: 1, def: 28 },
        { key: "ebitdaMargin", label: "EBITDA margin target (%)", min: 5, max: 55, step: 1, def: 24 },
        { key: "churn", label: "Net revenue churn (annual %)", min: 0, max: 35, step: 0.5, def: 8 },
      ];
  }
}

/** Scenario sliders per financial model template — mirrors FE_Intel_ET12.html `ET_TEMPLATES`. */
const ET_SCENARIO_TEMPLATES = {
  saas: {
    label: "SaaS / ARR Model",
    assumptions: [
      { id: "rev_cagr", label: "Revenue CAGR (%)", default: 22, min: 0, max: 150, step: 1 },
      { id: "gross_margin", label: "Gross Margin (%)", default: 80, min: 20, max: 100, step: 1 },
      { id: "ebitda_exit", label: "Exit EBITDA Margin (%)", default: 35, min: 0, max: 80, step: 1 },
      { id: "churn", label: "Annual Churn (%)", default: 5, min: 0, max: 50, step: 0.5 },
      { id: "entry_mult", label: "Entry EV/EBITDA (x)", default: 6.5, min: 1, max: 25, step: 0.5 },
      { id: "exit_mult", label: "Exit EV/EBITDA (x)", default: 7, min: 1, max: 30, step: 0.5 },
    ],
  },
  ecomm: {
    label: "E-Commerce / GMV Model",
    assumptions: [
      { id: "rev_cagr", label: "Revenue CAGR (%)", default: 25, min: 0, max: 150, step: 1 },
      { id: "gross_margin", label: "Gross Margin (%)", default: 45, min: 10, max: 80, step: 1 },
      { id: "ebitda_exit", label: "Exit EBITDA Margin (%)", default: 18, min: 0, max: 50, step: 1 },
      { id: "take_rate", label: "Take Rate (%)", default: 12, min: 1, max: 40, step: 0.5 },
      { id: "entry_mult", label: "Entry EV/EBITDA (x)", default: 7, min: 1, max: 25, step: 0.5 },
      { id: "exit_mult", label: "Exit EV/EBITDA (x)", default: 8, min: 1, max: 30, step: 0.5 },
    ],
  },
  "3stmt": {
    label: "3-Statement Model",
    assumptions: [
      { id: "rev_cagr", label: "Revenue CAGR (%)", default: 15, min: 0, max: 100, step: 1 },
      { id: "gross_margin", label: "Gross Margin (%)", default: 55, min: 10, max: 95, step: 1 },
      { id: "ebitda_exit", label: "Exit EBITDA Margin (%)", default: 22, min: 0, max: 60, step: 1 },
      { id: "capex_pct", label: "CapEx (% Revenue)", default: 3, min: 0, max: 30, step: 0.5 },
      { id: "entry_mult", label: "Entry EV/EBITDA (x)", default: 6, min: 1, max: 20, step: 0.5 },
      { id: "exit_mult", label: "Exit EV/EBITDA (x)", default: 7, min: 1, max: 25, step: 0.5 },
    ],
  },
  lbo: {
    label: "LBO Model",
    assumptions: [
      { id: "rev_cagr", label: "Revenue CAGR (%)", default: 12, min: 0, max: 80, step: 1 },
      { id: "ebitda_exit", label: "Exit EBITDA Margin (%)", default: 28, min: 0, max: 60, step: 1 },
      { id: "debt_equity", label: "Debt/Equity Ratio", default: 1.5, min: 0, max: 5, step: 0.1 },
      { id: "debt_rate", label: "Interest Rate (%)", default: 7, min: 2, max: 20, step: 0.5 },
      { id: "entry_mult", label: "Entry EV/EBITDA (x)", default: 8, min: 3, max: 20, step: 0.5 },
      { id: "exit_mult", label: "Exit EV/EBITDA (x)", default: 9, min: 3, max: 25, step: 0.5 },
    ],
  },
  content: {
    label: "Content / Affiliate Model",
    assumptions: [
      { id: "rev_cagr", label: "Revenue CAGR (%)", default: 20, min: 0, max: 150, step: 1 },
      { id: "gross_margin", label: "Gross Margin (%)", default: 70, min: 20, max: 100, step: 1 },
      { id: "ebitda_exit", label: "Exit EBITDA Margin (%)", default: 38, min: 0, max: 70, step: 1 },
      { id: "traffic_cagr", label: "Traffic CAGR (%)", default: 15, min: -20, max: 100, step: 1 },
      { id: "entry_mult", label: "Entry EV/EBITDA (x)", default: 5, min: 1, max: 20, step: 0.5 },
      { id: "exit_mult", label: "Exit EV/EBITDA (x)", default: 6, min: 1, max: 25, step: 0.5 },
    ],
  },
  services: {
    label: "Digital Services Model",
    assumptions: [
      { id: "rev_cagr", label: "Revenue CAGR (%)", default: 18, min: 0, max: 100, step: 1 },
      { id: "gross_margin", label: "Gross Margin (%)", default: 60, min: 20, max: 90, step: 1 },
      { id: "ebitda_exit", label: "Exit EBITDA Margin (%)", default: 25, min: 0, max: 55, step: 1 },
      { id: "utilisation", label: "Team Utilisation (%)", default: 78, min: 40, max: 100, step: 1 },
      { id: "entry_mult", label: "Entry EV/EBITDA (x)", default: 5.5, min: 1, max: 18, step: 0.5 },
      { id: "exit_mult", label: "Exit EV/EBITDA (x)", default: 6.5, min: 1, max: 22, step: 0.5 },
    ],
  },
};

const ET_SCENARIO_MULTIPLIERS = {
  base: { rev_cagr: 1, ebitda_exit: 1, entry_mult: 1, exit_mult: 1, churn: 1, gross_margin: 1 },
  up: { rev_cagr: 1.55, ebitda_exit: 1.15, entry_mult: 1.1, exit_mult: 1.2, churn: 0.6, gross_margin: 1.05 },
  dn: { rev_cagr: 0.45, ebitda_exit: 0.8, entry_mult: 0.85, exit_mult: 0.7, churn: 2.2, gross_margin: 0.95 },
};

function etScenarioClamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}

function etScenarioSliderValuesForTemplate(template) {
  const def = ET_SCENARIO_TEMPLATES[template] || ET_SCENARIO_TEMPLATES.saas;
  const out = { base: {}, up: {}, dn: {} };
  for (const sc of ["base", "up", "dn"]) {
    const mult = ET_SCENARIO_MULTIPLIERS[sc];
    for (const a of def.assumptions) {
      const m = mult[a.id] ?? 1;
      const scVal =
        Math.round(a.default * m * (a.step < 1 ? 10 : 1)) / (a.step < 1 ? 10 : 1);
      out[sc][a.id] = etScenarioClamp(scVal, a.min, a.max);
    }
  }
  return out;
}

function etScenarioFormatAssumptionValue(assumptionId, value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  const suffix = String(assumptionId).includes("mult") ? "x" : "%";
  return `${n}${suffix}`;
}

/** Matches Scenario Analysis “Computed Projections” summary formatting. */
function formatComputedProjectionsSummary(proj) {
  if (!proj || !Array.isArray(proj.years)) return "";
  const lastIdx = proj.years.length - 1;
  const hy = proj.horizonYears ?? lastIdx;
  return (
    `Horizon: ${hy} years\n` +
    `Revenue (Y0 → Y${hy}): ${Number(proj.revenue?.[0] ?? 0).toLocaleString()} → ${Number(proj.revenue?.[lastIdx] ?? 0).toLocaleString()}\n` +
    `Customers (Y0 → Y${hy}): ${Number(proj.totalCustomers?.[0] ?? 0).toLocaleString()} → ${Number(proj.totalCustomers?.[lastIdx] ?? 0).toLocaleString()}\n` +
    `Entry EV: ${Number(proj.entryEv ?? 0).toLocaleString()}\n` +
    `Exit EV: ${Number(proj.exitEv ?? 0).toLocaleString()}\n` +
    `Exit EBITDA: ${Number(proj.exitEbitda ?? 0).toLocaleString()}`
  );
}

const ET_SCENARIO_UPLOAD_ACCEPT = ".xlsx";

const CIM_FIN_UPLOAD_ACCEPT = ".xlsx,.xls,.csv,.pdf,.docx,.txt,.doc";
const CIM_NOTES_UPLOAD_ACCEPT = ".pdf,.docx,.pptx,.txt,.xlsx,.csv,.doc";
const CIM_UPLOAD_MAX_FILES = 5;
const CIM_UPLOAD_MAX_BYTES = 2 * 1024 * 1024;
/** Integration “Export Full Package” — uploaded CIM/Model must be plain .txt */
const INT_PACK_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

function fileMatchesCimAccept(fileName, acceptCommaList) {
  const lower = (fileName || "").toLowerCase();
  const exts = acceptCommaList.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
  return exts.some((ext) => {
    const e = ext.startsWith(".") ? ext : `.${ext}`;
    return lower.endsWith(e);
  });
}

/** ET-style supplementary file row (Known Financials / Analyst Notes) — payloads match origination `{ filename, data }`. */
function CimSupplementaryUploads({ idPrefix, buttonLabel, extensionsHint, acceptAttr, items, onItemsChange }) {
  const inputRef = useRef(null);
  const safe = Array.isArray(items) ? items : [];

  const ingestFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const rowsToAdd = [];
    for (const file of files) {
      if (!fileMatchesCimAccept(file.name, acceptAttr)) {
        window.alert(`${file.name}: type not allowed for this field. Use: ${extensionsHint}`);
        continue;
      }
      if (file.size > CIM_UPLOAD_MAX_BYTES) {
        window.alert(`${file.name} is larger than 2 MB and was skipped.`);
        continue;
      }
      try {
        rowsToAdd.push(await readFileAsBase64Payload(file));
      } catch {
        window.alert(`Could not read ${file.name}.`);
      }
    }
    if (!rowsToAdd.length) return;
    onItemsChange((prev) => {
      const base = Array.isArray(prev) ? [...prev] : [];
      for (const row of rowsToAdd) {
        if (base.length >= CIM_UPLOAD_MAX_FILES) {
          window.alert(`Maximum ${CIM_UPLOAD_MAX_FILES} files for this field.`);
          break;
        }
        base.push(row);
      }
      return base;
    });
  };

  const onPick = async (e) => {
    const input = e.target;
    const picked = Array.from(input.files || []);
    input.value = "";
    await ingestFiles(picked);
  };

  const removeAt = (ix) => {
    onItemsChange((prev) => (Array.isArray(prev) ? prev : []).filter((_, i) => i !== ix));
  };

  return (
    <div
      style={{ marginTop: 6 }}
      onDragOver={(ev) => {
        ev.preventDefault();
        ev.stopPropagation();
      }}
      onDrop={async (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        await ingestFiles(ev.dataTransfer?.files);
      }}
    >
      <input
        ref={inputRef}
        id={`${idPrefix}-cim-file`}
        type="file"
        multiple
        accept={acceptAttr}
        style={{ display: "none" }}
        onChange={onPick}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
        <button
          type="button"
          className="et-btn et-btn-ghost et-btn-sm"
          style={{ fontSize: 10, gap: 5 }}
          onClick={() => inputRef.current?.click()}
        >
          <span aria-hidden>📎</span> {buttonLabel}
        </button>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8.5, color: "var(--text-dim)" }}>{extensionsHint}</span>
      </div>
      {safe.length > 0 ? (
        <div className="et-cim-attach-chips" role="list" aria-live="polite">
          {safe.map((it, ix) => {
            const name = it.filename || it.name || `File ${ix + 1}`;
            return (
              <div key={`${name}-${ix}`} className="et-cim-attach-chip" role="listitem">
                <span className="et-cim-attach-chip-name">{name}</span>
                <button type="button" className="et-cim-attach-remove" onClick={() => removeAt(ix)}>
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function ExecutionToolkitPanel() {
  const modelInputRef = useRef(null);
  const [sideView, setSideView] = useState("cim-setup");
  const [companyName, setCompanyName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [projectCode, setProjectCode] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [knownFinancials, setKnownFinancials] = useState("");
  const [analystNotes, setAnalystNotes] = useState("");
  const [cimFinancialDocs, setCimFinancialDocs] = useState([]);
  const [cimAnalystDocs, setCimAnalystDocs] = useState([]);
  const [cimSections, setCimSections] = useState(() =>
    Object.fromEntries(CIM_ET_SECTIONS.map(({ key }) => [key, ""]))
  );
  const [activeSec, setActiveSec] = useState("exec_summary");
  const [cimGenLoading, setCimGenLoading] = useState(false);
  const [cimGenMsgIdx, setCimGenMsgIdx] = useState(0);
  const [cimError, setCimError] = useState("");
  const [extractedCompany, setExtractedCompany] = useState(null);
  const [copyFlash, setCopyFlash] = useState(false);
  const [cimExportOpen, setCimExportOpen] = useState(false);
  const [cimExportPptBusy, setCimExportPptBusy] = useState(false);
  const cimExportWrapRef = useRef(null);

  const [modelDragging, setModelDragging] = useState(false);
  const [modelFile, setModelFile] = useState(null);
  const [modelFileName, setModelFileName] = useState("");
  const [modelHeaders, setModelHeaders] = useState([]);
  const [modelColMap, setModelColMap] = useState(() =>
    Object.fromEntries(ET_MODEL_COL_TARGETS.map((t) => [t.key, ""]))
  );
  const [modelParseLoading, setModelParseLoading] = useState(false);
  const [modelCompany, setModelCompany] = useState("");
  const [modelTemplate, setModelTemplate] = useState("saas");
  const [modelHorizon, setModelHorizon] = useState("5");
  const [modelAssumptions, setModelAssumptions] = useState(() =>
    Object.fromEntries(etModelAssumptionSchema("saas").map((a) => [a.key, a.def]))
  );
  const [modelNarrativeNotes, setModelNarrativeNotes] = useState("");
  const [modelManualOpen, setModelManualOpen] = useState(false);
  const [modelManualMetrics, setModelManualMetrics] = useState(() => emptyEtModelManualMetrics());
  const [modelBuilt, setModelBuilt] = useState(false);
  const [modelRunLoading, setModelRunLoading] = useState(false);
  const [modelNarrativePlaceholder, setModelNarrativePlaceholder] = useState("");
  const [modelComputedProjections, setModelComputedProjections] = useState(null);
  const [modelCopyFlash, setModelCopyFlash] = useState(false);

  const scScenarioFileBaseRef = useRef(null);
  const scScenarioFileUpRef = useRef(null);
  const scScenarioFileDnRef = useRef(null);

  const [scTab, setScTab] = useState("base");
  const [scSliders, setScSliders] = useState(() => etScenarioSliderValuesForTemplate("saas"));
  const [scNotes, setScNotes] = useState({ base: "", up: "", dn: "" });
  const [scBusy, setScBusy] = useState({ base: false, up: false, dn: false });
  const [scProjText, setScProjText] = useState({ base: "", up: "", dn: "" });
  const [scNarrative, setScNarrative] = useState({ base: "", up: "", dn: "" });
  const [scComputed, setScComputed] = useState({ base: null, up: null, dn: null });
  const [scActualsChip, setScActualsChip] = useState({ base: null, up: null, dn: null });
  const [scActualsFile, setScActualsFile] = useState({ base: null, up: null, dn: null });
  const [scUploadDragging, setScUploadDragging] = useState({ base: false, up: false, dn: false });

  const intPackCimInputRef = useRef(null);
  const intPackModelInputRef = useRef(null);
  const [intPackDzCim, setIntPackDzCim] = useState(false);
  const [intPackDzModel, setIntPackDzModel] = useState(false);
  const [intPackCimName, setIntPackCimName] = useState("");
  const [intPackCimText, setIntPackCimText] = useState("");
  const [intPackModelName, setIntPackModelName] = useState("");
  const [intPackModelText, setIntPackModelText] = useState("");
  const [exportFullBusy, setExportFullBusy] = useState(false);

  const downloadModelTemplate = async () => {
    try {
      const res = await api.get("/templates/execution_toolkit_metric_template.xlsx", { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "execution_toolkit_metric_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert("Could not download template. Ensure backend is running.");
    }
  };

  useEffect(() => {
    if (!cimGenLoading) return undefined;
    const t = setInterval(() => {
      setCimGenMsgIdx((i) => (i + 1) % CIM_GEN_STATUS_MSGS.length);
    }, 2200);
    return () => clearInterval(t);
  }, [cimGenLoading]);

  useEffect(() => {
    if (!cimExportOpen) return undefined;
    const onDoc = (e) => {
      const el = cimExportWrapRef.current;
      if (el && !el.contains(e.target)) setCimExportOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [cimExportOpen]);

  useEffect(() => {
    const sch = etModelAssumptionSchema(modelTemplate);
    setModelAssumptions((prev) => {
      const next = {};
      for (const row of sch) {
        next[row.key] = prev[row.key] ?? row.def;
      }
      return next;
    });
  }, [modelTemplate]);

  useEffect(() => {
    if (sideView !== "model") return;
    const hint = (extractedCompany?.name || companyName || "").trim();
    if (hint && !modelCompany.trim()) setModelCompany(hint);
  }, [sideView, extractedCompany, companyName, modelCompany]);

  useEffect(() => {
    setScSliders(etScenarioSliderValuesForTemplate(modelTemplate));
    setScProjText({ base: "", up: "", dn: "" });
    setScNarrative({ base: "", up: "", dn: "" });
    setScComputed({ base: null, up: null, dn: null });
    setScActualsChip({ base: null, up: null, dn: null });
    setScActualsFile({ base: null, up: null, dn: null });
    setScUploadDragging({ base: false, up: false, dn: false });
  }, [modelTemplate]);

  const scenarioAssumptionRows = useMemo(
    () => (ET_SCENARIO_TEMPLATES[modelTemplate] || ET_SCENARIO_TEMPLATES.saas).assumptions,
    [modelTemplate]
  );

  const buildModelAssumptionsFromSliders = (sliders, horizonYears) => {
    const out = {};
    for (const row of scenarioAssumptionRows) {
      const v = sliders?.[row.id];
      if (v == null || Number.isNaN(Number(v))) continue;
      out[row.label] = v;
    }
    // Ensure required key is always present using backend's expected label.
    out["Forecast Horizon (Years)"] = Number(horizonYears || 5);
    return out;
  };

  const ingestModelFiles = async (fileList) => {
    const file = Array.from(fileList || [])[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx")) {
      window.alert("Please upload a .xlsx file (template format).");
      return;
    }
    setModelParseLoading(true);
    setModelBuilt(false);
    setModelNarrativePlaceholder("");
    setModelComputedProjections(null);
    try {
      let headers = [];
      headers = ["Template upload (.xlsx)", "Validated in backend"];
      setModelFile(file);
      setModelFileName(file.name);
      setModelHeaders(headers);
      setModelColMap(
        Object.fromEntries(
          ET_MODEL_COL_TARGETS.map((t, i) => [t.key, headers[i] ?? ""])
        )
      );
    } finally {
      setModelParseLoading(false);
    }
  };

  const resetModelBuilder = () => {
    if (modelInputRef.current) modelInputRef.current.value = "";
    setModelDragging(false);
    setModelFile(null);
    setModelFileName("");
    setModelHeaders([]);
    setModelColMap(Object.fromEntries(ET_MODEL_COL_TARGETS.map((t) => [t.key, ""])));
    setModelParseLoading(false);
    setModelCompany("");
    setModelTemplate("saas");
    setModelHorizon("5");
    setModelAssumptions(
      Object.fromEntries(etModelAssumptionSchema("saas").map((a) => [a.key, a.def]))
    );
    setModelNarrativeNotes("");
    setModelBuilt(false);
    setModelNarrativePlaceholder("");
    setModelComputedProjections(null);
    setModelManualOpen(false);
    setModelManualMetrics(emptyEtModelManualMetrics());
    setScTab("base");
    setScSliders(etScenarioSliderValuesForTemplate("saas"));
    setModelRunLoading(false);
  };

  const runFormulaEngine = () => {
    if (!modelFile) {
      window.alert("Upload a .xlsx template file first (or use manual entry).");
      return;
    }

    const horizon = Number(modelHorizon || 5);
    const sliders = scSliders.base || {};
    const assumptions = buildModelAssumptionsFromSliders(sliders, horizon);

    const payload = {
      companyName: (modelCompany || companyName || "").trim(),
      narrativeContext: modelNarrativeNotes || "",
      metrics: {
        // These are template "metrics" (not assumptions) in `model_builder_logic.py`.
        "Entry EV/EBITDA": sliders.entry_mult,
        "Exit EV/EBITDA": sliders.exit_mult,
        "Exit EBITDA Margin (%)": sliders.ebitda_exit,
      },
      assumptions,
    };

    setModelRunLoading(true);
    setModelBuilt(false);
    setModelNarrativePlaceholder("");
    setModelComputedProjections(null);
    const fd = new FormData();
    fd.append("file", modelFile);
    fd.append("payload", JSON.stringify(payload));

    api
      .post(`/model_builder/compute?narrative=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(({ data }) => {
        const proj = data?.computedProjections || null;
        setModelComputedProjections(proj);
        if (proj?.horizonYears) setModelHorizon(String(proj.horizonYears));
        setModelNarrativePlaceholder(data?.aiNarrative || "");
        setModelBuilt(true);
      })
      .catch((e) => {
        const msg = e.response?.data?.message || e.message || "Model Builder computation failed.";
        window.alert(msg);
      })
      .finally(() => setModelRunLoading(false));
  };

  const runFormulaEngineManual = () => {
    const horizon = Number(modelHorizon || 5);
    const sliders = scSliders.base || {};

    const ltmRevRaw = modelManualMetrics.ltmRevenue?.trim() ?? "";
    const arrRaw = modelManualMetrics.arr?.trim() ?? "";
    const revenue = ltmRevRaw !== "" ? Number(ltmRevRaw) : arrRaw !== "" ? Number(arrRaw) : NaN;
    const customers = Number(modelManualMetrics.totalCustomers || "");
    const ebitda = Number(modelManualMetrics.ltmEbitda || "");

    if (Number.isNaN(revenue) || revenue <= 0) {
      window.alert("Manual entry: LTM Revenue (or ARR) is required.");
      return;
    }
    if (Number.isNaN(customers) || customers <= 0) {
      window.alert("Manual entry: Total Customers is required.");
      return;
    }
    if (Number.isNaN(ebitda)) {
      window.alert("Manual entry: LTM EBITDA is required.");
      return;
    }

    const payload = {
      companyName: (modelCompany || companyName || "").trim(),
      narrativeContext: modelNarrativeNotes || "",
      metrics: {
        Revenue: revenue,
        "Total Customers": customers,
        EBITDA: ebitda,
        // These are template "metrics" (not assumptions) in `model_builder_logic.py`.
        "Entry EV/EBITDA": sliders.entry_mult,
        "Exit EV/EBITDA": sliders.exit_mult,
        "Exit EBITDA Margin (%)": sliders.ebitda_exit,
      },
      assumptions: buildModelAssumptionsFromSliders(sliders, horizon),
    };

    setModelRunLoading(true);
    setModelBuilt(false);
    setModelNarrativePlaceholder("");
    setModelComputedProjections(null);

    api
      .post(`/model_builder/compute?narrative=true`, payload)
      .then(({ data }) => {
        const proj = data?.computedProjections || null;
        setModelComputedProjections(proj);
        if (proj?.horizonYears) setModelHorizon(String(proj.horizonYears));
        setModelNarrativePlaceholder(data?.aiNarrative || "");
        setModelBuilt(true);
      })
      .catch((e) => {
        const msg = e.response?.data?.message || e.message || "Model Builder computation failed.";
        window.alert(msg);
      })
      .finally(() => setModelRunLoading(false));
  };

  const runFormulaEngineUnified = () => {
    if (modelHeaders.length > 0) {
      runFormulaEngine();
      return;
    }
    runFormulaEngineManual();
  };

  const copyModelNarrative = () => {
    const t = modelNarrativePlaceholder.trim();
    if (!t) return;
    navigator.clipboard.writeText(t).then(() => {
      setModelCopyFlash(true);
      window.setTimeout(() => setModelCopyFlash(false), 1200);
    });
  };

  const attachScenarioActuals = (scKey, fileList) => {
    const file = Array.from(fileList || [])[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx")) {
      window.alert("Please choose a .xlsx file (template format).");
      return;
    }
    setScActualsChip((c) => ({
      ...c,
      [scKey]: { name: file.name, kb: Math.max(1, Math.round(file.size / 1024)) },
    }));
    setScActualsFile((f) => ({ ...f, [scKey]: file }));
  };

  const runScenarioGen = async (key) => {
    const f = scActualsFile[key];
    if (!f) {
      window.alert("Upload financial data (.xlsx template) is required for this scenario.");
      return;
    }

    const caseKey = key === "base" ? "base" : key === "up" ? "upside" : "downside";
    const horizon = Number(modelHorizon || 5);
    const sliders = scSliders[key] || {};

    const assumptions = {
      "Revenue CAGR (%)": sliders.rev_cagr,
      "Annual Churn (%)": sliders.churn,
      "Forecast Horizon (Years)": horizon,
    };

    const payload = {
      caseKey,
      companyName: (modelCompany || companyName || "").trim(),
      narrativeContext: scNotes[key] || "",
      assumptions,
      metrics: {
        "Entry EV/EBITDA": sliders.entry_mult,
        "Exit EV/EBITDA": sliders.exit_mult,
        "Exit EBITDA Margin (%)": sliders.ebitda_exit,
      },
    };

    setScBusy((b) => ({ ...b, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("payload", JSON.stringify(payload));

      const { data } = await api.post(`/scenario_analysis/compute?narrative=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const proj = data?.computedProjections || null;
      setScComputed((c) => ({ ...c, [key]: proj }));
      if (proj && Array.isArray(proj.years)) {
        const lastIdx = proj.years.length - 1;
        const msg =
          `Horizon: ${proj.horizonYears} years\n` +
          `Revenue (Y0 → Y${proj.horizonYears}): ${Number(proj.revenue?.[0] ?? 0).toLocaleString()} → ${Number(proj.revenue?.[lastIdx] ?? 0).toLocaleString()}\n` +
          `Customers (Y0 → Y${proj.horizonYears}): ${Number(proj.totalCustomers?.[0] ?? 0).toLocaleString()} → ${Number(proj.totalCustomers?.[lastIdx] ?? 0).toLocaleString()}\n` +
          `Entry EV: ${Number(proj.entryEv ?? 0).toLocaleString()}\n` +
          `Exit EV: ${Number(proj.exitEv ?? 0).toLocaleString()}\n` +
          `Exit EBITDA: ${Number(proj.exitEbitda ?? 0).toLocaleString()}`;
        setScProjText((p) => ({ ...p, [key]: msg }));
      } else {
        setScProjText((p) => ({ ...p, [key]: "Computation complete." }));
      }

      setScNarrative((n) => ({ ...n, [key]: data?.aiNarrative || "" }));
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Scenario computation failed.";
      window.alert(msg);
    } finally {
      setScBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const scenarioHasProjections = (proj) =>
    proj && typeof proj === "object" && Array.isArray(proj.years) && proj.years.length > 0;

  const exportAllScenarios = () => {
    const sections = [];
    const appendCase = (heading, scKey) => {
      const proj = scComputed[scKey];
      if (!scenarioHasProjections(proj)) return;
      const projBlock =
        (scProjText[scKey] && String(scProjText[scKey]).trim()) || formatComputedProjectionsSummary(proj);
      const narrative = String(scNarrative[scKey] || "").trim();
      sections.push(
        `${heading}\n${"=".repeat(heading.length)}\n\nComputed Projections:\n${projBlock}\n\nAI Narrative:\n${narrative || "(none)"}`
      );
    };

    appendCase("BASE CASE", "base");
    appendCase("UPSIDE CASE", "up");
    appendCase("DOWNSIDE CASE", "dn");

    if (sections.length === 0) {
      window.alert("Export needs at least one scenario with computed projections. Run “Compute + Generate Narrative” for a case first.");
      return;
    }

    const company = (modelCompany || companyName || "").trim();
    const header = [
      "Scenario Analysis — combined text export",
      company ? `Company / Deal: ${company}` : null,
      `Generated (UTC): ${new Date().toISOString()}`,
    ]
      .filter(Boolean)
      .join("\n");

    const text = `${header}\n\n${sections.join("\n\n---\n\n")}\n`;

    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `scenario_analysis_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const ingestIntPackCim = async (fileList) => {
    const file = Array.from(fileList || [])[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".txt")) {
      window.alert("Upload must be a plain-text file (.txt only).");
      return;
    }
    if (file.size > INT_PACK_UPLOAD_MAX_BYTES) {
      window.alert(`File is larger than ${Math.round(INT_PACK_UPLOAD_MAX_BYTES / (1024 * 1024))} MB.`);
      return;
    }
    try {
      const text = await readFileAsUtf8Text(file);
      setIntPackCimText(text);
      setIntPackCimName(file.name);
    } catch {
      window.alert(`Could not read ${file.name}.`);
    }
  };

  const ingestIntPackModel = async (fileList) => {
    const file = Array.from(fileList || [])[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".txt")) {
      window.alert("Upload must be a plain-text file (.txt only).");
      return;
    }
    if (file.size > INT_PACK_UPLOAD_MAX_BYTES) {
      window.alert(`File is larger than ${Math.round(INT_PACK_UPLOAD_MAX_BYTES / (1024 * 1024))} MB.`);
      return;
    }
    try {
      const text = await readFileAsUtf8Text(file);
      setIntPackModelText(text);
      setIntPackModelName(file.name);
    } catch {
      window.alert(`Could not read ${file.name}.`);
    }
  };

  const buildGeneratedCimPlaintext = () => {
    const parts = CIM_ET_SECTIONS.map(({ key, label }) => {
      const body = (cimSections[key] || "").trim();
      if (!body) return "";
      return `${label.toUpperCase()}\n${"─".repeat(38)}\n\n${body}\n\n`;
    })
      .filter(Boolean)
      .join("");
    return parts.trim();
  };

  const buildGeneratedScenarioPackagePlaintext = () => {
    const sections = [];
    const appendCase = (heading, scKey) => {
      const proj = scComputed[scKey];
      if (!scenarioHasProjections(proj)) return;
      const projBlock =
        (scProjText[scKey] && String(scProjText[scKey]).trim()) || formatComputedProjectionsSummary(proj);
      const narrative = String(scNarrative[scKey] || "").trim();
      sections.push(
        `${heading}\n${"=".repeat(heading.length)}\n\nComputed Projections:\n${projBlock}\n\nAI Narrative:\n${narrative || "(none)"}`
      );
    };
    appendCase("BASE CASE", "base");
    appendCase("UPSIDE CASE", "up");
    appendCase("DOWNSIDE CASE", "dn");
    if (sections.length === 0) return "";
    const company = (modelCompany || companyName || "").trim();
    const header = [
      "Scenario Analysis — combined text export",
      company ? `Company / Deal: ${company}` : null,
      `Generated (UTC): ${new Date().toISOString()}`,
    ]
      .filter(Boolean)
      .join("\n");
    return `${header}\n\n${sections.join("\n\n---\n\n")}\n`;
  };

  const resolveExportPackageSources = () => {
    const uCim = intPackCimText.trim();
    const uModel = intPackModelText.trim();
    if (uCim || uModel) {
      if (!uCim || !uModel) {
        window.alert(
          "Upload both CIM and Model as plain-text (.txt) files, or remove both uploads to use generated CIM Draft and Scenario Analysis outputs instead."
        );
        return null;
      }
      return { cimText: intPackCimText, modelText: intPackModelText };
    }
    const cimText = buildGeneratedCimPlaintext();
    const modelText = buildGeneratedScenarioPackagePlaintext();
    if (!cimText) {
      window.alert(
        "No CIM content available. Generate a CIM Draft in this toolkit or upload a plain-text (.txt) CIM file."
      );
      return null;
    }
    if (!modelText) {
      window.alert(
        'No Scenario Analysis output available. Run "Compute + Generate Narrative" for at least one scenario, or upload a plain-text (.txt) Model file.'
      );
      return null;
    }
    return { cimText, modelText };
  };

  const exportFullPackage = async () => {
    const resolved = resolveExportPackageSources();
    if (!resolved) return;
    setExportFullBusy(true);
    try {
      const { data } = await api.post("/execution_toolkit/export_full_package", {
        cimText: resolved.cimText,
        modelText: resolved.modelText,
        companyName: (modelCompany || companyName || "").trim(),
      });
      const text = typeof data?.finalCimText === "string" ? data.finalCimText : "";
      if (!text.trim()) {
        window.alert(data?.message || "Export returned empty content.");
        return;
      }
      const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeCo = ((modelCompany || companyName || "deal").trim() || "deal").replace(/\s+/g, "-");
      a.download = `CIM-full-package-${safeCo}-${new Date().toISOString().slice(0, 10)}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Export Full Package failed.";
      window.alert(msg);
    } finally {
      setExportFullBusy(false);
    }
  };

  const clearCimForm = () => {
    setCompanyName("");
    setWebsiteUrl("");
    setProjectCode("");
    setTransactionType("");
    setTargetAudience("");
    setKnownFinancials("");
    setAnalystNotes("");
    setCimFinancialDocs([]);
    setCimAnalystDocs([]);
    setCimSections(Object.fromEntries(CIM_ET_SECTIONS.map(({ key }) => [key, ""])));
    setCimError("");
    setExtractedCompany(null);
    setSideView("cim-setup");
    setActiveSec("exec_summary");
  };

  const generateCim = async () => {
    const name = companyName.trim();
    const url = websiteUrl.trim();
    if (!name || !url) {
      setCimError("Company name and website URL are required.");
      return;
    }
    setCimError("");
    setCimGenLoading(true);
    setCimGenMsgIdx(0);
    try {
      const { data } = await api.post("/cim_draft", {
        companyName: name,
        websiteUrl: url,
        projectCode: projectCode.trim(),
        transactionType: transactionType.trim(),
        targetAudience: targetAudience.trim(),
        knownFinancials: knownFinancials.trim(),
        analystNotes: analystNotes.trim(),
        financialSupportingDocuments: cimFinancialDocs,
        analystSupportingDocuments: cimAnalystDocs,
      });
      const sec = data.sections && typeof data.sections === "object" ? data.sections : {};
      const next = {};
      for (const { key } of CIM_ET_SECTIONS) {
        next[key] = typeof sec[key] === "string" ? sec[key] : "";
      }
      setCimSections(next);
      setExtractedCompany({ name, url });
      setActiveSec("exec_summary");
      setSideView("cim-draft");
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Generation failed.";
      setCimError(msg);
    } finally {
      setCimGenLoading(false);
    }
  };

  const exportCimTxt = () => {
    setCimExportOpen(false);
    const parts = CIM_ET_SECTIONS.map(({ key, label }) => {
      const body = (cimSections[key] || "").trim();
      if (!body) return "";
      return `${label.toUpperCase()}\n${"─".repeat(38)}\n\n${body}\n\n`;
    })
      .filter(Boolean)
      .join("");
    if (!parts) {
      window.alert("No CIM content generated yet.");
      return;
    }
    const co = extractedCompany || { name: companyName.trim() || "Target Company", url: "" };
    const header = `CONFIDENTIAL INFORMATION MEMORANDUM\n${co.name}\nGenerated: ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} · FE International\n${"═".repeat(56)}\n\n`;
    const footer = `\n${"─".repeat(56)}\nDISCLAIMER: This content is AI-generated. Please review and validate all details before using for any official or external purposes.\n`;
    const blob = new Blob([header + parts + footer], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const safe = (co.name || "draft").replace(/\s+/g, "-");
    a.download = `CIM-${safe}-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copyActiveSection = () => {
    const t = (cimSections[activeSec] || "").trim();
    if (!t) return;
    navigator.clipboard.writeText(t).then(() => {
      setCopyFlash(true);
      setTimeout(() => setCopyFlash(false), 1400);
    });
  };

  const activeLabel = CIM_ET_SECTIONS.find((s) => s.key === activeSec)?.label || activeSec;
  const activeBody = (cimSections[activeSec] || "").trim();
  const hasAnySection = CIM_ET_SECTIONS.some(({ key }) => (cimSections[key] || "").trim());

  const exportCimPpt = async () => {
    const co = extractedCompany || { name: companyName.trim() || "Target Company", url: "" };
    const nm = co.name || "Target Company";
    if (!hasAnySection) {
      window.alert("No CIM content generated yet.");
      return;
    }
    setCimExportOpen(false);
    setCimExportPptBusy(true);
    try {
      const res = await api.post("/cim_export_ppt", { companyName: nm, sections: cimSections }, { responseType: "blob" });
      const ctype = (res.headers && res.headers["content-type"]) || "";
      if (ctype.includes("application/json")) {
        const text = await res.data.text();
        let msg = "Export failed.";
        try {
          const j = JSON.parse(text);
          if (j.message) msg = j.message;
        } catch {
          /* ignore */
        }
        window.alert(msg);
        return;
      }
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (nm || "draft").replace(/\s+/g, "-");
      a.download = `CIM-${safe}-${new Date().toISOString().slice(0, 10)}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "PowerPoint export failed.";
      window.alert(msg);
    } finally {
      setCimExportPptBusy(false);
    }
  };

  return (
    <div className="et-wrap" style={{ animation: "fadeUp 0.22s ease" }}>
      <aside className="et-sidebar">
        <div className="et-sb-brand">
          <div className="et-sb-logo">
            <span aria-hidden>⚡</span>
            Execution Toolkit
          </div>
          <div className="et-sb-sub">CIM · Model · Integration</div>
        </div>

        <div className="et-sb-section">CIM Drafting</div>
        <div className="et-nav" style={{ marginBottom: 4 }}>
          <button
            type="button"
            className={`et-nav-item${sideView === "cim-setup" ? " active" : ""}`}
            onClick={() => setSideView("cim-setup")}
          >
            <span className="et-nav-icon et-ni-cim" aria-hidden>
              📋
            </span>
            Setup &amp; Inputs
          </button>
          <button
            type="button"
            className={`et-nav-item${sideView === "cim-draft" ? " active" : ""}`}
            onClick={() => setSideView("cim-draft")}
          >
            <span className="et-nav-icon et-ni-cim" aria-hidden>
              ✍️
            </span>
            CIM Draft
          </button>
        </div>

        <div className="et-sb-section">Financial Model</div>
        <div className="et-nav" style={{ marginBottom: 4 }}>
          <button
            type="button"
            className={`et-nav-item${sideView === "model" ? " active" : ""}`}
            onClick={() => setSideView("model")}
          >
            <span className="et-nav-icon et-ni-model" aria-hidden>
              📊
            </span>
            Model Builder
          </button>
          <button
            type="button"
            className={`et-nav-item${sideView === "scenarios" ? " active" : ""}`}
            onClick={() => setSideView("scenarios")}
          >
            <span className="et-nav-icon et-ni-scenario" aria-hidden>
              📈
            </span>
            Scenario Analysis
          </button>
        </div>

        <div className="et-sb-section">Integration</div>
        <div className="et-nav">
          <button
            type="button"
            className={`et-nav-item${sideView === "integration" ? " active" : ""}`}
            onClick={() => setSideView("integration")}
          >
            <span className="et-nav-icon et-ni-sync" aria-hidden>
              🔗
            </span>
            Sync &amp; Export
          </button>
        </div>

        <div className="et-sb-foot" />
      </aside>

      <main className="et-main">
        <div className={`et-panel${sideView === "cim-setup" ? " active" : ""}`}>
          <div className="et-header cim">
            <h2>CIM Drafting Assistant</h2>
            <p>Enter company details — AI researches the web and drafts an institutional CIM from your inputs</p>
            <span className="et-badge cim">⬡ CIM MODULE · CREW-GENERATED</span>
          </div>

          <div className="et-grid">
            <div className="et-card">
              <div className="et-card-title">Company Identification</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <span className="et-label">
                    Company Name <span style={{ color: "#ef4444" }}>*</span>
                  </span>
                  <input
                    className="et-input"
                    type="text"
                    placeholder="e.g. Meridian SaaS Platform"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    autoComplete="organization"
                  />
                </div>
                <div>
                  <span className="et-label">Deal / Project Code</span>
                  <input
                    className="et-input"
                    type="text"
                    placeholder="e.g. PROJECT APEX"
                    value={projectCode}
                    onChange={(e) => setProjectCode(e.target.value)}
                  />
                </div>
                <div>
                  <span className="et-label">Transaction Type</span>
                  <select className="et-select" value={transactionType} onChange={(e) => setTransactionType(e.target.value)}>
                    <option value="">Select type…</option>
                    <option>Full Business Sale</option>
                    <option>Majority Stake</option>
                    <option>Minority Investment</option>
                    <option>Management Buyout</option>
                    <option>Recapitalisation</option>
                    <option>Asset Sale</option>
                  </select>
                </div>
                <div>
                  <span className="et-label">Target Audience</span>
                  <select className="et-select" value={targetAudience} onChange={(e) => setTargetAudience(e.target.value)}>
                    <option value="">Select audience…</option>
                    <option>Strategic Acquirers</option>
                    <option>Financial Sponsors / PE</option>
                    <option>Family Offices</option>
                    <option>HNW Individuals</option>
                    <option>Mixed / Broad Process</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="et-card">
              <div className="et-card-title teal">Business Profile</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <span className="et-label">
                    Company Website URL <span style={{ color: "#ef4444" }}>*</span>
                  </span>
                  <input
                    className="et-input"
                    type="url"
                    placeholder="https://example.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    style={{ paddingLeft: 11 }}
                  />
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8.5, color: "var(--text-dim)", marginTop: 3, letterSpacing: 0.2 }}>
                    The crew uses search tools to enrich context from this URL and public sources
                  </div>
                </div>
                <div>
                  <span className="et-label">
                    Known Financials{" "}
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, color: "var(--text-dim)", fontWeight: 400 }}>(optional)</span>
                  </span>
                  <textarea
                    className="et-textarea"
                    style={{ minHeight: 70 }}
                    placeholder="Optional: revenue, EBITDA, growth rate, margins."
                    value={knownFinancials}
                    onChange={(e) => setKnownFinancials(e.target.value)}
                  />
                  <CimSupplementaryUploads
                    idPrefix="et-cim-fin"
                    buttonLabel="Attach Financial Data"
                    extensionsHint=".xlsx · .xls · .csv · .pdf · .docx · .txt · .doc"
                    acceptAttr={CIM_FIN_UPLOAD_ACCEPT}
                    items={cimFinancialDocs}
                    onItemsChange={setCimFinancialDocs}
                  />
                </div>
                <div>
                  <span className="et-label">
                    Analyst Notes &amp; Additional Inputs{" "}
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, color: "var(--text-dim)", fontWeight: 400 }}>(optional)</span>
                  </span>
                  <textarea
                    className="et-textarea"
                    style={{ minHeight: 70 }}
                    placeholder="Deal nuances, emphasis areas, proprietary context…"
                    value={analystNotes}
                    onChange={(e) => setAnalystNotes(e.target.value)}
                  />
                  <CimSupplementaryUploads
                    idPrefix="et-cim-notes"
                    buttonLabel="Attach Document"
                    extensionsHint=".pdf · .docx · .pptx · .txt · .xlsx · .csv · .doc"
                    acceptAttr={CIM_NOTES_UPLOAD_ACCEPT}
                    items={cimAnalystDocs}
                    onItemsChange={setCimAnalystDocs}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="et-ai-info">
            <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }} aria-hidden>
              🤖
            </span>
            <div>
              <div className="et-ai-info-title">Crew workflow</div>
              <div style={{ fontSize: 12.5, color: "var(--text-sub)", lineHeight: 1.65 }}>
                A research agent gathers web-backed context, then a CIM author drafts thirteen sections — Exec. Summary through Contact — aligned to your inputs
                and any attached spreadsheets or documents (text is extracted server-side).
              </div>
            </div>
          </div>

          <div className={`et-loading${cimGenLoading ? " show" : ""}`} aria-live="polite">
            <div className="et-spin" aria-hidden />
            <span>{CIM_GEN_STATUS_MSGS[cimGenMsgIdx]}</span>
          </div>
          {cimError ? (
            <div className="deal-search-err" style={{ marginBottom: 12 }}>
              {cimError}
            </div>
          ) : null}
          <div className="et-gen-status">{cimGenLoading ? "This may take a few minutes." : ""}</div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button type="button" className="et-btn et-btn-ghost" onClick={clearCimForm} disabled={cimGenLoading}>
              ✕ Clear
            </button>
            <button type="button" className="et-btn et-btn-primary et-btn-lg" onClick={generateCim} disabled={cimGenLoading}>
              ⚡ Generate CIM Draft
            </button>
          </div>
        </div>

        <div className={`et-panel${sideView === "cim-draft" ? " active" : ""}`}>
          <div className="et-header cim">
            <h2>CIM Draft</h2>
            <p>AI-generated Confidential Information Memorandum — review, regenerate, or export</p>
            <span className="et-badge cim">⬡ DRAFT OUTPUT · SECTION-BY-SECTION</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <div className="et-sec-tabs">
              {CIM_ET_SECTIONS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className={`et-sec-tab${activeSec === key ? " active" : ""}`}
                  onClick={() => setActiveSec(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 7, flexShrink: 0, flexWrap: "wrap", alignItems: "center" }}>
              <button type="button" className="et-btn et-btn-ghost et-btn-sm" onClick={() => generateCim()} disabled={cimGenLoading}>
                ↺ Regenerate
              </button>
              <button type="button" className="et-btn et-btn-ghost et-btn-sm" onClick={() => setSideView("cim-setup")}>
                ← Edit Inputs
              </button>
              <div className="et-cim-export-wrap" ref={cimExportWrapRef}>
                <button
                  type="button"
                  className="et-btn et-btn-primary et-btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCimExportOpen((o) => !o);
                  }}
                  disabled={cimExportPptBusy}
                >
                  ⬇ Export <span style={{ opacity: 0.7, marginLeft: 2 }}>▾</span>
                </button>
                <div className={`et-cim-export-menu${cimExportOpen ? " open" : ""}`} role="menu" aria-hidden={!cimExportOpen}>
                  <button
                    type="button"
                    className="et-cim-export-row"
                    role="menuitem"
                    onClick={() => exportCimPpt()}
                    disabled={cimExportPptBusy}
                  >
                    <div className="et-cim-export-ico ppt" aria-hidden>
                      📊
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="et-cim-export-row-title">Export as PowerPoint</div>
                      <div className="et-cim-export-row-meta">Editable .pptx · one slide per section + title</div>
                    </div>
                    <span className="et-cim-export-tag rec">RECOMMENDED</span>
                  </button>
                  <button type="button" className="et-cim-export-row compact" role="menuitem" onClick={() => exportCimTxt()}>
                    <div className="et-cim-export-ico txt" aria-hidden>
                      📄
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="et-cim-export-row-title">Export as Text</div>
                      <div className="et-cim-export-row-meta">Plain .txt · all sections · copy-paste ready</div>
                    </div>
                  </button>
                  <button type="button" className="et-cim-export-row compact" disabled>
                    <div className="et-cim-export-ico pdf" aria-hidden>
                      📑
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="et-cim-export-row-title">Export as PDF</div>
                      <div className="et-cim-export-row-meta">Coming soon</div>
                    </div>
                    <span className="et-cim-export-tag soon">SOON</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="et-output" style={{ minHeight: 320, display: "block" }}>
            <div className="et-output-hdr">
              <span className="et-output-live">{activeLabel.toUpperCase()}</span>
              <button type="button" className="et-btn et-btn-ghost et-btn-sm" onClick={copyActiveSection} disabled={!activeBody}>
                {copyFlash ? "✓ Copied" : "⊞ Copy Section"}
              </button>
            </div>
            <div className="et-output-body" style={{ minHeight: 220 }}>
              {!hasAnySection ? (
                <span style={{ color: "var(--text-dim)", fontStyle: "italic", fontSize: 12.5 }}>
                  Complete Setup &amp; Inputs and click &quot;Generate CIM Draft&quot; to produce the memorandum. Sections will appear here.
                </span>
              ) : !activeBody ? (
                <span style={{ color: "var(--text-dim)", fontStyle: "italic", fontSize: 12.5 }}>This section is empty in the latest generation.</span>
              ) : (
                activeBody
              )}
            </div>
            <div className="et-disclaimer">
              <span style={{ fontSize: 14, flexShrink: 0 }} aria-hidden>
                ⚠️
              </span>
              <span className="et-disclaimer-text">
                <strong>DISCLAIMER:</strong> This content is AI-generated. Please review and validate all details before using for any official or external purposes.
              </span>
            </div>
          </div>
        </div>

        <div className={`et-panel${sideView === "model" ? " active" : ""}`}>
          <div className="et-header model">
            <h2>Financial Model Builder</h2>
            <p>Upload raw financials — deterministic formula engine computes all metrics — LLM writes narrative only</p>
            <span className="et-badge model">◈ DETERMINISTIC ENGINE · LLM NARRATIVE ONLY</span>
          </div>

          <div className="et-det-banner purple">
            <span style={{ fontSize: 17, flexShrink: 0, marginTop: 1 }} aria-hidden>
              🔒
            </span>
            <div>
              <div className="et-det-banner-title">Deterministic computation layer</div>
              <div style={{ fontSize: 12, color: "var(--text-sub)", lineHeight: 1.65 }}>
                All financial figures — revenue, EBITDA, margins, IRR, multiples — are computed by a{" "}
                <strong style={{ color: "var(--text-main)" }}>formula engine</strong>, not by the LLM. The AI only receives verified computed outputs and writes commentary.
              </div>
            </div>
          </div>

          <div className="et-grid">
            <div className="et-card" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <div className="et-card-title purple" style={{ marginBottom: 10 }}>
                  ① Upload financial data{" "}
                  <span style={{ font: "inherit", color: "inherit" }}>
                    FROM{" "}
                    <button
                      type="button"
                      onClick={downloadModelTemplate}
                      style={{
                        font: "inherit",
                        color: "inherit",
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      TEMPLATE
                    </button>
                  </span>
                </div>
                <input
                  ref={modelInputRef}
                  type="file"
                  accept={ET_MODEL_UPLOAD_ACCEPT}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    ingestModelFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <div
                  role="button"
                  tabIndex={0}
                  className={`et-dropzone${modelDragging ? " et-dz-drag" : ""}`}
                  onClick={() => modelInputRef.current?.click()}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" || ev.key === " ") {
                      ev.preventDefault();
                      modelInputRef.current?.click();
                    }
                  }}
                  onDragOver={(ev) => {
                    ev.preventDefault();
                    setModelDragging(true);
                  }}
                  onDragLeave={() => setModelDragging(false)}
                  onDrop={(ev) => {
                    ev.preventDefault();
                    setModelDragging(false);
                    ingestModelFiles(ev.dataTransfer?.files);
                  }}
                >
                  <div className="et-dz-icon" style={{ fontSize: 28 }} aria-hidden>
                    📊
                  </div>
                  <div className="et-dz-text">
                    <strong style={{ color: "var(--purple)" }}>Click to upload</strong> or drag &amp; drop
                  </div>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8.5, color: "var(--text-dim)", marginTop: 4 }}>
                    .xlsx files only. Must strictly follow the template.
                  </div>
                </div>
                {modelFileName ? (
                  <div style={{ marginTop: 8, fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "var(--purple)" }}>
                    Attached: {modelFileName}
                  </div>
                ) : null}
                <div className={`et-loading${modelParseLoading ? " show" : ""}`} style={{ marginTop: 8 }}>
                  <div className="et-spin purple" aria-hidden />
                  <span>Reading file…</span>
                </div>
              </div>

              <div style={{ textAlign: "center", paddingTop: 4 }}>
                <button
                  type="button"
                  className="et-manual-toggle"
                  onClick={() => {
                    setModelManualOpen((open) => {
                      return !open;
                    });
                  }}
                >
                  {modelManualOpen ? "↑ Hide manual entry" : "No file? Enter key metrics manually →"}
                </button>
              </div>

              <div
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  padding: 12,
                  background: "var(--off-white)",
                  marginTop: 10,
                }}
              >
                <div className="et-card-title purple" style={{ marginBottom: 10 }}>
                  Projection Assumptions
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {scenarioAssumptionRows.map((a) => (
                    <div key={a.id}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                        <span style={{ fontSize: 11.5, color: "var(--text-sub)", fontWeight: 500 }}>{a.label}</span>
                        <span
                          style={{
                            fontFamily: "'JetBrains Mono',monospace",
                            fontSize: 11,
                            color: "var(--purple)",
                            fontWeight: 600,
                          }}
                        >
                          {etScenarioFormatAssumptionValue(a.id, scSliders.base?.[a.id])}
                        </span>
                      </div>
                      <input
                        type="range"
                        min={a.min}
                        max={a.max}
                        step={a.step}
                        value={scSliders.base?.[a.id] ?? a.default}
                        onChange={(e) =>
                          setScSliders((prev) => ({
                            ...prev,
                            base: { ...prev.base, [a.id]: Number(e.target.value) },
                          }))
                        }
                        style={{ width: "100%", accentColor: "var(--purple)", cursor: "pointer" }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                <span className="et-label">Company / Deal</span>
                <input
                  className="et-input"
                  type="text"
                  placeholder="Auto-filled from CIM when available"
                  value={modelCompany}
                  onChange={(e) => setModelCompany(e.target.value)}
                />
              </div>
              <div style={{ marginTop: 8 }}>
                <span className="et-label">
                  Narrative Context{" "}
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, color: "var(--text-dim)", fontWeight: 400 }}>
                    (narrative guidance only)
                  </span>
                </span>
                <textarea
                  className="et-textarea"
                  style={{ minHeight: 55 }}
                  placeholder="Context for the LLM narrative — buyer type, key story…"
                  value={modelNarrativeNotes}
                  onChange={(e) => setModelNarrativeNotes(e.target.value)}
                />
              </div>
              {!modelManualOpen ? (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button
                    type="button"
                    className="et-btn et-btn-purple"
                    style={{ flex: 1 }}
                    onClick={runFormulaEngineUnified}
                    disabled={modelRunLoading}
                  >
                    ⚙ Run Formula Engine
                  </button>
                  <button type="button" className="et-btn et-btn-ghost" onClick={resetModelBuilder}>
                    ↺ Reset
                  </button>
                </div>
              ) : null}

              {modelManualOpen ? (
                <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                  <div className="et-card-title purple" style={{ marginBottom: 10 }}>
                    Manual Metric Entry
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--text-dim)", marginBottom: 10, lineHeight: 1.5 }}>
                    Enter verified figures directly. These will be treated as ground truth — no LLM computation.
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {ET_MODEL_MANUAL_FIELDS.map((f) => (
                      <div key={f.key}>
                        <span className="et-label" style={{ fontSize: 11 }}>
                          {f.label}
                        </span>
                        <input
                          className="et-input"
                          type="number"
                          inputMode="decimal"
                          placeholder={f.placeholder}
                          value={modelManualMetrics[f.key]}
                          onChange={(e) =>
                            setModelManualMetrics((m) => ({
                              ...m,
                              [f.key]: e.target.value,
                            }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <span className="et-label">Forecast Horizon</span>
                    <select className="et-select" value={modelHorizon} onChange={(e) => setModelHorizon(e.target.value)}>
                      <option value="3">3 years</option>
                      <option value="5">5 years</option>
                      <option value="7">7 years</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      className="et-btn et-btn-purple"
                      style={{ flex: 1 }}
                      onClick={runFormulaEngineUnified}
                      disabled={modelRunLoading}
                    >
                      ⚙ Run Formula Engine
                    </button>
                    <button type="button" className="et-btn et-btn-ghost" onClick={resetModelBuilder}>
                      ↺ Reset
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div className="et-card" style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
                <div>
                  <div className="et-card-title purple">📋 Computed Projections</div>
                  <div style={{ overflowX: "auto", minHeight: 80 }}>
                    {modelComputedProjections && Array.isArray(modelComputedProjections.years) ? (
                      <>
                        <div
                          style={{
                            fontSize: 12.5,
                            lineHeight: 1.65,
                            color: "var(--text-main)",
                            whiteSpace: "pre-wrap",
                            marginBottom: 10,
                          }}
                        >
                          {formatComputedProjectionsSummary(modelComputedProjections)}
                        </div>
                        <table className="et-model-proj-table">
                          <thead>
                            <tr>
                              <th>Metric</th>
                              {Array.from({ length: Number(modelHorizon) || 5 }, (_, i) => (
                                <th key={i}>Y{i + 1}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>Revenue</td>
                              {Array.from({ length: Number(modelHorizon) || 5 }, (_, i) => {
                                const p = modelComputedProjections;
                                const v = p?.revenue?.[i + 1];
                                const shown =
                                  v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
                                return <td key={i}>{shown}</td>;
                              })}
                            </tr>
                            <tr>
                              <td>Total Customers</td>
                              {Array.from({ length: Number(modelHorizon) || 5 }, (_, i) => {
                                const p = modelComputedProjections;
                                const v = p?.totalCustomers?.[i + 1];
                                const shown =
                                  v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
                                return <td key={i}>{shown}</td>;
                              })}
                            </tr>
                          </tbody>
                        </table>
                      </>
                    ) : (
                      <div style={{ color: "var(--text-dim)", fontStyle: "italic", fontSize: 12 }}>
                        Projections will appear here after computation.
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="et-card-title purple">AI Narrative</div>

                  <div className={`et-loading${modelRunLoading ? " show" : ""}`}>
                    <div className="et-spin purple" aria-hidden />
                    <span>Generating narrative…</span>
                  </div>

                  <div
                    style={{
                      minHeight: 150,
                      fontSize: 12.5,
                      lineHeight: 1.75,
                      color: modelNarrativePlaceholder ? "var(--text-main)" : "var(--text-dim)",
                      fontStyle: modelNarrativePlaceholder ? "normal" : "italic",
                    }}
                  >
                    {modelNarrativePlaceholder ||
                      "Run the formula engine to generate commentary from verified projections."}
                  </div>

                  <div
                    style={{
                      display: modelBuilt ? "flex" : "none",
                      marginTop: 10,
                      paddingTop: 10,
                      borderTop: "1px solid var(--border)",
                      gap: 7,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      className="et-btn et-btn-ghost et-btn-sm"
                      onClick={copyModelNarrative}
                      disabled={!modelNarrativePlaceholder}
                    >
                      {modelCopyFlash ? "✓ Copied" : "⊞ Copy"}
                    </button>
                  </div>

                  <div className="et-disclaimer" style={{ display: modelBuilt ? "flex" : "none", marginTop: 10 }}>
                    <span style={{ fontSize: 13, flexShrink: 0 }} aria-hidden>
                      ⚠️
                    </span>
                    <span className="et-disclaimer-text">
                      <strong>DISCLAIMER:</strong> Financial projections will be formula-computed from uploaded data. Narrative commentary is
                      AI-generated. Validate all outputs before external use.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`et-panel${sideView === "scenarios" ? " active" : ""}`}>
          <div className="et-header scenario">
            <h2>Scenario Analysis</h2>
            <p>Set assumptions via sliders — deterministic engine projects all numbers — LLM writes narrative around verified outputs</p>
            <span className="et-badge scenario">◈ FORMULA-DRIVEN PROJECTIONS · LLM NARRATIVE ONLY</span>
          </div>

          <div
            style={{
              background: "rgba(245,158,11,0.05)",
              border: "1px solid rgba(245,158,11,0.2)",
              borderRadius: "var(--radius)",
              padding: "10px 16px",
              marginBottom: 16,
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
            }}
          >
            <span style={{ fontSize: 16, flexShrink: 0 }} aria-hidden>
              🔒
            </span>
            <div style={{ fontSize: 12, color: "var(--text-sub)", lineHeight: 1.6 }}>
              Adjust assumption sliders → formula engine computes projections deterministically →{" "}
              <strong style={{ color: "var(--text-main)" }}>LLM receives only verified numbers</strong> and writes the scenario narrative. The LLM never calculates.{" "}
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "var(--text-dim)" }}>
                Scenario Analysis inputs are independent from Model Builder.
              </span>
            </div>
          </div>

          <div className="et-sc-tabs">
            <button type="button" className={`et-sc-tab${scTab === "base" ? " active base" : ""}`} onClick={() => setScTab("base")}>
              📊 Base Case
            </button>
            <button type="button" className={`et-sc-tab${scTab === "up" ? " active up" : ""}`} onClick={() => setScTab("up")}>
              📈 Upside Case
            </button>
            <button type="button" className={`et-sc-tab${scTab === "dn" ? " active dn" : ""}`} onClick={() => setScTab("dn")}>
              📉 Downside Case
            </button>
          </div>

          {[
            {
              sc: "base",
              leftTitle: "Base Case Assumptions",
              leftTitleStyle: undefined,
              accent: "var(--blue)",
              spinCls: "",
              uploadHint: ".xlsx · .csv — pre-fills base from actuals",
              taPh:
                "Strategic context for the narrative — e.g. 'Conservative management case reflecting existing pipeline only. No new market entry assumed.'",
              outIdle: "Run computation to generate base case narrative.",
              fileRef: scScenarioFileBaseRef,
            },
            {
              sc: "up",
              leftTitle: "Upside Case Assumptions",
              leftTitleStyle: { color: "#059669" },
              accent: "#059669",
              spinCls: " teal",
              uploadHint: ".xlsx · .csv",
              taPh:
                "Bull case rationale — e.g. 'International expansion drives accelerated growth. New enterprise tier launched in Year 2.'",
              outIdle: "Run computation to generate upside narrative.",
              fileRef: scScenarioFileUpRef,
            },
            {
              sc: "dn",
              leftTitle: "Downside Case Assumptions",
              leftTitleStyle: { color: "#b91c1c" },
              accent: "#b91c1c",
              spinCls: " amber",
              uploadHint: ".xlsx · .csv",
              taPh:
                "Bear case context — e.g. 'Stress test reflecting key customer churn and market headwinds. No new logo growth assumed.'",
              outIdle: "Run computation to generate downside narrative.",
              fileRef: scScenarioFileDnRef,
            },
          ].map((row) => (
            <div key={row.sc} style={{ display: scTab === row.sc ? "block" : "none" }}>
              <div className="et-grid">
                <div className="et-card">
                  <div className="et-card-title" style={row.leftTitleStyle}>
                    {row.leftTitle}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {scenarioAssumptionRows.map((a) => (
                      <div key={a.id}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                          <span style={{ fontSize: 11.5, color: "var(--text-sub)", fontWeight: 500 }}>{a.label}</span>
                          <span
                            style={{
                              fontFamily: "'JetBrains Mono',monospace",
                              fontSize: 11,
                              color: row.accent,
                              fontWeight: 600,
                            }}
                          >
                            {etScenarioFormatAssumptionValue(a.id, scSliders[row.sc]?.[a.id])}
                          </span>
                        </div>
                        <input
                          type="range"
                          min={a.min}
                          max={a.max}
                          step={a.step}
                          value={scSliders[row.sc]?.[a.id] ?? a.default}
                          onChange={(e) =>
                            setScSliders((prev) => ({
                              ...prev,
                              [row.sc]: { ...prev[row.sc], [a.id]: Number(e.target.value) },
                            }))
                          }
                          style={{ width: "100%", accentColor: row.accent, cursor: "pointer" }}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <span className="et-label">
                      Narrative Context{" "}
                      {row.sc === "base" ? (
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, color: "var(--text-dim)", fontWeight: 400 }}>
                          (optional — LLM narrative guidance)
                        </span>
                      ) : null}
                    </span>
                    <textarea
                      className="et-textarea"
                      style={{ minHeight: 70 }}
                      placeholder={row.taPh}
                      value={scNotes[row.sc]}
                      onChange={(e) => setScNotes((n) => ({ ...n, [row.sc]: e.target.value }))}
                    />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <span className="et-label">
                      Upload financial data (template){" "}
                      <span style={{ color: "var(--red)", fontWeight: 700 }}>*</span>
                    </span>
                    <input
                      ref={row.fileRef}
                      type="file"
                      accept={ET_SCENARIO_UPLOAD_ACCEPT}
                      style={{ display: "none" }}
                      onChange={(e) => {
                        attachScenarioActuals(row.sc, e.target.files);
                        e.target.value = "";
                      }}
                    />
                    <div
                      role="button"
                      tabIndex={0}
                      className={`et-dropzone${scUploadDragging[row.sc] ? " et-dz-drag" : ""}`}
                      style={{ padding: "14px 14px", marginTop: 6 }}
                      onClick={() => row.fileRef.current?.click()}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                          ev.preventDefault();
                          row.fileRef.current?.click();
                        }
                      }}
                      onDragOver={(ev) => {
                        ev.preventDefault();
                        setScUploadDragging((d) => ({ ...d, [row.sc]: true }));
                      }}
                      onDragLeave={() => setScUploadDragging((d) => ({ ...d, [row.sc]: false }))}
                      onDrop={(ev) => {
                        ev.preventDefault();
                        setScUploadDragging((d) => ({ ...d, [row.sc]: false }));
                        attachScenarioActuals(row.sc, ev.dataTransfer?.files);
                      }}
                    >
                      <div className="et-dz-icon" style={{ fontSize: 22 }} aria-hidden>
                        📊
                      </div>
                      <div className="et-dz-text">
                        <strong style={{ color: "var(--purple)" }}>Click to upload</strong> or drag &amp; drop
                      </div>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8.5, color: "var(--text-dim)", marginTop: 4 }}>
                        .xlsx files only. Must strictly follow the template.
                      </div>
                      <div style={{ marginTop: 6 }}>
                        <button
                          type="button"
                          className="et-btn et-btn-ghost et-btn-sm"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            downloadModelTemplate();
                          }}
                        >
                          ⬇ Download template
                        </button>
                      </div>
                    </div>
                    {scActualsChip[row.sc] ? (
                      <div style={{ marginTop: 8, fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "var(--purple)" }}>
                        Attached: {scActualsChip[row.sc].name} · {scActualsChip[row.sc].kb} KB
                      </div>
                    ) : (
                      <div style={{ marginTop: 8, fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "var(--text-dim)" }}>Required</div>
                    )}
                  </div>
                  {row.sc === "base" ? (
                    <button type="button" className="et-btn et-btn-primary" style={{ marginTop: 12, width: "100%" }} onClick={() => runScenarioGen("base")} disabled={scBusy.base}>
                      ⚡ Compute + Generate Narrative
                    </button>
                  ) : row.sc === "up" ? (
                    <button
                      type="button"
                      className="et-btn"
                      onClick={() => runScenarioGen("up")}
                      disabled={scBusy.up}
                      style={{
                        marginTop: 12,
                        width: "100%",
                        background: "linear-gradient(135deg,#10b981,#059669)",
                        color: "#fff",
                        border: "none",
                        fontFamily: "'Clash Display',sans-serif",
                        fontWeight: 700,
                        fontSize: 12,
                        padding: 10,
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      📈 Compute + Generate Narrative
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="et-btn"
                      onClick={() => runScenarioGen("dn")}
                      disabled={scBusy.dn}
                      style={{
                        marginTop: 12,
                        width: "100%",
                        background: "linear-gradient(135deg,#ef4444,#b91c1c)",
                        color: "#fff",
                        border: "none",
                        fontFamily: "'Clash Display',sans-serif",
                        fontWeight: 700,
                        fontSize: 12,
                        padding: 10,
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      📉 Compute + Generate Narrative
                    </button>
                  )}
                </div>
                <div className="et-card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <div className="et-card-title" style={row.leftTitleStyle}>
                      📋 Computed Projections
                    </div>
                    <div id={`et-sc-${row.sc}-proj`} style={{ overflowX: "auto", minHeight: 80 }}>
                      {scProjText[row.sc] ? (
                        <div style={{ fontSize: 12.5, lineHeight: 1.65, color: "var(--text-main)", whiteSpace: "pre-wrap" }}>{scProjText[row.sc]}</div>
                      ) : (
                        <div style={{ color: "var(--text-dim)", fontStyle: "italic", fontSize: 12 }}>Projections will appear here after computation.</div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="et-card-title" style={row.leftTitleStyle}>
                      AI Narrative
                    </div>
                    <div className={`et-loading${scBusy[row.sc] ? " show" : ""}`}>
                      <div className={`et-spin${row.spinCls}`} aria-hidden />
                      <span>Generating narrative…</span>
                    </div>
                    <div
                      style={{
                        minHeight: 150,
                        fontSize: 12.5,
                        color: scNarrative[row.sc] ? "var(--text-main)" : "var(--text-dim)",
                        fontStyle: scNarrative[row.sc] ? "normal" : "italic",
                      }}
                    >
                      {scNarrative[row.sc] || row.outIdle}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button type="button" className="et-btn et-btn-ghost et-btn-sm" onClick={exportAllScenarios}>
              ⬇ Download all scenarios (.txt)
            </button>
          </div>
        </div>

        <div className={`et-panel${sideView === "integration" ? " active" : ""}`}>
          <div className="et-sync-hero-wrap">
            <div className="et-header sync">
              <h2>Sync &amp; Export</h2>
              <p>
                Upload plain-text (.txt) sources or use generated outputs from <strong>CIM Draft</strong> and <strong>Scenario Analysis</strong> — export one
                integrated CIM package via the backend crew.
              </p>
              <span className="et-badge sync">🔗 INTEGRATION LAYER · SINGLE SOURCE OF TRUTH</span>
            </div>
          </div>

          <div className="et-grid">
            <div className="et-card">
              <div className="et-card-title teal">Upload CIM</div>
              <div style={{ fontSize: 12, color: "var(--text-sub)", lineHeight: 1.55, marginBottom: 8 }}>
                Plain text only — choose a <strong>.txt</strong> file (UTF-8). Other formats are not accepted here.
              </div>
              <input
                ref={intPackCimInputRef}
                type="file"
                accept=".txt,text/plain"
                style={{ display: "none" }}
                onChange={async (e) => {
                  await ingestIntPackCim(e.target.files);
                  e.target.value = "";
                }}
              />
              <div
                role="button"
                tabIndex={0}
                className={`et-dropzone${intPackDzCim ? " et-dz-drag" : ""}`}
                onClick={() => intPackCimInputRef.current?.click()}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    intPackCimInputRef.current?.click();
                  }
                }}
                onDragOver={(ev) => {
                  ev.preventDefault();
                  setIntPackDzCim(true);
                }}
                onDragLeave={() => setIntPackDzCim(false)}
                onDrop={async (ev) => {
                  ev.preventDefault();
                  setIntPackDzCim(false);
                  await ingestIntPackCim(ev.dataTransfer?.files);
                }}
              >
                <div className="et-dz-icon" style={{ fontSize: 26 }} aria-hidden>
                  📄
                </div>
                <div className="et-dz-text">
                  <strong style={{ color: "var(--teal)" }}>Upload CIM</strong> — .txt only
                </div>
              </div>
              {intPackCimName ? (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "var(--teal)" }}>{intPackCimName}</span>
                  <button
                    type="button"
                    className="et-btn et-btn-ghost et-btn-sm"
                    onClick={() => {
                      setIntPackCimName("");
                      setIntPackCimText("");
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : null}
            </div>

            <div className="et-card">
              <div className="et-card-title teal">Upload Model</div>
              <div style={{ fontSize: 12, color: "var(--text-sub)", lineHeight: 1.55, marginBottom: 8 }}>
                Plain text only — choose a <strong>.txt</strong> file (UTF-8), for example an exported scenario summary.
              </div>
              <input
                ref={intPackModelInputRef}
                type="file"
                accept=".txt,text/plain"
                style={{ display: "none" }}
                onChange={async (e) => {
                  await ingestIntPackModel(e.target.files);
                  e.target.value = "";
                }}
              />
              <div
                role="button"
                tabIndex={0}
                className={`et-dropzone${intPackDzModel ? " et-dz-drag" : ""}`}
                onClick={() => intPackModelInputRef.current?.click()}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    intPackModelInputRef.current?.click();
                  }
                }}
                onDragOver={(ev) => {
                  ev.preventDefault();
                  setIntPackDzModel(true);
                }}
                onDragLeave={() => setIntPackDzModel(false)}
                onDrop={async (ev) => {
                  ev.preventDefault();
                  setIntPackDzModel(false);
                  await ingestIntPackModel(ev.dataTransfer?.files);
                }}
              >
                <div className="et-dz-icon" style={{ fontSize: 26 }} aria-hidden>
                  📑
                </div>
                <div className="et-dz-text">
                  <strong style={{ color: "var(--teal)" }}>Upload Model</strong> — .txt only
                </div>
              </div>
              {intPackModelName ? (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "var(--teal)" }}>{intPackModelName}</span>
                  <button
                    type="button"
                    className="et-btn et-btn-ghost et-btn-sm"
                    onClick={() => {
                      setIntPackModelName("");
                      setIntPackModelText("");
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div
            className="et-det-banner"
            style={{ marginTop: 14, background: "rgba(47,169,254,0.06)", border: "1px solid rgba(47,169,254,0.22)" }}
          >
            <span style={{ fontSize: 16, flexShrink: 0 }} aria-hidden>
              ℹ️
            </span>
            <div style={{ fontSize: 12.5, color: "var(--text-sub)", lineHeight: 1.65 }}>
              If both uploads are provided, those <strong>.txt</strong> files are sent to the server. Otherwise the toolkit uses your latest{" "}
              <strong>CIM Draft</strong> sections and combined <strong>Scenario Analysis</strong> outputs (at least one computed scenario). If neither path has both
              sides, export stops with an error.
            </div>
          </div>

          <div className="et-card" style={{ marginTop: 16 }}>
            <div className="et-card-title">Export Package</div>
            <p style={{ fontSize: 12.5, color: "var(--text-sub)", lineHeight: 1.65, margin: "0 0 12px" }}>
              Sends CIM and Model text to the backend; a crew produces one final CIM and downloads it as a <strong>.txt</strong> file.
            </p>
            <button
              type="button"
              className="et-btn et-btn-primary et-btn-lg"
              onClick={exportFullPackage}
              disabled={exportFullBusy}
              style={{ width: "100%", maxWidth: 360 }}
            >
              {exportFullBusy ? "Generating…" : "⬇ Export Full Package"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════

export default function FEDealIntel() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() =>
    location.pathname === "/" || location.pathname === "/partner_dashboard" ? "home" : "deals"
  );
  const [maNews, setMaNews] = useState([]);
  const [maNewsLoading, setMaNewsLoading] = useState(false);
  const [maNewsError, setMaNewsError] = useState("");
  const [newsPage, setNewsPage] = useState(1);
  const [newsIndustry, setNewsIndustry] = useState("All Industries");
  const [filters, setFilters] = useState({ search: "", industry: "", sub: "", year: "", geo: "", priceMin: "", priceMax: "", revMin: "", revMax: "", evMin: "", evMax: "" });
  // Column-header Excel-style filters: { [colKey: string]: string[] }. An
  // entry with an empty array is normalised away so `Object.keys(...).length`
  // reflects only ACTIVE column filters.
  const [columnFilters, setColumnFilters] = useState({});
  const [openColFilter, setOpenColFilter] = useState(null);
  const [colFilterSearch, setColFilterSearch] = useState("");
  const [colFilterPos, setColFilterPos] = useState({ top: 0, left: 0, width: 260 });
  const colFilterPopRef = useRef(null);
  const colFilterBtnsRef = useRef({});
  const [sortKey, setSortKey] = useState("year");
  const [sortDir, setSortDir] = useState(-1);
  const [drawerDeal, setDrawerDeal] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);

  // AI Search
  const [aiSearchOn, setAiSearchOn] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchLoadMsg, setSearchLoadMsg] = useState("");
  const [dealSearchError, setDealSearchError] = useState("");
  /** Rows from last banner deal_search; null = show normal filtered/sorted list */
  const [intelSearchDeals, setIntelSearchDeals] = useState(null);

  // Clause Library
  const [curCat, setCurCat] = useState("fees");
  const [clauseSearch, setClauseSearch] = useState("");
  const [openClauses, setOpenClauses] = useState({});

  // Origination Toolkit
  const [curTool, setCurTool] = useState("briefing");
  const [genLoading, setGenLoading] = useState(false);
  const [genOutput, setGenOutput] = useState("");
  const [genLoadMsg, setGenLoadMsg] = useState("");
  const [origForms, setOrigForms] = useState(() =>
    JSON.parse(JSON.stringify(EMPTY_ORIG_FORMS))
  );
  const origFormsRef = useRef(origForms);
  origFormsRef.current = origForms;

  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [dealsError, setDealsError] = useState("");

  const [addDealOpen, setAddDealOpen] = useState(false);
  const [addDealForm, setAddDealForm] = useState(() => ({ ...EMPTY_ADD_DEAL_FORM }));
  const [addDealBusy, setAddDealBusy] = useState(false);
  const [addDealMsg, setAddDealMsg] = useState({ type: "", text: "" });
  const [exportBusy, setExportBusy] = useState(false);

  const requestProtectedTab = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const fetchDeals = useCallback(async () => {
    setDealsLoading(true);
    setDealsError("");
    try {
      const { data } = await api.post("/fetch_deals", {});
      const list = Array.isArray(data) ? data.map(normalizeIntelDeal) : [];
      setDeals(list);
    } catch (e) {
      setDealsError(e.response?.data?.message || e.message || "Could not load deals.");
      setDeals([]);
    } finally {
      setDealsLoading(false);
    }
  }, []);

  // Inject styles
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = GLOBAL_STYLES;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const filterIndustries = useMemo(() => {
    const seen = new Set();
    for (const d of deals) {
      const v = d.industry;
      if (v == null) continue;
      const s = String(v).trim();
      if (s) seen.add(s);
    }
    return [...seen].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [deals]);

  // Sub-industries cascade off the currently selected industry. When no
  // industry is picked we expose every distinct sub value in the dataset.
  const filterSubs = useMemo(() => {
    const seen = new Set();
    const selectedInd = filters.industry;
    for (const d of deals) {
      if (selectedInd && d.industry !== selectedInd) continue;
      const v = d.sub;
      if (v == null) continue;
      const s = String(v).trim();
      if (s) seen.add(s);
    }
    return [...seen].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [deals, filters.industry]);

  const filterYears = useMemo(() => {
    const seen = new Set();
    for (const d of deals) {
      const y = d.year;
      if (y == null || y === "") continue;
      const n = Number(y);
      if (!Number.isFinite(n)) continue;
      seen.add(n);
    }
    return [...seen].sort((a, b) => b - a).map(String);
  }, [deals]);

  const filterGeos = useMemo(() => {
    const seen = new Set();
    for (const d of deals) {
      const v = d.geo;
      if (v == null) continue;
      const s = String(v).trim();
      if (s) seen.add(s);
    }
    return [...seen].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [deals]);

  useEffect(() => {
    setFilters((f) => ({
      ...f,
      industry: f.industry && !filterIndustries.includes(f.industry) ? "" : f.industry,
      sub: f.sub && !filterSubs.includes(f.sub) ? "" : f.sub,
      year: f.year && !filterYears.includes(f.year) ? "" : f.year,
      geo: f.geo && !filterGeos.includes(f.geo) ? "" : f.geo,
    }));
  }, [filterIndustries, filterSubs, filterYears, filterGeos]);

  // ── Filtered & sorted deals ──
  const filtered = useMemo(() => {
    return deals.filter((d) => {
      const q = filters.search.toLowerCase();
      const nameLc = String(d.name || "").toLowerCase();
      const domainLc = String(d.domain || "").toLowerCase();
      if (q && !nameLc.includes(q) && !domainLc.includes(q)) return false;
      if (filters.industry && d.industry !== filters.industry) return false;
      if (filters.sub && d.sub !== filters.sub) return false;
      if (filters.year && String(d.year) !== filters.year) return false;
      if (filters.geo && !String(d.geo || "").includes(filters.geo.split(" ")[0])) return false;
      const pMin = parseFloat(filters.priceMin) || 0;
      const pMax = parseFloat(filters.priceMax) || Infinity;
      if (d.sale && (d.sale < pMin || d.sale > pMax)) return false;
      const rMin = parseFloat(filters.revMin) || 0;
      const rMax = parseFloat(filters.revMax) || Infinity;
      if (d.rev && (d.rev < rMin || d.rev > rMax)) return false;
      const eMin = parseFloat(filters.evMin) || 0;
      const eMax = parseFloat(filters.evMax) || Infinity;
      if (d.ev_eb && d.ev_eb > 0 && (d.ev_eb < eMin || d.ev_eb > eMax)) return false;
      return true;
    });
  }, [filters, deals]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => ((a[sortKey] || 0) - (b[sortKey] || 0)) * sortDir);
  }, [filtered, sortKey, sortDir]);

  // Apply column-header filters on top of the sidebar/AI-search pipeline. The
  // same `displayBase` is reused by the stats strip so the two stay in sync.
  const displayBase = useMemo(() => {
    const source = intelSearchDeals != null ? intelSearchDeals : filtered;
    return applyDealColumnFilters(source, columnFilters);
  }, [intelSearchDeals, filtered, columnFilters]);

  const displaySorted = useMemo(
    () => [...displayBase].sort((a, b) => ((a[sortKey] || 0) - (b[sortKey] || 0)) * sortDir),
    [displayBase, sortKey, sortDir]
  );

  // Stats (match whatever rows are shown in the table)
  const stats = useMemo(() => {
    const base = displayBase;
    // Include all positive EV/EBITDA values (table shows them even when ≥50x; old <50 cap hid median)
    const evs = base.map((d) => finitePositive(d.ev_eb)).filter((n) => n != null);
    const revs = base.map((d) => finitePositive(d.ev_rev)).filter((n) => n != null);
    const prices = base.filter((d) => d.sale && d.sale > 0).map((d) => d.sale);
    const margins = base.filter((d) => d.rev && d.rev > 0 && d.ebitda && d.ebitda > 0).map((d) => (d.ebitda / d.rev) * 100);
    return {
      medEvEbitda: evs.length ? med(evs).toFixed(2) + "x" : "—",
      medEvRev: revs.length ? med(revs).toFixed(2) + "x" : "—",
      avgPrice: prices.length ? fmt(avg(prices)) : "—",
      avgMargin: margins.length ? Math.round(avg(margins)) + "%" : "—",
    };
  }, [displayBase]);

  // Options for the currently-open column filter popover. Compute against the
  // dataset with EVERY OTHER column filter applied so that selections cascade
  // (Excel-style refinement) while the open column shows its full domain.
  const colFilterOptions = useMemo(() => {
    if (!openColFilter) return [];
    const cfg = DEAL_COLUMN_FILTER_CONFIG[openColFilter];
    if (!cfg) return [];
    const otherFilters = { ...columnFilters };
    delete otherFilters[openColFilter];
    const source = intelSearchDeals != null ? intelSearchDeals : filtered;
    const base = applyDealColumnFilters(source, otherFilters);
    const counts = new Map();
    for (const d of base) {
      const v = cfg.value(d);
      if (v == null) continue;
      counts.set(v, (counts.get(v) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) =>
        String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true, sensitivity: "base" })
      )
      .map(([value, count]) => ({ value, count }));
  }, [openColFilter, columnFilters, intelSearchDeals, filtered]);

  const visibleColFilterOptions = useMemo(() => {
    const q = colFilterSearch.trim().toLowerCase();
    if (!q) return colFilterOptions;
    return colFilterOptions.filter((o) => String(o.value).toLowerCase().includes(q));
  }, [colFilterOptions, colFilterSearch]);

  const totalActiveColumnFilters = useMemo(
    () => Object.values(columnFilters).reduce((n, v) => n + (Array.isArray(v) && v.length ? 1 : 0), 0),
    [columnFilters]
  );

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => d * -1);
    else { setSortKey(key); setSortDir(-1); }
  };

  const resetFilters = useCallback(() => {
    setFilters({ search: "", industry: "", sub: "", year: "", geo: "", priceMin: "", priceMax: "", revMin: "", revMax: "", evMin: "", evMax: "" });
    setColumnFilters({});
    setOpenColFilter(null);
    setColFilterSearch("");
  }, []);

  const updateFilter = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  // ── Column-header filter handlers ──
  const POPOVER_WIDTH = 260;
  const openColumnFilter = useCallback((colKey) => {
    const btn = colFilterBtnsRef.current[colKey];
    const cfg = DEAL_COLUMN_FILTER_CONFIG[colKey];
    if (!btn || !cfg) return;
    const r = btn.getBoundingClientRect();
    const align = cfg.popAlign === "right" ? "right" : "left";
    const left =
      align === "right"
        ? Math.max(8, Math.min(r.right - POPOVER_WIDTH, window.innerWidth - POPOVER_WIDTH - 8))
        : Math.max(8, Math.min(r.left, window.innerWidth - POPOVER_WIDTH - 8));
    setColFilterPos({ top: r.bottom + 6, left, width: POPOVER_WIDTH });
    setColFilterSearch("");
    setOpenColFilter(colKey);
  }, []);

  const toggleColumnFilter = useCallback(
    (colKey) => {
      if (openColFilter === colKey) setOpenColFilter(null);
      else openColumnFilter(colKey);
    },
    [openColFilter, openColumnFilter]
  );

  const toggleColumnFilterValue = useCallback((colKey, value) => {
    setColumnFilters((cf) => {
      const next = { ...cf };
      const cur = new Set(next[colKey] || []);
      if (cur.has(value)) cur.delete(value);
      else cur.add(value);
      if (cur.size === 0) delete next[colKey];
      else next[colKey] = [...cur];
      return next;
    });
  }, []);

  const clearColumnFilter = useCallback((colKey) => {
    setColumnFilters((cf) => {
      if (!cf[colKey]) return cf;
      const next = { ...cf };
      delete next[colKey];
      return next;
    });
  }, []);

  const selectAllColumnValues = useCallback((colKey, values) => {
    setColumnFilters((cf) => {
      if (!values || values.length === 0) {
        const next = { ...cf };
        delete next[colKey];
        return next;
      }
      return { ...cf, [colKey]: values };
    });
  }, []);

  // Close popover on outside click, Escape, scroll, or resize.
  useEffect(() => {
    if (!openColFilter) return undefined;
    const onDocMouseDown = (e) => {
      const pop = colFilterPopRef.current;
      const btn = colFilterBtnsRef.current[openColFilter];
      if (pop && pop.contains(e.target)) return;
      if (btn && btn.contains(e.target)) return;
      setOpenColFilter(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenColFilter(null);
    };
    const onScroll = (e) => {
      // Allow scrolling inside the popover (e.g., paging the option list)
      // without dismissing it. Any other scroll closes the popover.
      const pop = colFilterPopRef.current;
      if (e && e.target && pop && pop.contains(e.target)) return;
      setOpenColFilter(null);
    };
    const onResize = () => setOpenColFilter(null);
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [openColFilter]);

  const resetIntelligenceState = useCallback(() => {
    resetFilters();
    setSearchQuery("");
    setAiSearchOn(false);
    setSearchLoading(false);
    setSearchLoadMsg("");
    setIntelSearchDeals(null);
    setDealSearchError("");
    setDrawerDeal(null);
    setAiSummary(null);
    setAiSummaryLoading(false);
  }, [resetFilters]);

  const fetchMaNews = useCallback(async () => {
    setMaNewsLoading(true);
    setMaNewsError("");
    try {
      const { data } = await api.get("/fetch_ma_news");
      const list = Array.isArray(data) ? data : [];
      setMaNews(list);
      setNewsPage(1);
    } catch (e) {
      setMaNewsError(e.response?.data?.message || e.message || "Could not load M&A news.");
      setMaNews([]);
    } finally {
      setMaNewsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "home") return;
    fetchMaNews();
  }, [activeTab, fetchMaNews]);

  useEffect(() => {
    if (activeTab !== "deals") return;
    fetchDeals();
  }, [activeTab, fetchDeals]);

  useEffect(() => {
    if (activeTab !== "origination") return;
    const el = document.getElementById("origination-toolkit-form");
    if (!el) return;
    const frame = window.requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeTab]);

  const prevTabRef = useRef(activeTab);
  const resetDealsOnReturnRef = useRef(false);
  useEffect(() => {
    const prev = prevTabRef.current;
    if (prev === "deals" && activeTab !== "deals") resetDealsOnReturnRef.current = true;
    if (activeTab === "deals" && resetDealsOnReturnRef.current) {
      resetIntelligenceState();
      resetDealsOnReturnRef.current = false;
    }
    prevTabRef.current = activeTab;
  }, [activeTab, resetIntelligenceState]);

  // ── Drawer / AI Summary ──
  const openDrawer = useCallback(async (id) => {
    const d = deals.find((x) => x.id === id);
    if (!d) return;
    setDrawerDeal(d);
    setAiSummary(null);
    setAiSummaryLoading(true);
    try {
      const { data } = await api.post("/deal-summary", { deal: d });
      setAiSummary(data.summary || "Summary unavailable.");
    } catch {
      const fallback = `${d.name || d.domain || "This business"} operates in the ${d.industry || "digital"} sector${d.geo ? " based in " + d.geo : ""}, acquired in ${d.year || "an unspecified year"} at ${fmt(d.sale)}.`;
      setAiSummary(fallback);
    } finally {
      setAiSummaryLoading(false);
    }
  }, [deals]);

  const closeDrawer = () => setDrawerDeal(null);

  const clearIntelSearch = useCallback(() => {
    setSearchQuery("");
    setIntelSearchDeals(null);
    setDealSearchError("");
    fetchDeals();
  }, [fetchDeals]);

  const handleExportDeals = useCallback(async () => {
    if (dealsLoading || displaySorted.length === 0) return;
    setExportBusy(true);
    try {
      const body = { deals: displaySorted.map((d) => ({ ...d })) };
      const response = await api.post("/export_deals", body, { responseType: "blob" });
      const blob = response.data;
      const dispo = response.headers["content-disposition"] || response.headers["Content-Disposition"] || "";
      let filename = "fei_closed_deals_export.csv";
      const m = dispo.match(/filename="([^"]+)"/i) || dispo.match(/filename=([^;\s]+)/i);
      if (m) filename = String(m[1]).replace(/"/g, "");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = err.message || "Export failed.";
      if (err.response?.data instanceof Blob) {
        try {
          const t = await err.response.data.text();
          const j = JSON.parse(t);
          if (j?.message) msg = j.message;
        } catch {
          /* ignore */
        }
      } else if (err.response?.data?.message) {
        msg = err.response.data.message;
      }
      window.alert(msg);
    } finally {
      setExportBusy(false);
    }
  }, [dealsLoading, displaySorted]);

  // ── AI Intelligence Query ──
  const runIntelQuery = useCallback(async (q) => {
    const query = (q ?? searchQuery).trim();
    if (!query) return;
    setSearchLoading(true);
    setDealSearchError("");
    setSearchLoadMsg("Scanning…");

    try {
      const { data } = await api.post("/deal_search", {
        deals_data: sorted,
        prompt: query,
        ai_search: aiSearchOn,
      });
      setSearchLoading(false);
      if (!data || typeof data !== "object") {
        setIntelSearchDeals(null);
        setDealSearchError("Empty or invalid response from deal search.");
        return;
      }
      const deals = Array.isArray(data.deals) ? data.deals.map(normalizeIntelDeal) : [];
      setIntelSearchDeals(deals);
    } catch (e) {
      setSearchLoading(false);
      setDealSearchError(e.response?.data?.summary || e.message || "Connection error.");
    }
  }, [searchQuery, sorted, aiSearchOn]);

  // ── Origination Toolkit → /api/origination_toolkit ──
  const updateOrigForm = useCallback((tool, key, val) => {
    setOrigForms((o) => {
      const prevTool = o[tool] ?? {};
      const prevVal = prevTool[key];
      const resolved = typeof val === "function" ? val(prevVal) : val;
      return { ...o, [tool]: { ...prevTool, [key]: resolved } };
    });
  }, []);

  const submitOrigination = useCallback(
    async (opts) => {
      const vriStepArg = opts?.vriStep;
      const msgs = [
        "Drafting with FE Intel AI…"
      ];
      setGenLoading(true);
      if (curTool !== "vri") setGenOutput("");
      let mi = 0;
      setGenLoadMsg(msgs[0]);
      const intv = setInterval(() => {
        mi = (mi + 1) % msgs.length;
        setGenLoadMsg(msgs[mi]);
      }, 1800);

      try {
        if (curTool === "vri") {
          const vSnap = { ...origFormsRef.current.vri };
          setOrigForms((o) => ({ ...o, vri: { ...o.vri, stepError: "" } }));

          if (vriStepArg === "batch") {
            const urls = (vSnap.batchUrls || "")
              .split("\n")
              .map((u) => u.trim())
              .filter((u) => u.length > 3);
            if (!urls.length) {
              setOrigForms((o) => ({
                ...o,
                vri: { ...o.vri, stepError: "Please enter at least one website URL." },
              }));
              clearInterval(intv);
              setGenLoading(false);
              return;
            }
            const batch_urls_block = urls.map((u, i) => `${i + 1}. ${u}`).join("\n");
            const { data } = await api.post("/origination_toolkit", {
              origination_type: "vri_batch",
              form: { batch_urls_block },
            });
            const text = data.text || data.message || "Generation failed.";
            setOrigForms((o) => ({
              ...o,
              vri: { ...o.vri, batchRawOutput: text, step: "batch", stepError: "" },
            }));
          } else if (vriStepArg === "ingest") {
            const url = String(vSnap.url || "").trim();
            if (url.length < 4) {
              setOrigForms((o) => ({
                ...o,
                vri: { ...o.vri, stepError: "Please enter a company website URL." },
              }));
              clearInterval(intv);
              setGenLoading(false);
              return;
            }
            const { data } = await api.post("/origination_toolkit", {
              origination_type: "vri_ingest",
              form: {
                url: vSnap.url,
                ind: vSnap.ind,
                rev: vSnap.rev,
                ebitda: vSnap.ebitda,
                growth: vSnap.growth,
                ctx: vSnap.ctx,
                supporting_documents: Array.isArray(vSnap.supporting_documents)
                  ? vSnap.supporting_documents
                  : [],
              },
            });
            const text = data.text || data.message || "Generation failed.";
            const parsed = parseVriIngestOutput(text);
            if (!parsed.ok) {
              setOrigForms((o) => ({
                ...o,
                vri: {
                  ...o.vri,
                  stepError:
                    "Analysis returned unexpected formatting (missing <<<VRI_BENCHMARK>>>, <<<VRI_INTENT>>>, or <<<VRI_RESPONSE>>> markers). Try again or check backend logs.",
                },
              }));
            } else {
              setOrigForms((o) => ({
                ...o,
                vri: {
                  ...o.vri,
                  step1Output: "",
                  step2Output: parsed.benchmark,
                  step3Output: parsed.intent,
                  responseDraft: parsed.response,
                  step: 2,
                  stepError: "",
                },
              }));
            }
          }
        } else {
          const { data } = await api.post("/origination_toolkit", {
            origination_type: curTool,
            form: { ...origFormsRef.current[curTool] },
          });
          setGenOutput(data.text || data.message || "Generation failed.");
        }
      } catch (e) {
        const err = e.response?.data?.message || e.response?.data?.text || e.message || "Error connecting to AI.";
        if (curTool === "vri") {
          setOrigForms((o) => ({ ...o, vri: { ...o.vri, stepError: err } }));
        } else {
          setGenOutput(err);
        }
      } finally {
        clearInterval(intv);
        setGenLoading(false);
      }
    },
    [curTool]
  );

  const updateAddDealForm = (key, val) => setAddDealForm((f) => ({ ...f, [key]: val }));

  const openAddDealModal = useCallback(() => {
    setAddDealForm({ ...EMPTY_ADD_DEAL_FORM });
    setAddDealMsg({ type: "", text: "" });
    setAddDealOpen(true);
  }, []);

  const closeAddDealModal = useCallback(() => {
    setAddDealOpen(false);
    setAddDealMsg({ type: "", text: "" });
  }, []);

  const submitAddDeal = useCallback(
    async (e) => {
      e?.preventDefault();
      setAddDealMsg({ type: "", text: "" });
      const did = String(addDealForm.deal_id).trim();
      if (!did || !/^-?\d+$/.test(did)) {
        setAddDealMsg({ type: "err", text: "Deal ID is required (integer)." });
        return;
      }
      const intOrNull = (s) => {
        const t = String(s).trim();
        if (!t) return null;
        const n = parseInt(t, 10);
        return Number.isFinite(n) ? n : null;
      };
      const numOrNull = (s) => {
        const t = String(s).trim();
        if (!t) return null;
        const n = Number(t);
        return Number.isFinite(n) ? n : null;
      };
      const body = {
        deal_id: parseInt(did, 10),
        name: addDealForm.name.trim() || null,
        domain: addDealForm.domain.trim() || null,
        url: addDealForm.url.trim() || null,
        industry: addDealForm.industry.trim() || null,
        sub: addDealForm.sub.trim() || null,
        geo: addDealForm.geo.trim() || null,
        year: intOrNull(addDealForm.year),
        asking: numOrNull(addDealForm.asking),
        rev: numOrNull(addDealForm.rev),
        ebitda: numOrNull(addDealForm.ebitda),
        sale: numOrNull(addDealForm.sale),
        buyer: addDealForm.buyer.trim() || null,
        cim: addDealForm.cim.trim() || null,
        seller: addDealForm.seller.trim() || null,
        finalised: addDealForm.finalised.trim() || null,
        comm_pct: numOrNull(addDealForm.comm_pct),
        offer_t: intOrNull(addDealForm.offer_t),
        exec_t: intOrNull(addDealForm.exec_t),
        total_t: intOrNull(addDealForm.total_t),
        ev_rev: numOrNull(addDealForm.ev_rev),
        ev_eb: numOrNull(addDealForm.ev_eb),
        sale_mult: numOrNull(addDealForm.sale_mult),
        ask_mult: numOrNull(addDealForm.ask_mult),
      };
      setAddDealBusy(true);
      try {
        const { data } = await api.post("/add_deal", body);
        if (data?.ok) {
          setAddDealMsg({ type: "ok", text: data.message || "Deal added." });
          setAddDealForm({ ...EMPTY_ADD_DEAL_FORM });
          await fetchDeals();
        } else {
          setAddDealMsg({ type: "err", text: data?.message || "Could not add deal." });
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Request failed.";
        setAddDealMsg({ type: "err", text: msg });
      } finally {
        setAddDealBusy(false);
      }
    },
    [addDealForm, fetchDeals]
  );

  // ── Clause helpers ──
  const toggleClause = (cat, i) => {
    const key = `${cat}-${i}`;
    setOpenClauses((o) => ({ ...o, [key]: !o[key] }));
  };

  const copyClause = (cat, i) => {
    navigator.clipboard.writeText(CLAUSES[cat].items[i].text);
  };

  const clauseItems = useMemo(() => {
    const q = clauseSearch.toLowerCase();
    return (CLAUSES[curCat]?.items || []).filter((c) => !q || c.title.toLowerCase().includes(q) || c.text.toLowerCase().includes(q) || c.notes.toLowerCase().includes(q));
  }, [curCat, clauseSearch]);

  const newsIndustryOptions = useMemo(() => {
    const set = new Set();
    maNews.forEach((n) => {
      const v = (n?.industry ?? "").toString().trim();
      if (v) set.add(v);
    });
    return ["All Industries", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [maNews]);

  const filteredMaNews = useMemo(() => {
    if (!newsIndustry || newsIndustry === "All Industries") return maNews;
    return maNews.filter(
      (n) => (n?.industry ?? "").toString().trim() === newsIndustry
    );
  }, [maNews, newsIndustry]);

  const newsTotalPages = Math.max(1, Math.ceil(filteredMaNews.length / NEWS_PER_PAGE));
  const newsSlice = useMemo(() => {
    const total = Math.max(1, Math.ceil(filteredMaNews.length / NEWS_PER_PAGE));
    const page = Math.min(Math.max(1, newsPage), total);
    const start = (page - 1) * NEWS_PER_PAGE;
    return filteredMaNews.slice(start, start + NEWS_PER_PAGE);
  }, [filteredMaNews, newsPage]);

  useEffect(() => {
    setNewsPage(1);
  }, [newsIndustry]);

  // ═══════════════════════════════
  // RENDER
  // ═══════════════════════════════
  const allEvs = deals.filter((x) => x.ev_eb && x.ev_eb > 0 && x.ev_eb < 50).map((x) => x.ev_eb).sort((a, b) => a - b);

  return (
    <>
      {/* ══ TOPBAR ══ */}
      <header className="topbar">
        <div className="brand">
          <img src="fe_logo_new.png" alt="FE International" className="brand-logo-img" />
          <span className="brand-divider" />
          <button
            type="button"
            className="nav-tab nav-tab-brand"
            onClick={() => setActiveTab("home")}
          >
            <span className="brand-name">FE Deal <span>Intel</span></span>
          </button>
        </div>
        <nav className="nav-tabs">
          <button className={`nav-tab ${activeTab === "deals" ? "active" : ""}`} onClick={() => requestProtectedTab("deals")}>
            <span className="nav-tab-icon icon-deals">⬡</span> Intelligence
          </button>
          <button type="button" className="nav-tab" disabled aria-disabled="true">
            <span className="nav-tab-icon icon-clause">§</span> Clause Library
          </button>
          <button
            type="button"
            className={`nav-tab ${activeTab === "origination" ? "active" : ""}`}
            onClick={() => {
              if (activeTab === "origination") {
                document.getElementById("origination-toolkit-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
              } else {
                requestProtectedTab("origination");
              }
            }}
          >
            <span className="nav-tab-icon icon-orig">◈</span> Origination Toolkit
          </button>
          <button
            type="button"
            className={`nav-tab ${activeTab === "metrics" ? "active" : ""}`}
            onClick={() => requestProtectedTab("metrics")}
          >
            <span className="nav-tab-icon icon-metrics">◎</span> Metrics
          </button>
          <button
            type="button"
            className={`nav-tab ${activeTab === "execution" ? "active" : ""}`}
            onClick={() => requestProtectedTab("execution")}
          >
            <span className="nav-tab-icon icon-exec">⚡</span> Execution Toolkit
          </button>
        </nav>
        <div className="topbar-right" />
      </header>

      {/* ══ PANEL: HOME (placeholder) ══ */}
      {activeTab === "home" && (
        <div id="panel-home" style={{ animation: "fadeUp 0.22s ease" }}>
          <div className="feed-page-heading">
            <div>
              <h1>Global <em>M&amp;A</em>: Deals &amp; Insights</h1>
              <div className="feed-page-sub">Real-time deal intelligence · FE International</div>
            </div>
          </div>

          <div className="home-news-wrap">
            <div className="home-news-head">
              <div>
                <h2 className="home-news-title">Latest M&amp;A headlines</h2>
                <div className="home-news-meta">
                  {maNewsLoading ? "Loading…" : maNewsError ? "Could not load" : `${filteredMaNews.length} stor${filteredMaNews.length === 1 ? "y" : "ies"}`}
                </div>
              </div>
              <div className="home-news-filter">
                <label className="home-news-filter-label" htmlFor="home-news-industry">Industry</label>
                <select
                  id="home-news-industry"
                  className="home-news-industry-select"
                  value={newsIndustry}
                  onChange={(e) => setNewsIndustry(e.target.value)}
                  disabled={maNewsLoading && maNews.length === 0}
                >
                  {newsIndustryOptions.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
            </div>

            {maNewsError && !maNewsLoading && (
              <div className="deal-search-err" style={{ marginBottom: 16 }}>
                {maNewsError}
              </div>
            )}

            {maNewsLoading && maNews.length === 0 && (
              <div className="gen-loading">
                <span className="spin" aria-hidden />
                <span>Fetching M&amp;A news…</span>
              </div>
            )}

            {!maNewsLoading && !maNewsError && maNews.length === 0 && (
              <div className="empty-msg" style={{ padding: "40px 20px" }}>
                <div className="em-icon" aria-hidden>
                  📰
                </div>
                <p>No news items yet.</p>
              </div>
            )}

            {!maNewsLoading && !maNewsError && maNews.length > 0 && filteredMaNews.length === 0 && (
              <div className="empty-msg" style={{ padding: "40px 20px" }}>
                <div className="em-icon" aria-hidden>
                  📰
                </div>
                <p>No news items for {newsIndustry}.</p>
              </div>
            )}

            {newsSlice.length > 0 && (
              <>
                <div className="news-grid">
                  {newsSlice.map((item, idx) => {
                    const key = `${item.url || ""}-${item.published_at || ""}-${idx}`;
                    const href = item.url || "#";
                    const safeHref = href === "#" ? undefined : href;
                    const inner = (
                      <>
                        <MaNewsThumbnail src={item.thumbnail_url} />
                        <div className="news-card-body">
                          <span className="news-card-source">{item.source || "Source"}</span>
                          <span className="news-card-title">{item.title || "Untitled"}</span>
                          {item.snippet ? <p className="news-card-snippet">{item.snippet}</p> : null}
                          <div className="news-card-foot">
                            <span className="news-card-date">{formatNewsDate(item.published_at)}</span>
                            {safeHref ? <span className="news-card-cta">Open article →</span> : null}
                          </div>
                        </div>
                      </>
                    );
                    return safeHref ? (
                      <a key={key} className="news-card" href={safeHref} target="_blank" rel="noopener noreferrer">
                        {inner}
                      </a>
                    ) : (
                      <div key={key} className="news-card" style={{ cursor: "default" }}>
                        {inner}
                      </div>
                    );
                  })}
                </div>

                {filteredMaNews.length > NEWS_PER_PAGE && (
                  <div className="news-pagi">
                    <button
                      type="button"
                      className="news-pagi-btn"
                      disabled={newsPage <= 1 || maNewsLoading}
                      onClick={() => setNewsPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                    <span className="news-pagi-info">
                      Page {Math.min(newsPage, newsTotalPages)} of {newsTotalPages}
                    </span>
                    <button
                      type="button"
                      className="news-pagi-btn"
                      disabled={newsPage >= newsTotalPages || maNewsLoading}
                      onClick={() => setNewsPage((p) => Math.min(newsTotalPages, p + 1))}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}

            {maNewsLoading && maNews.length > 0 && (
              <div className="search-loading" style={{ marginTop: 12 }}>
                <span className="spin-sm" aria-hidden />
                <span>Refreshing…</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ SEARCH BANNER ══ */}
      {activeTab === "deals" && (
        <div className="search-banner">
          <div className="search-banner-inner">
            <div className="search-banner-row">
              <div className="search-bar-wrap">
                <span className="search-icon-left">⌕</span>
                <input className={`search-bar${searchQuery ? " search-bar--clearable" : ""}`} type="text" placeholder='Search company, e.g. SaaS deals with sales price between 500K and 1M' value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runIntelQuery()} />
                {searchQuery ? (
                  <button type="button" className="search-bar-clear" aria-label="Clear search text" onClick={() => setSearchQuery("")}>×</button>
                ) : null}
              </div>
              <div className="search-actions">
                <div className="ai-search-toggle-wrap">
                  <span className="ai-search-toggle-label" id="ai-search-label">AI Search</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={aiSearchOn}
                    aria-labelledby="ai-search-label"
                    className={`ai-search-switch${aiSearchOn ? " on" : ""}`}
                    onClick={() => setAiSearchOn((v) => !v)}
                  >
                    <span className="ai-search-switch-thumb" />
                  </button>
                </div>
                <button type="button" className="search-go-btn" onClick={() => runIntelQuery()}>GO</button>
                <button type="button" className="search-clear-btn" onClick={clearIntelSearch}>RESET</button>
              </div>
            </div>
            <div className="search-examples">
              {EXAMPLE_QUERIES.map((eq) => (
                <span key={eq.label} className="ex-pill" onClick={() => { setSearchQuery(eq.q); runIntelQuery(eq.q); }}>{eq.label}</span>
              ))}
            </div>
            {searchLoading && (
              <div className="search-loading">
                <div className="spin-sm" />
                <span>{searchLoadMsg}</span>
              </div>
            )}
            {dealSearchError && !searchLoading && <div className="deal-search-err">{dealSearchError}</div>}
          </div>
        </div>
      )}

      {/* ══ PANEL: INTELLIGENCE ══ */}
      {activeTab === "deals" && (
        <div style={{ animation: "fadeUp 0.22s ease" }}>
          <div className="intel-wrap">
            {/* Sidebar */}
            <aside className="sidebar">
              <div className="sidebar-group">
                <label className="filter-label">Search Company</label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)", fontSize: 13, pointerEvents: "none" }}>⌕</span>
                  <input style={{ width: "100%", padding: "8px 10px 8px 30px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "12.5px", color: "var(--text-main)", background: "var(--surface)", outline: "none" }} type="text" placeholder="Company name…" value={filters.search} onChange={(e) => updateFilter("search", e.target.value)} />
                </div>
              </div>
              <div className="sidebar-group">
                <label className="filter-label">Industry</label>
                <select className="filter-select" value={filters.industry} onChange={(e) => updateFilter("industry", e.target.value)}>
                  <option value="">All Industries</option>
                  {filterIndustries.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
                <label className="filter-label" style={{ marginTop: 6 }}>Sub Industry</label>
                <select
                  className="filter-select"
                  value={filters.sub}
                  onChange={(e) => updateFilter("sub", e.target.value)}
                  disabled={filterSubs.length === 0}
                >
                  <option value="">All Sub Industries</option>
                  {filterSubs.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <label className="filter-label" style={{ marginTop: 6 }}>Deal Year</label>
                <select className="filter-select" value={filters.year} onChange={(e) => updateFilter("year", e.target.value)}>
                  <option value="">All Years</option>
                  {filterYears.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <label className="filter-label" style={{ marginTop: 6 }}>Location</label>
                <select className="filter-select" value={filters.geo} onChange={(e) => updateFilter("geo", e.target.value)}>
                  <option value="">All Locations</option>
                  {filterGeos.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="sidebar-divider" />
              <div className="sidebar-group">
                <label className="filter-label">Sale Price ($)</label>
                <div className="range-row">
                  <div className="range-field"><label>Min</label><input className="range-input" type="number" placeholder="0" value={filters.priceMin} onChange={(e) => updateFilter("priceMin", e.target.value)} /></div>
                  <div className="range-field"><label>Max</label><input className="range-input" type="number" placeholder="Any" value={filters.priceMax} onChange={(e) => updateFilter("priceMax", e.target.value)} /></div>
                </div>
                <label className="filter-label" style={{ marginTop: 6 }}>Revenue LTM ($)</label>
                <div className="range-row">
                  <div className="range-field"><label>Min</label><input className="range-input" type="number" placeholder="0" value={filters.revMin} onChange={(e) => updateFilter("revMin", e.target.value)} /></div>
                  <div className="range-field"><label>Max</label><input className="range-input" type="number" placeholder="Any" value={filters.revMax} onChange={(e) => updateFilter("revMax", e.target.value)} /></div>
                </div>
                <label className="filter-label" style={{ marginTop: 6 }}>EV / EBITDA (x)</label>
                <div className="range-row">
                  <div className="range-field"><label>Min</label><input className="range-input" type="number" placeholder="0" step="0.1" value={filters.evMin} onChange={(e) => updateFilter("evMin", e.target.value)} /></div>
                  <div className="range-field"><label>Max</label><input className="range-input" type="number" placeholder="Any" step="0.1" value={filters.evMax} onChange={(e) => updateFilter("evMax", e.target.value)} /></div>
                </div>
              </div>
              <div className="sidebar-divider" />
              <button
                type="button"
                className="reset-btn"
                onClick={() => {
                  resetFilters();
                  fetchDeals();
                }}
              >
                ↺ Reset All Filters
              </button>
            </aside>

            <div className="main-content">
              {/* Stats Strip */}
              <div className="stats-strip">
                <div className="stat-cell"><span className="stat-val">{stats.medEvEbitda}</span><span className="stat-lbl">Median EV/EBITDA</span></div>
                <div className="stat-cell"><span className="stat-val">{stats.medEvRev}</span><span className="stat-lbl">Median EV/Revenue</span></div>
                <div className="stat-cell"><span className="stat-val">{stats.avgPrice}</span><span className="stat-lbl">Avg Sale Price</span></div>
                <div className="stat-cell"><span className="stat-val">{stats.avgMargin}</span><span className="stat-lbl">Avg EBITDA Margin</span></div>
              </div>

              {/* Results Bar */}
              <div className="results-bar">
                <div className="results-count">
                  {dealsLoading && deals.length === 0 ? (
                    "Loading deals…"
                  ) : dealsError && deals.length === 0 ? (
                    <span style={{ color: "var(--text-dim)" }}>Could not load deals</span>
                  ) : (
                    <>
                      Showing <strong>{displaySorted.length}</strong> deals
                      {intelSearchDeals != null && <span style={{ marginLeft: 8, fontWeight: 400, color: "var(--text-dim)", fontSize: 12 }}>· natural-language search (CLEAR for full list)</span>}
                      {totalActiveColumnFilters > 0 && (
                        <span style={{ marginLeft: 8, fontWeight: 400, color: "var(--text-dim)", fontSize: 12 }}>
                          · {totalActiveColumnFilters} column filter{totalActiveColumnFilters === 1 ? "" : "s"}{" "}
                          <button
                            type="button"
                            onClick={() => { setColumnFilters({}); setOpenColFilter(null); }}
                            style={{ background: "none", border: "none", padding: 0, marginLeft: 4, color: "var(--blue)", cursor: "pointer", font: "inherit", fontWeight: 500 }}
                          >CLEAR</button>
                        </span>
                      )}
                      {dealsLoading && deals.length > 0 ? <span style={{ marginLeft: 8, fontWeight: 400, color: "var(--text-dim)", fontSize: 12 }}>· refreshing…</span> : null}
                    </>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="export-deals-btn"
                    disabled={dealsLoading || displaySorted.length === 0 || exportBusy}
                    onClick={handleExportDeals}
                  >
                    {exportBusy ? "Exporting…" : "Export CSV"}
                  </button>
                  <button type="button" className="add-deal-btn" onClick={openAddDealModal}>
                    +Add deal
                  </button>
                  <div className="sort-group">
                    <span className="sort-lbl">Sort:</span>
                    {[["year", "Year"], ["sale", "Price"], ["ev_rev", "EV/REV"], ["ev_eb", "EV/EBITDA"], ["rev", "Revenue"], ["ebitda", "EBITDA"]].map(([k, label]) => (
                      <button key={k} type="button" className={`sort-pill ${sortKey === k ? "active" : ""}`} onClick={() => handleSort(k)}>{label}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="table-wrap">
                {displaySorted.length === 0 ? (
                  <div className="empty-msg">
                    <div className="em-icon">◌</div>
                    {dealsLoading && deals.length === 0 ? (
                      <p>Loading deals from database…</p>
                    ) : dealsError && deals.length === 0 ? (
                      <>
                        <p>{dealsError}</p>
                        <button type="button" className="reset-btn" style={{ marginTop: 12 }} onClick={() => fetchDeals()}>Retry</button>
                      </>
                    ) : deals.length === 0 ? (
                      <p>No closed deals returned from the database.</p>
                    ) : intelSearchDeals != null ? (
                      <>
                        <p>No deals matched your search prompt.</p>
                        <button type="button" className="reset-btn" style={{ marginTop: 12 }} onClick={clearIntelSearch}>CLEAR search</button>
                      </>
                    ) : (
                      <p>No deals match your filters.<br />Try adjusting the criteria or resetting filters.</p>
                    )}
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        {[
                          { key: "name",     label: "Company",     style: { minWidth: 160 } },
                          { key: "industry", label: "Industry" },
                          { key: "geo",      label: "Location" },
                          { key: "year",     label: "Year" },
                          { key: "rev",      label: "Revenue LTM", numeric: true },
                          { key: "ebitda",   label: "EBITDA LTM",  numeric: true },
                          { key: "sale",     label: "Sale Price",  numeric: true },
                          { key: "ev_eb",    label: "EV/EBITDA",   numeric: true },
                          { key: "ev_rev",   label: "EV/Rev",      numeric: true },
                          { key: "buyer",    label: "Buyer",       style: { maxWidth: 120, width: 120 } },
                          { key: "cim",      label: "CIM",         style: { minWidth: 52 }, center: true },
                        ].map((col) => {
                          const selCount = (columnFilters[col.key] || []).length;
                          const align = col.center ? "center" : col.numeric ? "right" : "left";
                          return (
                            <th
                              key={col.key}
                              style={{ ...(col.style || {}), textAlign: align }}
                            >
                              <div className={`th-inner th-inner-${align}`}>
                                <span className="th-label">{col.label}</span>
                                <button
                                  type="button"
                                  ref={(el) => { colFilterBtnsRef.current[col.key] = el; }}
                                  className={`th-filter-btn${selCount > 0 ? " active" : ""}`}
                                  title={selCount > 0 ? `${selCount} value${selCount === 1 ? "" : "s"} selected` : "Filter"}
                                  aria-label={`Filter ${col.label}`}
                                  aria-expanded={openColFilter === col.key}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => { e.stopPropagation(); toggleColumnFilter(col.key); }}
                                >
                                  <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
                                    <path d="M1 2h10L7 7v3.5L5 11V7z" fill="currentColor" />
                                  </svg>
                                  {selCount > 0 && <span className="th-filter-badge">{selCount}</span>}
                                </button>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {displaySorted.slice(0, 500).map((d, rowIdx) => {
                        const cimLink = cimHref(d.cim);
                        return (
                        <tr key={d.id != null ? d.id : `${d.domain || ""}-${d.year || ""}-${rowIdx}`} onClick={() => openDrawer(d.id)}>
                          <td className="cell-name">{d.name || d.domain || "—"}</td>
                          <td>{d.industry ? <span className="chip chip-blue">{d.industry}</span> : <span className="dim">—</span>}</td>
                          <td><span className="dim" style={{ fontSize: 12 }}>{d.geo || "—"}</span></td>
                          <td><span className="mono dim">{d.year || "—"}</span></td>
                          <td style={{ textAlign: "right" }}><span className="mono">{fmt(d.rev)}</span></td>
                          <td style={{ textAlign: "right" }}><span className="mono">{fmt(d.ebitda)}</span></td>
                          <td style={{ textAlign: "right" }}><span className="mono num-accent">{fmt(d.sale)}</span></td>
                          <td style={{ textAlign: "right" }}><span className="mono">{fmtX(d.ev_eb)}</span></td>
                          <td style={{ textAlign: "right" }}><span className="mono">{fmtX(d.ev_rev)}</span></td>
                          <td className="cell-buyer" title={d.buyer ? String(d.buyer) : undefined}>{d.buyer || "—"}</td>
                          <td style={{ textAlign: "center" }}>
                            {cimLink ? (
                              <a
                                href={cimLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{ fontSize: 12, color: "var(--blue)", fontWeight: 600 }}
                              >
                                LINK
                              </a>
                            ) : null}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* ── Column header filter popover (rendered at panel root so the
                table-wrap's overflow can't clip it; positioned via fixed) ── */}
          {openColFilter && (() => {
            const cfg = DEAL_COLUMN_FILTER_CONFIG[openColFilter];
            if (!cfg) return null;
            const selected = new Set(columnFilters[openColFilter] || []);
            const allValues = colFilterOptions.map((o) => o.value);
            const visible = visibleColFilterOptions;
            const visVals = visible.map((o) => o.value);
            const allVisibleSelected =
              visVals.length > 0 && visVals.every((v) => selected.has(v));
            return (
              <div
                ref={colFilterPopRef}
                className="th-filter-pop"
                style={{ top: colFilterPos.top, left: colFilterPos.left, width: colFilterPos.width }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                role="dialog"
                aria-label={`Filter ${cfg.label}`}
              >
                <div className="th-filter-head">
                  <span className="th-filter-title">Filter · {cfg.label}</span>
                  <button
                    type="button"
                    className="th-filter-x"
                    aria-label="Close filter"
                    onClick={() => setOpenColFilter(null)}
                  >✕</button>
                </div>
                <div className="th-filter-search-wrap">
                  <input
                    type="text"
                    className="th-filter-search"
                    placeholder="Search values…"
                    value={colFilterSearch}
                    onChange={(e) => setColFilterSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="th-filter-actions">
                  <button
                    type="button"
                    className="th-filter-act"
                    disabled={visVals.length === 0}
                    onClick={() => {
                      if (allVisibleSelected) {
                        // Remove visible values from selection
                        const next = [...selected].filter((v) => !visVals.includes(v));
                        if (next.length === 0) clearColumnFilter(openColFilter);
                        else selectAllColumnValues(openColFilter, next);
                      } else {
                        // Add all visible values to selection
                        const merged = new Set(selected);
                        for (const v of visVals) merged.add(v);
                        selectAllColumnValues(openColFilter, [...merged]);
                      }
                    }}
                  >
                    {allVisibleSelected ? "Unselect all" : "Select all"}
                  </button>
                  <button
                    type="button"
                    className="th-filter-act"
                    disabled={selected.size === 0}
                    onClick={() => clearColumnFilter(openColFilter)}
                  >
                    Clear
                  </button>
                </div>
                <div className="th-filter-list">
                  {visible.length === 0 ? (
                    <div className="th-filter-empty">
                      {allValues.length === 0 ? "No values to filter" : "No matches"}
                    </div>
                  ) : (
                    visible.map((opt) => {
                      const checked = selected.has(opt.value);
                      return (
                        <label
                          key={String(opt.value)}
                          className={`th-filter-row${checked ? " checked" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleColumnFilterValue(openColFilter, opt.value)}
                          />
                          <span className="th-filter-val" title={String(opt.value)}>
                            {String(opt.value)}
                          </span>
                          <span className="th-filter-count">{opt.count}</span>
                        </label>
                      );
                    })
                  )}
                </div>
                <div className="th-filter-foot">
                  <span className="th-filter-meta">
                    {selected.size > 0
                      ? `${selected.size} of ${allValues.length} selected`
                      : `${allValues.length} value${allValues.length === 1 ? "" : "s"}`}
                  </span>
                  <button
                    type="button"
                    className="th-filter-done"
                    onClick={() => setOpenColFilter(null)}
                  >Done</button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ══ ADD DEAL MODAL ══ */}
      {addDealOpen && (
        <div
          className="add-deal-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-deal-modal-title"
          onClick={closeAddDealModal}
        >
          <div className="add-deal-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="add-deal-modal-head">
              <h2 id="add-deal-modal-title" className="add-deal-modal-title">
                Add closed deal
              </h2>
              <button type="button" className="add-deal-modal-x" aria-label="Close" onClick={closeAddDealModal}>
                ✕
              </button>
            </div>
            <form id="add-deal-form" className="add-deal-modal-scroll" onSubmit={submitAddDeal}>
              <div className="add-deal-form-grid">
                <div className="add-deal-field">
                  <label htmlFor="adf-deal-id">Deal ID</label>
                  <input id="adf-deal-id" type="number" inputMode="numeric" value={addDealForm.deal_id} onChange={(e) => updateAddDealForm("deal_id", e.target.value)} required />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-name">Deal name</label>
                  <input id="adf-name" type="text" value={addDealForm.name} onChange={(e) => updateAddDealForm("name", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-domain">Domain</label>
                  <input id="adf-domain" type="text" placeholder="e.g. example.com" value={addDealForm.domain} onChange={(e) => updateAddDealForm("domain", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-url">URL</label>
                  <input id="adf-url" type="text" placeholder="https://…" value={addDealForm.url} onChange={(e) => updateAddDealForm("url", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-industry">Industry</label>
                  <input id="adf-industry" type="text" maxLength={200} value={addDealForm.industry} onChange={(e) => updateAddDealForm("industry", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-sub">Sub industry</label>
                  <input id="adf-sub" type="text" maxLength={200} value={addDealForm.sub} onChange={(e) => updateAddDealForm("sub", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-geo">Geography</label>
                  <input id="adf-geo" type="text" maxLength={200} value={addDealForm.geo} onChange={(e) => updateAddDealForm("geo", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-year">Year</label>
                  <input id="adf-year" type="number" inputMode="numeric" value={addDealForm.year} onChange={(e) => updateAddDealForm("year", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-asking">Asking price</label>
                  <input id="adf-asking" type="number" step="any" value={addDealForm.asking} onChange={(e) => updateAddDealForm("asking", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-rev">Revenue</label>
                  <input id="adf-rev" type="number" step="any" value={addDealForm.rev} onChange={(e) => updateAddDealForm("rev", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-ebitda">EBITDA</label>
                  <input id="adf-ebitda" type="number" step="any" value={addDealForm.ebitda} onChange={(e) => updateAddDealForm("ebitda", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-sale">Sale</label>
                  <input id="adf-sale" type="number" step="any" value={addDealForm.sale} onChange={(e) => updateAddDealForm("sale", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-buyer">Buyer</label>
                  <input id="adf-buyer" type="text" maxLength={200} value={addDealForm.buyer} onChange={(e) => updateAddDealForm("buyer", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-cim">CIM (URL)</label>
                  <input id="adf-cim" type="text" placeholder="https://…" value={addDealForm.cim} onChange={(e) => updateAddDealForm("cim", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-seller">Seller</label>
                  <input id="adf-seller" type="text" maxLength={200} value={addDealForm.seller} onChange={(e) => updateAddDealForm("seller", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-finalised">Finalized</label>
                  <input id="adf-finalised" type="date" value={addDealForm.finalised} onChange={(e) => updateAddDealForm("finalised", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-comm">Commission %</label>
                  <input id="adf-comm" type="number" step="any" placeholder="0.1 for 10%" value={addDealForm.comm_pct} onChange={(e) => updateAddDealForm("comm_pct", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-offer-t">Offer time (days)</label>
                  <input id="adf-offer-t" type="number" inputMode="numeric" value={addDealForm.offer_t} onChange={(e) => updateAddDealForm("offer_t", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-exec-t">Execution time (days)</label>
                  <input id="adf-exec-t" type="number" inputMode="numeric" value={addDealForm.exec_t} onChange={(e) => updateAddDealForm("exec_t", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-total-t">Total time (days)</label>
                  <input id="adf-total-t" type="number" inputMode="numeric" value={addDealForm.total_t} onChange={(e) => updateAddDealForm("total_t", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-ev-rev">EV / Revenue</label>
                  <input id="adf-ev-rev" type="number" step="any" value={addDealForm.ev_rev} onChange={(e) => updateAddDealForm("ev_rev", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-ev-eb">EV / EBITDA</label>
                  <input id="adf-ev-eb" type="number" step="any" value={addDealForm.ev_eb} onChange={(e) => updateAddDealForm("ev_eb", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-sale-mult">Sale multiple</label>
                  <input id="adf-sale-mult" type="number" step="any" value={addDealForm.sale_mult} onChange={(e) => updateAddDealForm("sale_mult", e.target.value)} />
                </div>
                <div className="add-deal-field">
                  <label htmlFor="adf-ask-mult">Asking multiple</label>
                  <input id="adf-ask-mult" type="number" step="any" value={addDealForm.ask_mult} onChange={(e) => updateAddDealForm("ask_mult", e.target.value)} />
                </div>
              </div>
            </form>
            <div className="add-deal-modal-foot">
              <div>
                {addDealMsg.type === "ok" ? (
                  <p className="add-deal-msg-success" role="status">
                    {addDealMsg.text}
                  </p>
                ) : addDealMsg.type === "err" ? (
                  <p className="add-deal-msg-err" role="alert">
                    {addDealMsg.text}
                  </p>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button type="button" className="search-clear-btn" style={{ height: 40 }} onClick={closeAddDealModal}>
                  Cancel
                </button>
                <button type="submit" form="add-deal-form" className="add-deal-submit" disabled={addDealBusy}>
                  {addDealBusy ? "Saving…" : "Save deal"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ DRAWER ══ */}
      {drawerDeal && (
        <>
          <div className="overlay" onClick={closeDrawer} />
          <div className="drawer" style={{ transform: "translateX(0)" }}>
            <div className="drawer-top">
              <button className="drawer-close" onClick={closeDrawer}>✕</button>
              <div className="drawer-title">
                {drawerDeal.domain && drawerDeal.domain.includes(".") && (
                  <img className="favicon" src={`https://www.google.com/s2/favicons?domain=${drawerDeal.domain}&sz=32`} style={{ width: 20, height: 20, borderRadius: 4 }} onError={(e) => e.target.style.display = "none"} alt="" />
                )}
                <span>{drawerDeal.name || drawerDeal.domain || "Deal Detail"}</span>
              </div>
              <div className="drawer-sub">{drawerDeal.sub || drawerDeal.industry || ""}</div>
            </div>
            <div className="drawer-body">
              <DrawerContent deal={drawerDeal} aiSummary={aiSummary} aiLoading={aiSummaryLoading} allEvs={allEvs} />
            </div>
          </div>
        </>
      )}

      {/* ══ PANEL: CLAUSE LIBRARY ══ */}
      {activeTab === "clauses" && (
        <div style={{ animation: "fadeUp 0.22s ease" }}>
          <div className="mod-banner">
            <div><h2>Clause Library</h2><p>Built from FE engagement agreements · What we've agreed to and where we hold firm</p></div>
            <span className="mod-pill">Live Reference</span>
          </div>
          <div className="clause-wrap">
            <nav className="clause-sidebar">
              <span className="csb-head">Categories</span>
              {CLAUSE_CATEGORIES.map((c) => (
                <button key={c.key} className={`cat-btn ${curCat === c.key ? "active" : ""}`} onClick={() => { setCurCat(c.key); setClauseSearch(""); setOpenClauses({}); }}>
                  {c.label}<span className="cat-count">{c.count}</span>
                </button>
              ))}
            </nav>
            <div className="clause-main">
              <div className="clause-top">
                <div className="clause-top-left">
                  <h2>{CLAUSES[curCat].title}</h2>
                  <p>{CLAUSES[curCat].desc}</p>
                </div>
                <input className="clause-search-input" type="text" placeholder="Search clauses…" value={clauseSearch} onChange={(e) => setClauseSearch(e.target.value)} />
              </div>
              <div>
                {clauseItems.length === 0 ? (
                  <div style={{ padding: 32, textAlign: "center", color: "var(--text-dim)", fontSize: 13 }}>No clauses match your search.</div>
                ) : clauseItems.map((c, i) => {
                  const isOpen = !!openClauses[`${curCat}-${i}`];
                  return (
                    <div key={`${curCat}-${i}`} className={`clause-card ${isOpen ? "open" : ""}`}>
                      <div className="clause-head" onClick={() => toggleClause(curCat, i)}>
                        <span className="clause-title">{c.title}</span>
                        <span className={`stance ${c.stance === "firm" ? "s-firm" : c.stance === "flexible" ? "s-flex" : "s-neg"}`}>
                          {c.stance === "firm" ? "🔒 Hold Firm" : c.stance === "flexible" ? "✓ Flexible" : "↔ Negotiable"}
                        </span>
                        <span className="clause-chevron">▾</span>
                      </div>
                      {isOpen && (
                        <div className="clause-body">
                          <div className="clause-text">{c.text}</div>
                          <div className="clause-notes" dangerouslySetInnerHTML={{ __html: c.notes }} />
                          <div className="clause-actions">
                            <button className="act-btn" onClick={() => copyClause(curCat, i)}>⊞ Copy Clause</button>
                            {c.stance === "firm" && <div className="hold-tag">🔒 Non-negotiable</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ PANEL: ORIGINATION TOOLKIT ══ */}
      {activeTab === "origination" && (
        <div style={{ animation: "fadeUp 0.22s ease" }}>
          <div className="mod-banner">
            <div>
              <h2>Origination Toolkit</h2>
              <p>Institutional-grade M&amp;A origination · Valuation validation · Inbound deal qualification · 6 institutional-grade tools</p>
            </div>
            <span className="mod-pill">AI Powered</span>
          </div>
          <div className="orig-page">
            <div className="orig-hero">
              <h2>Origination Toolkit</h2>
              <p>M&amp;A origination · Valuation validation · Deal qualification · Institutional advisory-grade</p>
            </div>

            <div className="orig-section-label">Core Origination Tools</div>
            <div className="tools-grid-v2">
              {[
                { key: "briefing", cls: "t-brief", icon: "icon-brief", emoji: "📋", name: "Prospect Briefing", desc: "Institutional-grade briefing covering 3-year financial trends, revenue drivers, and market positioning.", badge: "upgrade", badgeText: "↑ Upgraded" },
                { key: "teaser", cls: "t-teas", icon: "icon-teas", emoji: "◈", name: "Deal Teaser", desc: "Institutional CIM-standard teaser with anonymity controls & narrative focus.", badge: "upgrade", badgeText: "↑ Upgraded" },
                { key: "valuation_narrative", cls: "t-val", icon: "icon-val", emoji: "⬡", name: "Valuation Narrative", desc: "Audit-grade narrative with revenue quality, EBITDA normalisation & comps.", badge: "upgrade", badgeText: "↑ Upgraded" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`tool-card ${t.cls} ${curTool === t.key ? "active-tool" : ""}`}
                  onClick={() => {
                    setCurTool(t.key);
                    setGenOutput("");
                  }}
                >
                  <div className={`tc-icon ${t.icon}`}>{t.emoji}</div>
                  <div className="tc-name">{t.name}</div>
                  <div className="tc-desc">{t.desc}</div>
                  {t.badge === "upgrade" ? <div className="tool-badge badge-upgrade">{t.badgeText}</div> : null}
                </button>
              ))}
            </div>

            <div className="orig-section-label">Advanced Modules</div>
            <div className="tools-grid-v2-row2">
              {[
                { key: "risk", cls: "t-risk", icon: "icon-risk", emoji: "⚠", name: "Commercial Risk Lens", desc: "Structured risk snapshot — customer concentration, revenue volatility, margin stability, contract visibility. Replaces subjective evaluation with measurable deal risk scores.", badge: "new", badgeText: "★ New" },
                { key: "vri", cls: "t-vri", icon: "icon-vri", emoji: "🧠", name: "Valuation Request Intelligence", desc: "Inbound deal qualification — one ingest fills benchmark, intent score, and response draft; batch URL scoring runs separately.", badge: "new", badgeText: "★ New" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`tool-card ${t.cls} ${curTool === t.key ? "active-tool" : ""}`}
                  onClick={() => {
                    setCurTool(t.key);
                    setGenOutput("");
                  }}
                >
                  <div className={`tc-icon ${t.icon}`}>{t.emoji}</div>
                  <div className="tc-name">{t.name}</div>
                  <div className="tc-desc">{t.desc}</div>
                  {t.badge === "new" ? <div className="tool-badge badge-new">{t.badgeText}</div> : null}
                </button>
              ))}
            </div>

            <div className="gen-box" id="origination-toolkit-form">
              <div className="gen-header">
                <div>
                  <div className="gen-h-title">{TOOL_INFO[curTool].title}</div>
                  <div className="gen-h-sub">{TOOL_INFO[curTool].sub}</div>
                </div>
              </div>
              <div className="gen-body-inner">
                <OriginationForm
                  tool={curTool}
                  origForms={origForms}
                  updateOrigForm={updateOrigForm}
                  onSubmit={() => submitOrigination()}
                  onVriStep={(step) => submitOrigination({ vriStep: step })}
                />
                {genLoading && (
                  <div className="gen-loading">
                    <div className="spin" />
                    <span>{genLoadMsg}</span>
                  </div>
                )}
                {curTool !== "vri" && !genLoading && genOutput ? (
                  <>
                    <label className="form-label" htmlFor="origination-output-ta" style={{ marginTop: 18 }}>
                      Generated output
                    </label>
                    <textarea
                      id="origination-output-ta"
                      className="orig-output-ta"
                      readOnly
                      rows={14}
                      value={genOutput}
                      aria-label="Generated origination output"
                    />
                    <div className="output-btns">
                      <button type="button" className="act-btn" onClick={() => navigator.clipboard.writeText(genOutput)}>⊞ Copy</button>
                      <button type="button" className="act-btn" onClick={() => setGenOutput("")}>✕ Clear</button>
                      <button type="button" className="act-btn" onClick={() => submitOrigination()}>↺ Regenerate</button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ PANEL: METRICS ══ */}
      {activeTab === "metrics" && <MetricsPanel />}

      {/* ══ PANEL: EXECUTION TOOLKIT ══ */}
      {activeTab === "execution" && <ExecutionToolkitPanel />}
    </>
  );
}

// ═══════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════

function DrawerContent({ deal: d, aiSummary, aiLoading, allEvs }) {
  const margin = d.rev && d.rev > 0 && d.ebitda && d.ebitda > 0 ? Math.round((d.ebitda / d.rev) * 100) : null;
  const pct = d.ev_eb > 0 ? Math.round(allEvs.filter((v) => v <= d.ev_eb).length / allEvs.length * 100) : null;
  const hasDom = d.domain && d.domain.includes(".");
  const cimLink = cimHref(d.cim);

  return (
    <>
      <div className="kpi-row">
        <div className="kpi"><span className="kpi-lbl">Sale Price</span><div className="kpi-v">{fmt(d.sale)}</div><div className="kpi-s">Ask: {fmt(d.asking)}</div></div>
        <div className="kpi"><span className="kpi-lbl">EV / EBITDA</span><div className="kpi-v">{fmtX(d.ev_eb)}</div><div className="kpi-s">EV/Rev: {fmtX(d.ev_rev)}</div></div>
        <div className="kpi"><span className="kpi-lbl">Revenue LTM</span><div className="kpi-v">{fmt(d.rev)}</div><div className="kpi-s">EBITDA: {fmt(d.ebitda)}</div></div>
        <div className="kpi"><span className="kpi-lbl">EBITDA Margin</span><div className="kpi-v">{margin !== null ? margin + "%" : "—"}</div><div className="kpi-s">{d.year || "—"} · {d.geo || "—"}</div></div>
      </div>

      <div className="sec-head bold">Deal Details</div>
      <div className="info-grid">
        {[
          ["Company", d.name || "—"],
          ["Domain", hasDom ? <a href={`https://${d.domain}`} target="_blank" rel="noopener noreferrer">{d.domain}</a> : d.domain || "—"],
          ["Seller Name", d.seller || "—"],
          ["Industry", d.industry || "—"],
          ["Sub-Sector", d.sub || "—"],
          ["Buyer", d.buyer || "—"],
          ["Location", d.geo || "—"],
          ["Year Closed", d.year || "—"],
          ["Finalised Date", fmtDealDate(d.finalised)],
          ["Commission %", d.comm_pct != null ? (d.comm_pct * 100).toFixed(1) + "%" : "—"],
          ["EV/Rev", fmtX(d.ev_rev)],
          ["EV/EBITDA", fmtX(d.ev_eb)],
          ["Offer Time", d.offer_t ? d.offer_t + " days" : "—"],
          ["Execution Time", d.exec_t ? d.exec_t + " days" : "—"],
          ["Total Time to Close", d.total_t ? d.total_t + " days" : "—"],
          ["CIM", cimLink ? <a href={cimLink} target="_blank" rel="noopener noreferrer">Link</a> : "—"],
        ].map(([k, v]) => (
          <div className="info-row" key={k}><span className="ik">{k}</span><span className="iv">{v}</span></div>
        ))}
      </div>

      {pct !== null && (
        <div className="comp-section">
          <div className="sec-head">Valuation Benchmark</div>
          <div className="comp-row"><span>0x</span><span>This deal: {d.ev_eb.toFixed(2)}x EV/EBITDA</span><span>50x+</span></div>
          <div className="comp-track">
            <div className="comp-fill" style={{ width: `${Math.min(d.ev_eb / 50 * 100, 100).toFixed(1)}%` }} />
            <div className="comp-median" style={{ left: `${(3.18 / 50 * 100).toFixed(1)}%` }} title="Dataset median 3.18x" />
          </div>
          <div className="comp-note">{pct}th percentile · Dataset median: 3.18x EV/EBITDA (line)</div>
        </div>
      )}

      <div className="ai-box">
        <div className="ai-label">Deal Summary</div>
        <div className="ai-text">
          {aiLoading ? (
            <div className="ai-dots"><span /><span /><span /></div>
          ) : (
            aiSummary || "Summary unavailable."
          )}
        </div>
      </div>
    </>
  );
}

/** Escape for safe HTML injection (origination / VRI formatted output). */
function htmlEscape(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** FE_Intel_v20 `renderSections` — headings, bullets, light **bold**. */
function originationSectionsHtml(text) {
  const lines = String(text || "").split("\n");
  let html = '<div class="orig-sec-body">';
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.slice(0, 3) === "## ") {
      html += `<div class="out-sec-head">${htmlEscape(trimmed.slice(3))}</div>`;
    } else if (trimmed.slice(0, 2) === "# ") {
      html += `<div class="out-sec-head">${htmlEscape(trimmed.slice(2))}</div>`;
    } else if (trimmed.slice(0, 2) === "• " || trimmed.slice(0, 2) === "* " || trimmed.slice(0, 2) === "- ") {
      html += `<div class="out-bullet">${formatOriginationLine(trimmed.slice(2))}</div>`;
    } else if (trimmed === "") {
      html += '<div style="height:6px"></div>';
    } else {
      html += `<div>${formatOriginationLine(trimmed)}</div>`;
    }
  });
  html += "</div>";
  return html;
}

function formatOriginationLine(trimmed) {
  const parts = trimmed.split(/\*\*(.+?)\*\*/);
  return parts.map((part, i) => (i % 2 === 1 ? `<strong>${htmlEscape(part)}</strong>` : htmlEscape(part))).join("");
}

/** FE_Intel_v20 `renderVRIIntent` — intent score card + sectioned body. */
function VRIIntentPresentation({ text }) {
  const raw = String(text || "");
  const scoreMatch = raw.match(/Score:\s*([1-5])(?:\/5)?/i);
  const score = scoreMatch ? parseInt(scoreMatch[1], 10) : null;
  const actionMatch = raw.match(/Decision:\s*(🟢\s*Pursue|🟡\s*Review|🔴\s*Pass|Pursue|Review|Pass)/i);
  let rawAction = actionMatch ? actionMatch[1].toLowerCase() : "";
  let action = rawAction.includes("pursue") ? "pursue" : rawAction.includes("pass") ? "pass" : "review";
  if (!actionMatch && score !== null) {
    action = score >= 4 ? "pursue" : score === 3 ? "review" : "pass";
  }
  const actionCfg = {
    pursue: {
      label: "🟢 Pursue — Strong acquisition candidate",
      pillCls: "intent-action-pursue",
      numColor: "#059669",
      numBg: "rgba(16,185,129,0.12)",
      numBorder: "rgba(16,185,129,0.35)",
    },
    review: {
      label: "🟡 Review — Requires further diligence",
      pillCls: "intent-action-review",
      numColor: "#b45309",
      numBg: "rgba(245,158,11,0.12)",
      numBorder: "rgba(245,158,11,0.35)",
    },
    pass: {
      label: "🔴 Pass — Weak or low-quality asset",
      pillCls: "intent-action-pass",
      numColor: "#b91c1c",
      numBg: "rgba(239,68,68,0.1)",
      numBorder: "rgba(239,68,68,0.3)",
    },
  };
  const cfg = actionCfg[action];
  const displayScore = score !== null ? score : action === "pursue" ? 4 : action === "review" ? 3 : 2;

  return (
    <>
      <div className="intent-score-card">
        <div className="intent-score-row">
          <div
            className="intent-score-num"
            style={{
              background: cfg.numBg,
              color: cfg.numColor,
              border: `2px solid ${cfg.numBorder}`,
            }}
          >
            {displayScore}
          </div>
          <div className="intent-score-label">
            <div className="score-title">Intent Score {displayScore}/5</div>
            <div className="score-sub">VRI COMPOSITE · 70% INTENT SIGNALS + 30% BENCHMARK</div>
          </div>
          <span className={`intent-action-pill ${cfg.pillCls}`}>{cfg.label}</span>
        </div>
      </div>
      <div className="vri-intent-detail" dangerouslySetInnerHTML={{ __html: originationSectionsHtml(raw) }} />
    </>
  );
}

/** Split backend `vri_ingest` output into benchmark / intent / response sections (see origination_tasks.yaml). */
function parseVriIngestOutput(raw) {
  const t = String(raw || "");
  const re = /<<<VRI_BENCHMARK>>>\s*([\s\S]*?)<<<VRI_INTENT>>>\s*([\s\S]*?)<<<VRI_RESPONSE>>>\s*([\s\S]*)/i;
  const m = t.match(re);
  if (!m) {
    return { benchmark: "", intent: "", response: "", ok: false };
  }
  return {
    benchmark: m[1].trim(),
    intent: m[2].trim(),
    response: m[3].trim(),
    ok: true,
  };
}

/** Parse FE_Intel_v20 batch output lines into table rows (same regex strategy as `renderBatchResults`). */
function parseVriBatchRows(rawText) {
  const lines = String(rawText || "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 10);
  const rows = [];
  const ACTION_CFG = {
    pursue: { label: "🟢 Pursue", cls: "action-pursue" },
    review: { label: "🟡 Review", cls: "action-review" },
    pass: { label: "🔴 Pass", cls: "action-pass" },
  };
  lines.forEach((line) => {
    let m = line.match(
      /Website:\s*(\S+)\s*\|\s*Intent Score:\s*(\d)\/5\s*\|\s*Reason:\s*([^|]+)\|\s*Recommended Action:\s*(.+)/i
    );
    if (!m) {
      m = line.match(
        /Website:\s*(\S+)\s*\|\s*Intent Score:\s*(\d)\/5\s*\|\s*Reason:\s*([^|]+)\|\s*(Pursue|Review|Pass)/i
      );
    }
    if (!m) {
      m = line.match(
        /Website:\s*(\S+).*?Intent Score:\s*(\d)\/5.*?Reason:\s*([^|]+?)(?:\|.*?(Pursue|Review|Pass))?$/i
      );
    }
    if (!m) return;
    const url = m[1].replace(/\|/g, "").trim();
    const score = parseInt(m[2], 10);
    const reason = m[3].replace(/\|/g, "").trim();
    let rawAct = (m[4] || "").replace(/\|/g, "").trim().toLowerCase().replace(/[^a-z]/g, "");
    let actKey = rawAct.includes("pursue") ? "pursue" : rawAct.includes("pass") ? "pass" : "review";
    if (!m[4] && !rawAct) {
      actKey = score >= 4 ? "pursue" : score === 3 ? "review" : "pass";
    }
    const sc = Math.min(5, Math.max(1, score)) || 3;
    rows.push({ url, score: sc, reason, actKey, act: ACTION_CFG[actKey] });
  });
  let pursue = 0;
  let review = 0;
  let pass = 0;
  lines.forEach((line) => {
    if (/Pursue/i.test(line)) pursue += 1;
    else if (/Review/i.test(line)) review += 1;
    else if (/Pass/i.test(line)) pass += 1;
  });
  return { rows, pursue, review, pass, lineCount: lines.length };
}

function VriBatchResultsBlock({ rawText }) {
  const [copied, setCopied] = useState(false);
  const t = (rawText || "").trim();
  if (!t) return null;
  const { rows, pursue, review, pass } = parseVriBatchRows(t);
  const count = rows.length;

  const doCopy = () => {
    navigator.clipboard.writeText(t).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  };
  const doDownload = () => {
    const blob = new Blob([t], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `FE-Intel-Batch-Intent-Scores-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="batch-results-wrap show">
      <div className="batch-results-title">Intent Score Results</div>
      {count === 0 ? (
        <pre style={{ fontSize: 11.5, color: "var(--text-sub)", lineHeight: 1.75, whiteSpace: "pre-wrap", margin: 0 }}>{t}</pre>
      ) : (
        <>
          <table className="batch-score-table">
            <thead>
              <tr>
                <th>Website</th>
                <th style={{ textAlign: "center", width: 68 }}>Score</th>
                <th>Reason</th>
                <th style={{ width: 110 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.url}-${i}`}>
                  <td className="batch-url-cell">{r.url}</td>
                  <td style={{ textAlign: "center" }}>
                    <span className={`score-badge score-${r.score}`}>{r.score}</span>
                  </td>
                  <td className="batch-reason-cell">{r.reason}</td>
                  <td>
                    <span className={`action-badge ${r.act.cls}`}>{r.act.label}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="batch-summary-row">
            <span style={{ color: "#059669" }}>🟢 Pursue: {pursue}</span>
            <span style={{ color: "#b45309" }}>🟡 Review: {review}</span>
            <span style={{ color: "#b91c1c" }}>🔴 Pass: {pass}</span>
            <span style={{ marginLeft: "auto", color: "var(--text-dim)" }}>
              {count} site{count !== 1 ? "s" : ""} evaluated · VRI 4-step methodology
            </span>
          </div>
        </>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <button type="button" className="act-btn" onClick={doCopy}>{copied ? "✓ Copied" : "⊞ Copy Results"}</button>
        <button type="button" className="act-btn" onClick={doDownload}>⬇ Download</button>
      </div>
    </div>
  );
}

const BR_PRICING_OPTS = ["Subscription (SaaS)", "Transaction-based", "Hybrid (Sub + Usage)", "Advertising / CPM", "Affiliate / CPA", "One-time + Recurring"];
const BR_MKTPOS_OPTS = ["Market Leader", "Challenger", "Niche Specialist", "Emerging Player"];
const TS_NARRATIVE_OPTS = ["Growth Story", "Margin Expansion / Profitability", "Strategic Synergies", "Market Expansion Opportunity", "PE/Financial Buyers"];
const SUPPORTING_DOC_ACCEPT = ".pdf,.doc,.docx,.txt";
const SUPPORTING_DOC_MAX_FILES = 8;
const SUPPORTING_DOC_MAX_BYTES = 2 * 1024 * 1024;

function SupportingDocumentsField({ idPrefix, items, onItemsChange }) {
  const safeItems = Array.isArray(items) ? items : [];
  const inputId = `${idPrefix}-supporting-docs`;

  const ingestFiles = async (fileList) => {
    const files = Array.isArray(fileList) ? fileList : Array.from(fileList || []);
    if (!files.length) return;

    const rowsToAdd = [];
    for (const file of files) {
      if (!/\.(pdf|doc|docx|txt)$/i.test(file.name)) {
        window.alert(`${file.name}: only PDF, DOC, DOCX, or TXT are accepted.`);
        continue;
      }
      if (file.size > SUPPORTING_DOC_MAX_BYTES) {
        window.alert(`${file.name} is larger than 2 MB and was skipped.`);
        continue;
      }
      try {
        rowsToAdd.push(await readFileAsBase64Payload(file));
      } catch {
        window.alert(`Could not read ${file.name}.`);
      }
    }
    if (!rowsToAdd.length) return;

    onItemsChange((prev) => {
      const base = Array.isArray(prev) ? [...prev] : [];
      for (const row of rowsToAdd) {
        if (base.length >= SUPPORTING_DOC_MAX_FILES) {
          window.alert(`Maximum ${SUPPORTING_DOC_MAX_FILES} files.`);
          break;
        }
        base.push(row);
      }
      return base;
    });
  };

  const onPick = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    input.value = "";
    await ingestFiles(files);
  };

  const removeAt = (ix) => {
    onItemsChange((prev) => (Array.isArray(prev) ? prev : []).filter((_, i) => i !== ix));
  };

  return (
    <div
      className="form-row"
      style={{ marginTop: 6 }}
      onDragOver={(ev) => { ev.preventDefault(); ev.stopPropagation(); }}
      onDrop={async (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        await ingestFiles(ev.dataTransfer?.files);
      }}
    >
      <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span>Supporting Documents</span>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: 1, color: "var(--text-dim)", textTransform: "uppercase" }}>
          (PDF, DOC, DOCX, TXT — multiple allowed)
        </span>
      </label>
      <label htmlFor={inputId} className="supporting-docs-dropzone">
        <span style={{ fontSize: 14 }} aria-hidden>📎</span>
        <span style={{ fontSize: 12, color: "var(--text-sub)", lineHeight: 1.45 }}>
          {safeItems.length
            ? `${safeItems.length} file${safeItems.length === 1 ? "" : "s"} attached — click to add more, or drag and drop`
            : "Click to attach files, or drag and drop here"}
        </span>
        <input
          type="file"
          id={inputId}
          multiple
          accept={SUPPORTING_DOC_ACCEPT}
          style={{ display: "none" }}
          onChange={onPick}
        />
      </label>
      {safeItems.length > 0 ? (
        <div aria-live="polite" aria-atomic="true">
          <div className="supporting-docs-list" role="list">
            <div className="supporting-docs-list-title">
              Attached files ({safeItems.length})
            </div>
            {safeItems.map((it, ix) => {
              const name = it.filename || it.name || `File ${ix + 1}`;
              return (
                <div key={`${name}-${ix}`} className="supporting-docs-file-row" role="listitem">
                  <span className="supporting-docs-file-name">
                    <span className="sd-ico" aria-hidden>📄</span>
                    <span>{name}</span>
                  </span>
                  <button type="button" className="supporting-docs-remove" onClick={() => removeAt(ix)}>
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function OriginationForm({ tool, origForms, updateOrigForm, onSubmit, onVriStep }) {
  const f = origForms[tool] || {};
  const v = origForms.vri;
  const u = (k, val) => updateOrigForm(tool, k, val);

  if (tool === "briefing") {
    return (
      <div>
        <div className="field-group-header">Business Identification</div>
        <div className="form-2col">
          <div className="form-row"><label className="form-label">Company Name / Domain</label><input className="fi" placeholder="e.g. cloudtool.io" value={f.domain || ""} onChange={(e) => u("domain", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Industry / Vertical</label><input className="fi" placeholder="e.g. B2B SaaS — HR Tech" value={f.ind || ""} onChange={(e) => u("ind", e.target.value)} /></div>
        </div>
        <div className="field-group-header">Financial Snapshot (3-Year Where Available)</div>
        <div className="form-2col">
          <div className="form-row"><label className="form-label">Revenue LTM</label><input className="fi" placeholder="e.g. $1.2M ARR" value={f.rev || ""} onChange={(e) => u("rev", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">EBITDA / Margin</label><input className="fi" placeholder="e.g. $480K (~40%)" value={f.ebitda || ""} onChange={(e) => u("ebitda", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Revenue Growth (YoY / CAGR %)</label><input className="fi" placeholder="e.g. 28% YoY" value={f.growth || ""} onChange={(e) => u("growth", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Prior Year Revenue (Y-1)</label><input className="fi" placeholder="e.g. $940K (optional)" value={f.rev1 || ""} onChange={(e) => u("rev1", e.target.value)} /></div>
        </div>
        <div className="field-group-header">Commercial Quality Metrics</div>
        <div className="form-3col">
          <div className="form-row"><label className="form-label">Revenue Mix (Recurring %)</label><input className="fi" placeholder="e.g. 75% recurring" value={f.recur || ""} onChange={(e) => u("recur", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Customer Concentration</label><input className="fi" placeholder="e.g. Top 5 = 32% of rev" value={f.conc || ""} onChange={(e) => u("conc", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Churn / Retention Rate</label><input className="fi" placeholder="e.g. 94% net retention" value={f.churn || ""} onChange={(e) => u("churn", e.target.value)} /></div>
        </div>
        <div className="form-2col">
          <div className="form-row"><label className="form-label">Pricing Model</label>
            <select className="fi-select" value={f.pricing || ""} onChange={(e) => u("pricing", e.target.value)}>
              <option value="">— Select —</option>
              {BR_PRICING_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="form-row"><label className="form-label">Market Positioning</label>
            <select className="fi-select" value={f.mktpos || ""} onChange={(e) => u("mktpos", e.target.value)}>
              <option value="">— Select —</option>
              {BR_MKTPOS_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <div className="field-group-header">Context &amp; Qualitative Inputs</div>
        <div className="form-row"><label className="form-label">Additional Context</label><textarea className="fi" placeholder="Founding story, team size, notable customers, tech stack, geographic footprint…" value={f.ctx || ""} onChange={(e) => u("ctx", e.target.value)} /></div>
        <button type="button" className="gen-btn" onClick={onSubmit}>⚡ Generate Big 4 Briefing</button>
      </div>
    );
  }

  if (tool === "teaser") {
    return (
      <div>
        <div className="field-group-header">Core Deal Parameters</div>
        <div className="form-2col">
          <div className="form-row"><label className="form-label">Business Model</label><input className="fi" placeholder="e.g. SaaS subscription, Affiliate" value={f.model || ""} onChange={(e) => u("model", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Industry Vertical</label><input className="fi" placeholder="e.g. Legal Tech, Travel Media" value={f.ind || ""} onChange={(e) => u("ind", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Revenue LTM</label><input className="fi" placeholder="e.g. $2.4M" value={f.rev || ""} onChange={(e) => u("rev", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">EBITDA LTM</label><input className="fi" placeholder="e.g. $900K (38% margin)" value={f.ebitda || ""} onChange={(e) => u("ebitda", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Revenue Growth (YoY %)</label><input className="fi" placeholder="e.g. 31% YoY" value={f.growth || ""} onChange={(e) => u("growth", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Asking Price / Range</label><input className="fi" placeholder="e.g. $4–5M" value={f.price || ""} onChange={(e) => u("price", e.target.value)} /></div>
        </div>
        <div className="field-group-header">Revenue Quality Inputs</div>
        <div className="form-3col">
          <div className="form-row"><label className="form-label">Recurring Revenue %</label><input className="fi" placeholder="e.g. 80%" value={f.recur || ""} onChange={(e) => u("recur", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Net Retention / Churn</label><input className="fi" placeholder="e.g. 93% retention" value={f.churn || ""} onChange={(e) => u("churn", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Customer Concentration</label><input className="fi" placeholder="e.g. Top 10 = 28%" value={f.conc || ""} onChange={(e) => u("conc", e.target.value)} /></div>
        </div>
        <div className="field-group-header">Teaser Controls</div>
        <div className="form-2col">
          <div className="form-row"><label className="form-label">Anonymity Level</label>
            <select className="fi-select" value={f.anon || "High"} onChange={(e) => u("anon", e.target.value)}>
              <option value="High">High — Maximum anonymisation</option>
              <option value="Medium">Medium — Industry &amp; size visible</option>
              <option value="Low">Low — Light anonymisation only</option>
            </select>
          </div>
          <div className="form-row"><label className="form-label">Investment Narrative Focus</label>
            <select className="fi-select" value={f.narrative || "Growth Story"} onChange={(e) => u("narrative", e.target.value)}>
              {TS_NARRATIVE_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row"><label className="form-label">Key Strengths</label><input className="fi" placeholder="e.g. Recurring revenue, 95% retention" value={f.str || ""} onChange={(e) => u("str", e.target.value)} /></div>
        <div className="form-row"><label className="form-label">EBITDA Quality Notes</label><input className="fi" placeholder="e.g. One-off founder salary normalised…" value={f.ebnotes || ""} onChange={(e) => u("ebnotes", e.target.value)} /></div>
        <div className="form-row">
          <button
            type="button"
            className={`toggle-row${f.redFlag ? " active" : ""}`}
            onClick={() => u("redFlag", !f.redFlag)}
          >
            <div>
              <div className="toggle-label">Red Flag Masking</div>
              <div className="toggle-label"><small>Neutralise sensitive financial identifiers in output</small></div>
            </div>
            <div className="toggle-switch" aria-hidden />
          </button>
        </div>
        <SupportingDocumentsField
          idPrefix="orig-teaser"
          items={f.supporting_documents}
          onItemsChange={(arr) => u("supporting_documents", arr)}
        />
        <button type="button" className="gen-btn" onClick={onSubmit}>⚡ Generate Institutional Teaser</button>
      </div>
    );
  }

  if (tool === "valuation_narrative") {
    return (
      <div>
        <div className="field-group-header">Company Identification</div>
        <div className="form-2col">
          <div className="form-row"><label className="form-label">Company Name</label><input className="fi" placeholder="e.g. Acme SaaS Co." value={f.name || ""} onChange={(e) => u("name", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Industry</label><input className="fi" placeholder="e.g. Technology / SaaS, e-Commerce…" value={f.ind || ""} onChange={(e) => u("ind", e.target.value)} /></div>
        </div>
        <div className="field-group-header">Financial Inputs</div>
        <div className="form-2col">
          <div className="form-row"><label className="form-label">Revenue LTM ($)</label><input className="fi" type="number" placeholder="e.g. 1500000" value={f.rev || ""} onChange={(e) => u("rev", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">EBITDA LTM ($)</label><input className="fi" type="number" placeholder="e.g. 600000" value={f.ebitda || ""} onChange={(e) => u("ebitda", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Revenue Growth (YoY / CAGR %)</label><input className="fi" placeholder="e.g. 25% YoY" value={f.growth || ""} onChange={(e) => u("growth", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Revenue Mix (Recurring %)</label><input className="fi" placeholder="e.g. 70% recurring ARR" value={f.recur || ""} onChange={(e) => u("recur", e.target.value)} /></div>
        </div>
        <div className="field-group-header">Revenue Quality &amp; Commercial Metrics</div>
        <div className="form-3col">
          <div className="form-row"><label className="form-label">Customer Concentration</label><input className="fi" placeholder="e.g. Top 5 = 30%" value={f.conc || ""} onChange={(e) => u("conc", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Churn / Net Retention</label><input className="fi" placeholder="e.g. 92% net retention" value={f.churn || ""} onChange={(e) => u("churn", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Market Positioning</label>
            <select className="fi-select" value={f.mktpos || ""} onChange={(e) => u("mktpos", e.target.value)}>
              <option value="">— Select —</option>
              {BR_MKTPOS_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <div className="field-group-header">Normalisation &amp; Highlights</div>
        <div className="form-row"><label className="form-label">EBITDA Quality Adjustment Notes</label><input className="fi" placeholder="e.g. Normalised for founder salary add-back…" value={f.ebnotes || ""} onChange={(e) => u("ebnotes", e.target.value)} /></div>
        <div className="form-row"><label className="form-label">Business Highlights</label><textarea className="fi" placeholder="Growth trajectory, retention, tech moat, team depth…" value={f.hi || ""} onChange={(e) => u("hi", e.target.value)} /></div>
        <button type="button" className="gen-btn" onClick={onSubmit}>⚡ Generate Audit-Grade Narrative</button>
      </div>
    );
  }

  if (tool === "risk") {
    return (
      <div>
        <div className="field-group-header">Business Profile</div>
        <div className="form-2col">
          <div className="form-row"><label className="form-label">Company / Deal Name</label><input className="fi" placeholder="e.g. Acme SaaS (Internal)" value={f.name || ""} onChange={(e) => u("name", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Industry / Vertical</label><input className="fi" placeholder="e.g. B2B SaaS — HR Tech" value={f.ind || ""} onChange={(e) => u("ind", e.target.value)} /></div>
        </div>
        <div className="field-group-header">Financial Inputs</div>
        <div className="form-2col">
          <div className="form-row"><label className="form-label">Revenue LTM</label><input className="fi" placeholder="e.g. $2.4M" value={f.rev || ""} onChange={(e) => u("rev", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">EBITDA / Margin</label><input className="fi" placeholder="e.g. $900K (38%)" value={f.ebitda || ""} onChange={(e) => u("ebitda", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Revenue Growth (YoY %)</label><input className="fi" placeholder="e.g. 22% YoY" value={f.growth || ""} onChange={(e) => u("growth", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Prior Year EBITDA</label><input className="fi" placeholder="e.g. $720K (optional)" value={f.ebitdaPrior || ""} onChange={(e) => u("ebitdaPrior", e.target.value)} /></div>
        </div>
        <div className="field-group-header">Commercial Quality Metrics</div>
        <div className="form-3col">
          <div className="form-row"><label className="form-label">Top 5 Customer Revenue %</label><input className="fi" placeholder="e.g. 45%" value={f.top5 || ""} onChange={(e) => u("top5", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Top 10 Customer Revenue %</label><input className="fi" placeholder="e.g. 65%" value={f.top10 || ""} onChange={(e) => u("top10", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Largest Single Customer %</label><input className="fi" placeholder="e.g. 18%" value={f.top1 || ""} onChange={(e) => u("top1", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Recurring Revenue %</label><input className="fi" placeholder="e.g. 68%" value={f.recur || ""} onChange={(e) => u("recur", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Churn Rate / Retention</label><input className="fi" placeholder="e.g. 8% annual churn" value={f.churn || ""} onChange={(e) => u("churn", e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Avg Contract Duration</label><input className="fi" placeholder="e.g. Monthly / Annual / 3yr" value={f.contract || ""} onChange={(e) => u("contract", e.target.value)} /></div>
        </div>
        <div className="form-row"><label className="form-label">Additional Risk Context</label><textarea className="fi" placeholder="Market conditions, key person risk, platform risk…" value={f.ctx || ""} onChange={(e) => u("ctx", e.target.value)} /></div>
        <button type="button" className="gen-btn" onClick={onSubmit}>⚡ Generate Risk Snapshot</button>
      </div>
    );
  }

  if (tool === "vri") {
    const uv = (k, val) => updateOrigForm("vri", k, val);
    return (
      <div>
        <div className="vri-step-tabs" role="tablist">
          {[1, 2, 3, 4].map((n) => (
            <button
              key={n}
              type="button"
              role="tab"
              className={`vri-step-tab${v.step === n ? " active" : ""}`}
              onClick={() => uv("step", n)}
            >
              <span className="step-num">{n}</span>
              {n === 1 ? "Ingest" : n === 2 ? "Benchmark" : n === 3 ? "Intent Score" : "Response"}
            </button>
          ))}
          <button
            type="button"
            role="tab"
            className={`vri-step-tab vri-tab-batch${v.step === "batch" ? " active" : ""}`}
            onClick={() => uv("step", "batch")}
          >
            <span className="step-num">⚡</span>
            Batch Score
          </button>
        </div>
        {v.stepError ? (
          <div className="deal-search-err" style={{ marginBottom: 12 }}>{v.stepError}</div>
        ) : null}

        <div className={`vri-panel${v.step === 1 ? " active" : ""}`}>
          <div className="field-group-header">Website &amp; Business Ingestion</div>
          <div className="form-row"><label className="form-label">Company Website URL</label><input className="fi" placeholder="e.g. https://acme-saas.io" value={v.url || ""} onChange={(e) => uv("url", e.target.value)} /></div>
          <div className="form-2col">
            <div className="form-row"><label className="form-label">Industry / Vertical</label><input className="fi" placeholder="e.g. B2B SaaS — Workflow Automation" value={v.ind || ""} onChange={(e) => uv("ind", e.target.value)} /></div>
            <div className="form-row"><label className="form-label">Self-Reported Revenue</label><input className="fi" placeholder="e.g. $1.8M ARR" value={v.rev || ""} onChange={(e) => uv("rev", e.target.value)} /></div>
            <div className="form-row"><label className="form-label">Self-Reported EBITDA / Margin</label><input className="fi" placeholder="e.g. $540K (30%)" value={v.ebitda || ""} onChange={(e) => uv("ebitda", e.target.value)} /></div>
            <div className="form-row"><label className="form-label">Revenue Growth (YoY)</label><input className="fi" placeholder="e.g. 35%" value={v.growth || ""} onChange={(e) => uv("growth", e.target.value)} /></div>
          </div>
          <div className="field-group-header">Qualitative Context</div>
          <div className="form-row"><label className="form-label">Business Description / Notes from Inbound</label><textarea className="fi" placeholder="Paste the seller's email, form submission, or describe the business…" style={{ minHeight: 80 }} value={v.ctx || ""} onChange={(e) => uv("ctx", e.target.value)} /></div>
          <SupportingDocumentsField
            idPrefix="orig-vri"
            items={v.supporting_documents}
            onItemsChange={(arr) => uv("supporting_documents", arr)}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button type="button" className="vri-nav-btn vri-next" onClick={() => onVriStep("ingest")}>
              ⚡ Submit &amp; run analysis
            </button>
          </div>
        </div>

        <div className={`vri-panel${v.step === 2 ? " active" : ""}`}>
          <div className="field-group-header">Benchmarking Against FE Deal Database</div>
          {v.step2Output ? (
            <div className="vri-step-output">{v.step2Output}</div>
          ) : (
            <div className="vri-step-output" style={{ color: "var(--text-dim)" }}>
              This section is filled automatically when you submit the ingest form (tab 1). It mirrors internal benchmarking: sector comps, implied valuation range, positioning verdict, and value drivers to validate.
            </div>
          )}
        </div>

        <div className={`vri-panel${v.step === 3 ? " active" : ""}`}>
          <div className="field-group-header">Lead Intent Classification</div>
          {v.step3Output ? (
            <div className="vri-intent-stack">
              <VRIIntentPresentation text={v.step3Output} />
            </div>
          ) : (
            <div className="vri-step-output" style={{ color: "var(--text-dim)" }}>
              Intent score, scoring signals, and recommended action (Pursue / Review / Pass) appear here after ingest — composite view aligned to FE Intel (intent signals plus benchmark alignment).
            </div>
          )}
        </div>

        <div className={`vri-panel${v.step === "batch" ? " active" : ""}`}>
          <div className="batch-score-header">
            <div className="field-group-header" style={{ marginBottom: 0 }}>Batch Website Intent Scoring</div>
            <span className="batch-badge">Beta</span>
          </div>
          <p style={{ fontSize: 12, color: "var(--text-dim)", margin: "6px 0 14px", lineHeight: 1.6 }}>
            Paste one website URL per line. Each site is evaluated on a composite <strong>Intent Score (1–5)</strong>
            {" "}— weighted 70% intent signals (commercial relevance, monetisation, activity, outreach fit) and 30% FE deal benchmark alignment.
          </p>
          <div className="form-row">
            <label className="form-label">
              Website URLs{" "}
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "var(--text-dim)", letterSpacing: 0.5 }}>(one per line)</span>
            </label>
            <textarea
              className="batch-url-input"
              placeholder={"https://example-saas.com\nhttps://another-business.io\nhttps://ecommerce-store.com\n..."}
              value={v.batchUrls || ""}
              onChange={(e) => uv("batchUrls", e.target.value)}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <button
              type="button"
              className="vri-nav-btn vri-next"
              style={{
                background: "linear-gradient(135deg,var(--purple),var(--blue))",
                boxShadow: "0 2px 10px rgba(144,107,246,0.3)",
              }}
              onClick={() => onVriStep("batch")}
            >
              ⚡ Score All Websites →
            </button>
          </div>
          <VriBatchResultsBlock rawText={v.batchRawOutput} />
        </div>

        <div className={`vri-panel${v.step === 4 ? " active" : ""}`}>
          <div className="field-group-header">Response Draft — Edit Before Sending</div>
          <div style={{ fontSize: 11.5, color: "var(--text-dim)", marginBottom: 10, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 0.3 }}>
            ⚠ This draft is for review and editing only. No sending or CRM functionality.
          </div>
          <div className="response-draft-box">
            <textarea
              className="response-draft-textarea"
              placeholder="After you run ingest (tab 1), an editable outreach email draft appears here…"
              value={v.responseDraft || ""}
              onChange={(e) => uv("responseDraft", e.target.value)}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="vri-nav-btn vri-back" onClick={() => uv("step", 3)}>← Back</button>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="act-btn" onClick={() => navigator.clipboard.writeText(v.responseDraft || "")}>⊞ Copy Draft</button>
              <button
                type="button"
                className="act-btn"
                onClick={() => {
                  const blob = new Blob([v.responseDraft || ""], { type: "text/plain" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `FE-Intel-VRI-Response-${new Date().toISOString().slice(0, 10)}.txt`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
              >
                ⬇ Download
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
